// ============================================================================
// HALAL CALGARY - MAP APP
//
// The interactive map: Leaflet, the sidebar list, search, the place sheet,
// its reviews, and the submit/report forms. Only loaded on pages with a map.
//
// Runs after main.js and uses its global-scope declarations directly: el(),
// fromTrustedMarkup(), thumbSrc(), proxiedImageSrc(), IS_BOT, dataReady,
// maybeHideLoadingOverlay(), finishLoadingBorder() and onThemeChange().
//
// Table of contents:
//   EXTERNAL MAP LINKS
//   MAP MARKERS & POPUPS
//   URL HASH DEEP-LINKING
//   TOAST NOTIFICATIONS
//   SHARE / COPY LINK
//   SUBMIT / GET-FEATURED MODAL
//   DISTANCE CALCULATIONS
//   GEOLOCATION
//   SIDEBAR LIST
//   SEARCH AUTOCOMPLETE
//   MAP VIEWPORT / RESET CONTROL
//   FEATURED CAROUSEL
//   DATA LOADING & BOOTSTRAP
// ============================================================================

/* Wrapped (unlike main.js) so the minifier can rename these names - nothing
   outside this file refers to them. main.js's globals are still in scope. */
(() => {
  'use strict';

/* THE BASEMAP, from Site settings. Falls back to OpenStreetMap's own tiles
   if the layout's script did not run - a map with tiles beats no map. */
const BASEMAP = window.__hcBasemap || {};

/* THE BASEMAP LAYER: raster (someone else's PNGs, one URL per theme) or
   vector (our PMTiles archive drawn by protomaps-leaflet, themed by colours).
   Both are plain Leaflet layers, so nothing downstream cares which. */
function buildRasterLayer(isDark) {
  return L.tileLayer(isDark ? DARK_TILE_URL : LIGHT_TILE_URL, {
    maxZoom: BASEMAP.maxZoom || 19,
    subdomains: BASEMAP.subdomains || 'abc',
    attribution: BASEMAP.attribution
      || '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
  });
}

/* The protomaps-leaflet "flavor" (named colours) for one theme - the two
   shipped flavors, used as they come. */
function vectorFlavor(isDark) {
  return isDark ? basemaps.DARK : basemaps.LIGHT;
}

function applyVectorTheme(layer, isDark) {
  const flavor = vectorFlavor(isDark);

  layer.paintRules = protomapsL.paintRules(flavor);
  layer.labelRules = protomapsL.labelRules(flavor);
  // Labels are laid out once and cached with their tile; leaving the old
  // index in place keeps the previous theme's halo colours on screen.
  if (layer.rerenderTiles) layer.rerenderTiles();
  if (layer.clearLayout) layer.clearLayout();
}

/* THE BASEMAP'S FIRST 16KB, FROM BOOT.JS. protomaps reads the archive's header and root
   directory (bytes 0-16383) before any tile, then a leaf directory - which in this archive
   lies inside those same bytes, so it used to be downloaded a second time. boot.js has
   requested the 16KB during the parse (window.__hcTileHead - "THE BASEMAP'S HEADER" there);
   this gives protomaps a source that answers every range inside them from that response.
   A reuse within this page load only: nothing is stored, and the next page load requests
   the header again. Every other range goes to protomaps' own fetch - and all of them when
   the early request got nothing (it failed, or the server ignored the Range) or once
   protomaps has seen the archive's ETag change and must reload.
   protomaps-leaflet 5.1.0 does not export PMTiles, so this reaches in: a PmtilesSource's `p`
   is its PMTiles, whose `source` is the fetching Source (getKey(), getBytes(offset, length,
   signal, etag), mustReload). Each is checked, and anything missing gives the plain URL -
   re-check on upgrade, with install.sh's patch. */
function vectorArchive() {
  const head = window.__hcTileHead;

  if (!head || head.url !== BASEMAP.url || !head.bytes || typeof protomapsL.PmtilesSource !== 'function') {
    return BASEMAP.url;
  }

  const archive = new protomapsL.PmtilesSource(BASEMAP.url, true).p;
  const own = archive && archive.source;

  if (!own || typeof own.getBytes !== 'function' || typeof own.getKey !== 'function') return BASEMAP.url;

  const early = Promise.resolve(head.bytes).catch(() => null);

  archive.source = {
    getKey: () => own.getKey(),
    getBytes: (offset, length, signal, etag) => early.then((first) => (
      first && !own.mustReload && offset + length <= first.data.byteLength
        ? { data: first.data.slice(offset, offset + length), etag: first.etag, cacheControl: first.cacheControl, expires: first.expires }
        : own.getBytes(offset, length, signal, etag)
    )),
  };

  return archive;
}

function buildVectorLayer(isDark) {
  const flavor = vectorFlavor(isDark);

  return protomapsL.leafletLayer({
    // The archive itself when boot.js started its header, else the URL.
    url: vectorArchive(),
    paintRules: protomapsL.paintRules(flavor),
    labelRules: protomapsL.labelRules(flavor),
    maxDataZoom: 15,
    maxZoom: BASEMAP.maxZoom || 19,
    attribution: BASEMAP.attribution
  });
}

/* The site's name from Site settings. Never spell it out in this file -
   every string naming the site reads it from here. */
const SITE_NAME = window.__hcSiteName || document.title.split(' | ')[0];
// OSM rather than CARTO as the fallback: CARTO's free tiles now carry an
// "API KEY REQUIRED" watermark.
const LIGHT_TILE_URL = BASEMAP.light || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const DARK_TILE_URL = BASEMAP.dark || LIGHT_TILE_URL;

// Undefined where there is no #map (articles view); anything using them is
// guarded.
let map, tileLayer;
let places = [];
// Keyed by placeSlug(), never by name: a chain's branches share one name.
const markers = {};

// The configured home view. Declared at this level, not inside the
// !IS_ARTICLES_VIEW block, because computeHomeView() in another scope needs it.
const defaultView = window.__hcDefaultView || {};
const startLat = typeof defaultView.lat === 'number' ? defaultView.lat : 51.06;
const startLng = typeof defaultView.lng === 'number' ? defaultView.lng : -114.03;
const startZoom = typeof defaultView.zoom === 'number' ? defaultView.zoom : 12;

if (!IS_ARTICLES_VIEW) {
  map = L.map('map', { zoomControl: false }).setView([startLat, startLng], startZoom);
  L.control.zoom({ position: 'topright' }).addTo(map);

  // Attribution, subdomains and maxZoom come from the style, so the map
  // always credits whoever's tiles it is actually drawing.
  tileLayer = BASEMAP.vector ? buildVectorLayer(prefersDarkMode) : buildRasterLayer(prefersDarkMode);
  tileLayer.addTo(map);

  // Replaces the "Leaflet" prefix, which BSD-2-Clause does not require (the
  // notice ships in the vendored file - see install.sh).
  map.attributionControl.setPrefix(SITE_NAME);
}

// The tile layer follows the theme (main.js owns the toggle). Raster swaps
// URLs; vector re-applies paint rules to tiles already in memory.
onThemeChange((isDark) => {
  if (!tileLayer) return;

  if (BASEMAP.vector) {
    applyVectorTheme(tileLayer, isDark);
  } else {
    tileLayer.setUrl(isDark ? DARK_TILE_URL : LIGHT_TILE_URL);
  }

  if (typeof flightLine !== 'undefined' && flightLine) {
    flightLine.setStyle({ color: isDark ? '#f2f2f2' : '#1a1a1a' });
  }
});

  // ==========================================================================
  // EXTERNAL MAP LINKS
  // ==========================================================================

  function googleMapsLink(p) {
    return "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(p.name + " " + p.address);
  }

  function appleMapsLink(p) {
    return "https://maps.apple.com/?ll=" + p.lat + "," + p.lng + "&q=" + encodeURIComponent(p.name);
  }

  /* The price under a place name: Google's range ("$10–20", "$100+") when the
     record has one, else the "$".."$$$$" level. Same rule as
     App\Support\Price; tests/js/price.test.mjs holds the two together. The
     range is {from, to?, currency} - `to` is absent when Google only states a
     floor, and CAD prints as a bare "$" like Google's Canadian card. */
  function priceLabel(p) {
    const r = p && p.priceRange;
    const from = r && Number(r.from);

    if (r && Number.isFinite(from) && from > 0) {
      const to = Number(r.to);
      const cur = String(r.currency || 'CAD').toUpperCase();
      const symbol = cur === 'CAD' ? '$' : (cur === 'USD' ? 'US$' : cur + ' ');

      return Number.isFinite(to) && to > from ? symbol + from + '–' + to : symbol + from + '+';
    }

    return (p && p.price) ? String(p.price).trim() : '';
  }

  /* A description as plain text (twin of App\Support\PlaceDetails::text()),
     for the page intro of a fully fetched place and, cut, its meta
     description (detailsSummary). Cards render the server's pre-stripped
     `summary` instead. No ellipsis: only the CSS line clamp knows whether it
     truncated. Character for character the server's, so the whitespace is
     PCRE's Unicode \s rather than JavaScript's (NEL and U+180E in, the BOM
     out) and the trim is PHP's. */
  function detailsText(text) {
    if (!text) return '';

    const plain = decodeEntities(String(text)
      // Block boundaries become a space, or "kebabs.</p><h4>Good to know"
      // reads as "kebabs.Good to know" and an hours row as "Monday11am".
      // Same list as PlaceDetails::BLOCKS. A table header row goes first,
      // as there: "Hours Day Hours Monday" is noise.
      .replace(/<thead\b[^>]*>[\s\S]*?<\/thead>/gi, ' ')
      .replace(/<\/?(p|h4|h5|br|ul|ol|li|table|thead|tbody|tr|th|td)\b[^>]*>/gi, ' ')
      .replace(/<[^>]*>/g, ''));

    return phpTrim(plain.replace(/[\t\n\v\f\r \u0085\u00a0\u1680\u180e\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+/g, ' '));
  }

  /* The entities html_entity_decode() turns back into characters, for those
     an editor writes; any other named one stays as typed. */
  const HTML_ENTITIES = { amp: '&', AMP: '&', lt: '<', LT: '<', gt: '>', GT: '>', quot: '"', QUOT: '"', apos: '\'', nbsp: '\u00a0' };

  // html_entity_decode(ENT_QUOTES | ENT_HTML5): the semicolon is required,
  // and a number decodes only to a character HTML5 allows as one.
  function decodeEntities(text) {
    return text.replace(/&(?:#(\d+)|#[xX]([0-9a-fA-F]+)|([A-Za-z]+));/g, (whole, dec, hex, name) => {
      if (name) return Object.prototype.hasOwnProperty.call(HTML_ENTITIES, name) ? HTML_ENTITIES[name] : whole;

      const code = dec ? parseInt(dec, 10) : parseInt(hex, 16);
      const allowed = (code >= 0x20 && code <= 0x7e) || code === 0x09 || code === 0x0a || code === 0x0c
        || (code >= 0xa0 && code <= 0xd7ff)
        || (code >= 0xe000 && code <= 0x10ffff && (code & 0xffff) < 0xfffe && (code < 0xfdd0 || code > 0xfdef));

      return allowed ? String.fromCodePoint(code) : whole;
    });
  }

  // PHP's trim(): only these characters, not a form feed or Unicode space.
  function phpTrim(text) {
    return text.replace(/^[ \t\n\r\0\v]+|[ \t\n\r\0\v]+$/g, '');
  }

  /* The opening of a description for a meta description (twin of
     PlaceDetails::summary()): headings out - run into the sentence after
     them they read as a stutter - then cut to 155 characters. */
  function detailsSummary(details) {
    return summaryCut(detailsText(String(details || '').replace(/<h[1-6]\b[^>]*>[\s\S]*?<\/h[1-6]>/gi, ' ')), 155);
  }

  // Cut on a word boundary with an ellipsis, counting characters as
  // mb_substr() does (an emoji is one, not two UTF-16 units).
  function summaryCut(text, limit) {
    const chars = Array.from(text);

    if (chars.length <= limit) return text;

    let cut = chars.slice(0, limit).join('');
    const lastSpace = cut.lastIndexOf(' ');

    if (lastSpace !== -1) cut = cut.slice(0, lastSpace);

    return cut.replace(/[ \t\n\r\0\v.,;:]+$/, '') + '…';
  }

  /* The description as markup, for the sheet: parsed with DOMParser, kept to
     the tags PlaceDetails allows and stripped of every attribute. Keep
     DETAIL_TAGS in step with the server's list (tests/js/text.test.mjs
     checks). Tables are still allowed, though the house style keeps
     timetables out of descriptions (opening hours show from hoursNode());
     CKEditor's <figure class="table"> wrapper is not listed, so only the
     table stays. */
  const DETAIL_TAGS = [
    'H4', 'H5', 'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'UL', 'OL', 'LI',
    'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD'
  ];

  function detailsNode(text) {
    const wrap = el('div', 'popup-details popup-details--full');
    const parsed = new DOMParser().parseFromString(String(text || ''), 'text/html');

    const keep = (node) => {
      if (node.nodeType === 3) return document.createTextNode(node.nodeValue);
      if (node.nodeType !== 1) return null;

      // Anything not on the list contributes its text and nothing else, so a
      // stray tag loses its formatting rather than its content.
      const target = DETAIL_TAGS.indexOf(node.tagName) === -1
        ? document.createDocumentFragment()
        : document.createElement(node.tagName.toLowerCase());

      Array.prototype.forEach.call(node.childNodes, (child) => {
        const built = keep(child);

        if (built) target.appendChild(built);
      });

      return target;
    };

    Array.prototype.forEach.call(parsed.body.childNodes, (node) => {
      const built = keep(node);

      if (built) wrap.appendChild(built);
    });

    return wrap;
  }

  /* The opening hours under the description, laid out the way Google lays
     them out: a bold "Hours:" beside a line a day, today first and in bold.
     The lines arrive worded and Monday first (OpeningHours::lines()); only
     the order is decided here, by the day it is in the site's timezone (Site
     settings, on <html data-timezone>). Same markup as
     partials/place-hours.blade.php, so the morph adopts the server's. */
  const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  function siteWeekday() {
    try {
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        timeZone: document.documentElement.dataset.timezone || undefined,
      }).format(new Date()).toLowerCase();
    } catch (e) {
      // An unknown zone name throws; the visitor's own day is close enough.
      return WEEKDAYS[new Date().getDay()];
    }
  }

  /* "Open now · Closes at 10 p.m.", the last item on the services line - same
     markup as partials/place-open-status.blade.php, so the morph adopts the
     server's. */
  function openStatusNode(status) {
    const tone = status.open
      ? (status.soon ? 'closing' : 'open')
      : (status.soon ? 'opening' : 'closed');
    const line = el('span', 'popup-open-status popup-open-status--' + tone);

    line.appendChild(el('span', 'popup-open-status-state', status.state));
    if (status.detail) line.appendChild(el('span', 'popup-open-status-detail', ' · ' + status.detail));

    return line;
  }

  function hoursNode(lines) {
    const today = siteWeekday();
    const at = Math.max(0, lines.findIndex((line) => line.day === today));
    const wrap = el('div', 'popup-hours');
    const table = el('table', 'popup-hours-table');
    const body = document.createElement('tbody');

    wrap.appendChild(el('span', 'popup-hours-label', 'Hours:'));
    table.setAttribute('aria-label', 'Opening hours');

    lines.slice(at).concat(lines.slice(0, at)).forEach((line) => {
      const row = el('tr', line.day === today ? 'popup-hours-today' : '');
      const day = el('th', '', line.label);
      const hours = el('td');
      const shifts = line.hours.split(', ');

      // A shift to a <span>, its comma inside, so a day that has to wrap
      // breaks after the comma rather than after a dash.
      shifts.forEach((shift, i) => {
        const last = i === shifts.length - 1;

        hours.appendChild(el('span', '', last ? shift : shift + ','));
        if (!last) hours.appendChild(document.createTextNode(' '));
      });

      day.setAttribute('scope', 'row');
      row.appendChild(day);
      row.appendChild(hours);
      body.appendChild(row);
    });

    table.appendChild(body);
    wrap.appendChild(table);

    return wrap;
  }

  const googleMapsIconSvg = '<svg width="14" height="14" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/><path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/><path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/><path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/></svg>';

  // The "G" in front of a Google star rating, matching the Facebook badge
  // beside it: an empty element main.css paints with the mark (see
  // App\Support\IconStrip). Icons::googleMark() to the byte - the server's
  // cards, sheet and featured cards carry it, and the first render adopts
  // them (tests/Feature/MapPagePayloadTest).
  const googleMark = '<i class="google-mark" title="Google" aria-hidden="true"></i>';
  const appleMapsIconSvg = '<svg width="13" height="13" viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>';
  const shareIconSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 16V4M12 4L7.5 8.5M12 4l4.5 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const phoneIconSvg = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  // Both carousel arrows; "previous" is flipped in CSS.
  const carouselArrowSvg = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="m9 5 7 7-7 7" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const websiteIconSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  // Service badges; each <title> is the SVG's accessible name.
  const dineInIconSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><title>Dine-in available</title><path d="M3 2v7a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M7 2v20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const takeoutIconSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><title>Takeout available</title><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M3 6h18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 10a4 4 0 0 1-8 0" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const deliveryIconSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><title>Delivery available</title><path d="m7.5 4.27 9 5.15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M3.3 7 12 12l8.7-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 22V12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  /* ---- Building rows as nodes ----
     Rows are built with createElement/textContent, never HTML strings, so
     place data can never be markup. fromTrustedMarkup() parses only the SVG
     constants written in this file, never data. */

  /**
   * An <a>, with the icon and label the popup buttons all share.
   *
   * href is assigned as a property, so a non-URL becomes a broken link
   * rather than anything executable.
   */
  function linkNode(href, className, opts) {
    const a = el('a', className);
    const o = opts || {};

    a.href = href;

    if (o.newTab) {
      a.target = '_blank';
      a.rel = 'noopener';
    }

    if (o.title) a.title = o.title;
    if (o.ariaLabel) a.setAttribute('aria-label', o.ariaLabel);
    if (o.icon) a.appendChild(fromTrustedMarkup(o.icon));
    if (o.label) a.appendChild(el('span', null, o.label));
    if (o.text) a.appendChild(document.createTextNode(o.text));

    return a;
  }

  /** A <button type="button">, same shape as linkNode. */
  function buttonNode(className, opts) {
    const b = el('button', className);
    const o = opts || {};

    b.type = 'button';

    if (o.slug) b.dataset.slug = o.slug;
    if (o.title) b.title = o.title;
    if (o.ariaLabel) b.setAttribute('aria-label', o.ariaLabel);
    if (o.icon) b.appendChild(fromTrustedMarkup(o.icon));
    if (o.label) b.appendChild(el('span', null, o.label));

    return b;
  }


  /** A text run with the matched part wrapped in <mark>, as nodes. */
  function highlightNodes(text, query) {
    const fragment = document.createDocumentFragment();
    const idx = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1;

    if (idx === -1) {
      fragment.appendChild(document.createTextNode(text));

      return fragment;
    }

    fragment.appendChild(document.createTextNode(text.slice(0, idx)));
    fragment.appendChild(el('mark', 'search-match', text.slice(idx, idx + query.length)));
    fragment.appendChild(document.createTextNode(text.slice(idx + query.length)));

    return fragment;
  }

  /** The " · " between meta items, as a node. */
  function separatorNode() {
    return document.createTextNode(' · ');
  }

  /* One segment of a wrapping meta row - its leading "·" and its content as a
     single flex item, so a wrap can never leave the separator orphaned at
     the end of a line. Trailing badges go in the same group. */
  function metaSegment(...nodes) {
    const group = el('span', 'meta-segment');

    group.appendChild(separatorNode());
    nodes.forEach(n => n && group.appendChild(n));

    return group;
  }

  /**
   * A place thumbnail. Never watermarked: it is the business's own photo, not
   * ours (only article photos and OG cards take the mark - see articles.js).
   */
  function thumbNode(p, className, lazy, displayWidth) {
    const img = el('img', className + (p.image ? '' : ' ' + className + '-fallback'));

    img.alt = p.name;
    img.loading = 'lazy';

    if (lazy && p.image) {
      img.src = defaultThumbSrc;
      img.dataset.src = thumbSrc(p, displayWidth);
      img.dataset.fallback = defaultThumbSrc;
      img.dataset.lazyRoot = lazy;
    } else {
      img.src = thumbSrc(p, displayWidth);
    }

    img.addEventListener('error', function handle() {
      img.removeEventListener('error', handle);
      img.src = defaultThumbSrc;
      img.classList.add(className + '-fallback');
    });

    return img;
  }

  /* ---- Photo carousel (places with more than one photo) ----
     Built on top of the existing first <img>, which stays frame zero; a
     single-photo place gets nothing added.

     State lives in the DOM, never a closure: the open sheet is re-rendered
     via dom.patch, which morphs and would keep old listeners alongside new
     ones. The showing frame is the one not hidden, and clicks go through the
     sheet's delegated listener. Frames get their src only when first needed. */
  const CAROUSEL_MS = 4200;

  function addCarousel(wrap, p) {
    const urls = Array.isArray(p.images) ? p.images.filter(Boolean) : [];
    const first = dom.one('.popup-thumb', wrap);

    if (!first) return;

    // The original photo behind each frame, for the overlay (photoOverlay),
    // which shows it larger than the frame's 420px copy.
    if (urls[0] || p.image) {
      first.dataset.photo = urls[0] || p.image;
      photoOpener(first);
    }

    if (urls.length < 2) return;

    // On a re-render this wrap may already carry a carousel - start clean.
    dom.all('.popup-thumb-frame, .popup-thumb-arrow, .popup-thumb-dots', wrap)
      .forEach((node) => dom.remove(node));

    first.hidden = false;
    wrap.classList.add('popup-thumb-wrap--carousel');

    urls.slice(1).forEach((url) => {
      const img = el('img', 'popup-thumb popup-thumb-frame');

      img.alt = p.name;
      img.dataset.photo = url;
      photoOpener(img);
      // Not loading=lazy: src is set only when about to be shown or preloaded,
      // and lazy would defer it again for an image hidden under another.
      img.dataset.src = proxiedImageSrc(url, { width: 420 });
      img.hidden = true;

      img.addEventListener('error', function handle() {
        img.removeEventListener('error', handle);
        img.src = defaultThumbSrc;
        img.classList.add('popup-thumb-fallback');
      });

      // Before the report/social overlays, which must stay on top.
      dom.insert(wrap, img, first.nextSibling);
    });

    const dots = el('div', 'popup-thumb-dots');

    urls.forEach((url, i) => {
      const dot = el('button', 'popup-thumb-dot' + (i ? '' : ' is-current'));

      dot.type = 'button';
      dot.dataset.frame = String(i);
      dot.setAttribute('aria-label', 'Photo ' + (i + 1) + ' of ' + urls.length);
      dots.appendChild(dot);
    });

    const prev = buttonNode('popup-thumb-arrow popup-thumb-arrow--prev', {
      ariaLabel: 'Previous photo', title: 'Previous photo', icon: carouselArrowSvg,
    });
    const next = buttonNode('popup-thumb-arrow popup-thumb-arrow--next', {
      ariaLabel: 'Next photo', title: 'Next photo', icon: carouselArrowSvg,
    });

    prev.dataset.step = '-1';
    next.dataset.step = '1';

    dom.insert(wrap, prev);
    dom.insert(wrap, next);
    dom.insert(wrap, dots);

    // Preload the second photo so the first advance has nothing to wait for.
    loadCarouselFrame(dom.all('.popup-thumb', wrap)[1]);
  }

  /** Gives a frame its src the first time it is needed. */
  function loadCarouselFrame(frame) {
    if (frame && frame.dataset.src) {
      frame.src = frame.dataset.src;
      delete frame.dataset.src;
    }
  }

  /* A frame is revealed only once its image has loaded (or failed), so no
     blank box shows between photos, and the next one is preloaded. The dot
     moves at once; `pending` makes the latest of several quick clicks win. */
  const CAROUSEL_WAIT_MS = 2500;

  /** Shows one frame of a carousel, by index, and lights its dot. */
  function showCarouselFrame(wrap, index) {
    const frames = dom.all('.popup-thumb', wrap);
    const dots = dom.all('.popup-thumb-dot', wrap);

    if (frames.length < 2) return;

    const to = ((index % frames.length) + frames.length) % frames.length;
    const target = frames[to];

    dots.forEach((dot, i) => dot.classList.toggle('is-current', i === to));
    wrap.dataset.pending = String(to);
    loadCarouselFrame(target);

    const reveal = () => {
      if (wrap.dataset.pending !== String(to)) return;

      delete wrap.dataset.pending;
      dom.all('.popup-thumb', wrap).forEach((frame, i) => { frame.hidden = i !== to; });
      loadCarouselFrame(frames[(to + 1) % frames.length]);
    };

    if (target.complete && target.naturalWidth > 0) {
      reveal();

      return;
    }

    target.addEventListener('load', reveal, { once: true });
    target.addEventListener('error', reveal, { once: true });
    // A photo that never answers must not freeze the carousel on the old one.
    setTimeout(reveal, CAROUSEL_WAIT_MS);
  }

  /** Which frame is showing, read off the DOM rather than remembered - or the
      one still loading on its way in, so quick swipes and presses each count. */
  function currentCarouselFrame(wrap) {
    if (wrap.dataset.pending !== undefined) return Number(wrap.dataset.pending);

    return Math.max(0, dom.all('.popup-thumb', wrap).findIndex((f) => !f.hidden));
  }

  /* One timer for the whole page (a timer per build leaks on rebuild); it
     advances whichever carousel is on screen. Pressing an arrow or dot stops
     it for good; hovering pauses it. */
  let carouselTimer = null;

  function stopCarousels() {
    clearInterval(carouselTimer);
    carouselTimer = null;
  }

  function startCarousels() {
    stopCarousels();

    // Reduced motion: no auto-advance; arrows and dots still work.
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    carouselTimer = setInterval(() => {
      const wrap = dom.one('#place-modal .popup-thumb-wrap--carousel');

      if (!wrap || wrap.dataset.settled === '1' || wrap.matches(':hover')) return;

      showCarouselFrame(wrap, currentCarouselFrame(wrap) + 1);
    }, CAROUSEL_MS);
  }

  /* ---- The photo overlay ----
     A click on a sheet photo opens it large, over everything, in the full
     review's livery (.review-popover): arrows, the arrow keys and a swipe step
     through the place's photos, and closing leaves the sheet's slideshow on
     the photo the reader stopped at. Built once, on first use, and reused. */
  const photoOverlay = (() => {
    let box = null;
    let img = null;
    let count = null;
    let arrows = [];
    let photos = [];
    let index = 0;
    let fromWrap = null;
    let opener = null;

    function build() {
      box = el('div', 'photo-overlay');
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.hidden = true;

      const figure = el('div', 'photo-overlay-figure');

      img = el('img', 'photo-overlay-img');
      img.draggable = false;
      count = el('span', 'photo-overlay-count');

      const close = buttonNode('photo-overlay-close', { ariaLabel: 'Close photo', title: 'Close', icon: UI_ICON_CLOSE });
      const prev = buttonNode('photo-overlay-arrow photo-overlay-arrow--prev', {
        ariaLabel: 'Previous photo', title: 'Previous photo', icon: carouselArrowSvg,
      });
      const next = buttonNode('photo-overlay-arrow photo-overlay-arrow--next', {
        ariaLabel: 'Next photo', title: 'Next photo', icon: carouselArrowSvg,
      });

      close.addEventListener('click', hide);
      prev.addEventListener('click', () => show(index - 1));
      next.addEventListener('click', () => show(index + 1));
      arrows = [prev, next];

      figure.append(img, prev, next, count, close);
      box.appendChild(figure);
      // A click on the dimmed backdrop, outside the photo, closes it.
      // Pressed AND released there: a drag from the photo that ends on the
      // backdrop is a swipe gone wide, not a request to close.
      let pressedOnBackdrop = false;

      box.addEventListener('pointerdown', (e) => { pressedOnBackdrop = e.target === box; });
      box.addEventListener('click', (e) => { if (e.target === box && pressedOnBackdrop) hide(); });
      dom.insert(document.body, box);
    }

    function show(i) {
      const n = photos.length;

      index = ((i % n) + n) % n;
      img.src = proxiedImageSrc(photos[index], { width: 900 });
      dom.text(count, (index + 1) + ' / ' + n);
      // Warm the next one, so stepping forward has nothing to wait for.
      if (n > 1) new Image().src = proxiedImageSrc(photos[(index + 1) % n], { width: 900 });
    }

    function open(wrap, at, name) {
      if (!box) build();

      fromWrap = wrap;
      opener = document.activeElement;
      photos = dom.all('.popup-thumb', wrap).map((frame) => frame.dataset.photo).filter(Boolean);

      if (!photos.length) return;

      // Looking at the photos is a choice; the slideshow stops for good.
      wrap.dataset.settled = '1';
      img.alt = name;
      box.setAttribute('aria-label', 'Photos of ' + name);
      arrows.forEach((arrow) => { arrow.hidden = photos.length < 2; });
      count.hidden = photos.length < 2;
      show(at);
      box.hidden = false;
      // Force a reflow so the fade starts from the closed state.
      void box.offsetHeight;
      box.classList.add('is-open');
      dom.one('.photo-overlay-close', box).focus();
    }

    function hide() {
      if (!box || box.hidden) return;

      box.classList.remove('is-open');
      box.hidden = true;

      if (fromWrap && fromWrap.isConnected && photos.length > 1) showCarouselFrame(fromWrap, index);
      if (opener && opener.focus && opener.isConnected) opener.focus();

      fromWrap = null;
      opener = null;
    }

    // Capture phase: Escape closes the overlay only, never the sheet beneath.
    window.addEventListener('keydown', (e) => {
      if (!box || box.hidden) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        hide();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopImmediatePropagation();
        show(index + (e.key === 'ArrowRight' ? 1 : -1));
      } else if (e.key === 'Tab') {
        // A modal: Tab cycles its own buttons rather than walking off behind it.
        const stops = dom.all('button', box).filter((b) => !b.hidden);
        const at = stops.indexOf(document.activeElement);

        e.preventDefault();
        stops[(at + (e.shiftKey ? -1 : 1) + stops.length) % stops.length].focus();
      }
    }, true);

    return { open, close: hide, step: (d) => show(index + d) };
  })();

  /* ---- Swipe and drag ----
     A finger or a mouse dragged sideways across a sheet photo, or the
     overlay's, steps the slideshow: left for the next photo, right for the
     last. Anything shorter or more up-and-down than sideways stays a click
     (or a scroll - the photo is touch-action: pan-y), and the click that ends
     a swipe is swallowed so it cannot also open the overlay. Delegated, like
     the arrows, so a re-rendered sheet needs nothing rebound. */
  const SWIPE_PX = 40;
  let swipeStart = null;
  let swallowClick = false;

  const SWIPE_TARGETS = '.popup-thumb-wrap--carousel .popup-thumb, .photo-overlay-img';

  document.addEventListener('pointerdown', (e) => {
    const target = e.button === 0 && e.target.closest ? e.target.closest(SWIPE_TARGETS) : null;

    swipeStart = target ? { target, x: e.clientX, y: e.clientY, id: e.pointerId } : null;
  });

  document.addEventListener('pointercancel', () => { swipeStart = null; });

  document.addEventListener('pointerup', (e) => {
    if (!swipeStart || e.pointerId !== swipeStart.id) return;

    const { target } = swipeStart;
    const dx = e.clientX - swipeStart.x;
    const dy = e.clientY - swipeStart.y;

    swipeStart = null;

    if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) <= Math.abs(dy)) return;

    const step = dx < 0 ? 1 : -1;

    swallowClick = true;
    setTimeout(() => { swallowClick = false; }, 0);

    if (target.classList.contains('photo-overlay-img')) {
      photoOverlay.step(step);

      return;
    }

    const wrap = target.closest('.popup-thumb-wrap');

    // A swipe stops the auto-advance for good, as a press on an arrow does.
    wrap.dataset.settled = '1';
    showCarouselFrame(wrap, currentCarouselFrame(wrap) + step);
  });

  document.addEventListener('click', (e) => {
    if (!swallowClick) return;

    swallowClick = false;
    e.preventDefault();
    e.stopPropagation();
  }, true);

  // A dragged photo would otherwise lift off as the browser's drag image.
  document.addEventListener('dragstart', (e) => {
    if (e.target.closest && e.target.closest('.popup-thumb, .photo-overlay-img')) e.preventDefault();
  });

  /** A sheet photo that opens the overlay: a button to the keyboard too. */
  function photoOpener(img) {
    img.tabIndex = 0;
    img.setAttribute('role', 'button');
    img.setAttribute('aria-label', 'View photos of ' + img.alt);
  }

  // Enter or Space on a focused sheet photo opens it, as a click does.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;

    const photo = e.target.closest ? e.target.closest('#place-modal .popup-thumb[role="button"]') : null;

    if (!photo) return;

    e.preventDefault();
    photo.click();
  });

  // A click on the sheet's photo - any photo, one or many - opens the overlay
  // on it. The no-photo placeholder has nothing to show larger.
  document.addEventListener('click', (e) => {
    const photo = e.target.closest ? e.target.closest('#place-modal .popup-thumb') : null;

    if (!photo || photo.classList.contains('popup-thumb-fallback') || !photo.dataset.photo) return;

    const wrap = photo.closest('.popup-thumb-wrap');

    photoOverlay.open(wrap, currentCarouselFrame(wrap), photo.alt || '');
  });

  /** The thumbnail, wrapped in the featured ring when it earns one. */
  function thumbWithRingNode(p, className, frameClass, lazy) {
    // 60px (the featured frame) for the 52px square too, so both share one
    // cached copy.
    const img = thumbNode(p, className, lazy, 60);

    if (! isFeaturedForDisplay(p)) return img;

    const frame = el('div', frameClass);

    frame.appendChild(img);
    frame.appendChild(fromTrustedMarkup(featuredThumbRingSvg));
    frame.appendChild(el('span', 'sr-only', 'Featured'));

    return frame;
  }

  /* The prayer-space and clothing badges beside a price use their
     categories' icons from Site settings (window.__hcBadgeIcons, keyed by
     place field - see PlaceTags::BADGE_CATEGORIES). The fallback is the
     catalogue default, for a page whose config predates the key. No <title>:
     the words beside the badge (or the tag chips) already label it. */
  const BADGE_ICONS = window.__hcBadgeIcons || {};

  function badgeIconNode(field, fallback, size) {
    return tagIconNode(BADGE_ICONS[field] || fallback, size || 13);
  }

  function prayerSpaceNode(p) {
    if (! p.prayerSpace) return null;

    const badge = el('span', 'prayer-space-badge');

    badge.appendChild(badgeIconNode('prayerSpace', 'prayer'));

    return badge;
  }

  function clothingNode(p) {
    if (! p.clothing) return null;

    const badge = el('span', 'clothing-badge');

    badge.appendChild(badgeIconNode('clothing', 'shirt'));

    return badge;
  }


  // Social buttons overlaid on the photo's bottom-right corner (the report
  // button takes bottom-left).
  const facebookIconSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><title>Facebook</title><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const instagramIconSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><title>Instagram</title><rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" stroke-width="1.6"/><circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="1.6"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor"/></svg>';
  // "i in a circle", for the report button on the photo.
  const reportIconSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><line x1="12" y1="11" x2="12" y2="16.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="12" cy="7.6" r="1.2" fill="currentColor"/></svg>';

  function socialLinksNode(p) {
    if (! p.facebook && ! p.instagram) return null;

    const wrap = el('span', 'popup-thumb-social-links');
    const add = (url, network, icon) => {
      if (! url) return;

      const label = 'Visit ' + p.name + '’s ' + network + ' page';

      wrap.appendChild(linkNode(url, 'popup-thumb-social-btn', {
        newTab: true, title: label, ariaLabel: label, icon,
      }));
    };

    add(p.facebook, 'Facebook', facebookIconSvg);
    add(p.instagram, 'Instagram', instagramIconSvg);

    return wrap;
  }

  // Brand-colour order/booking badges. Uber Eats and DoorDash are plain SVGs
  // (their favicons blur this small); SkipTheDishes is its real favicon.
  const uberEatsIconSvg = '<svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><title>Order on Uber Eats</title><circle cx="12" cy="12" r="11" fill="#06C167"/><text x="12" y="16.5" font-family="Arial, Helvetica, sans-serif" font-size="13" font-weight="700" fill="#000" text-anchor="middle">U</text></svg>';
  // White initial on DoorDash red: black fails contrast at this size.
  const doorDashIconSvg = '<svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><title>Order on DoorDash</title><circle cx="12" cy="12" r="11" fill="#FF3008"/><text x="12" y="16.5" font-family="Arial, Helvetica, sans-serif" font-size="13" font-weight="700" fill="#fff" text-anchor="middle">D</text></svg>';
  // OpenTable red, white initial, for the same reason.
  const openTableIconSvg = '<svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><title>Book on OpenTable</title><circle cx="12" cy="12" r="11" fill="#DA3743"/><text x="12" y="16.5" font-family="Arial, Helvetica, sans-serif" font-size="13" font-weight="700" fill="#fff" text-anchor="middle">O</text></svg>';
  const skipTheDishesIconSvg = '<img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAABmklEQVR4AdyQP0iVURjGf+dcE6LcamqSQpDEUBwjsiHCuzQVtEmtERQRReZXOVRuEdIiQdBQGDR1iaCCCoxSakl3Qfw3OAiK93qPv+9TLs66+X3n4X2f57zvc855I3v89pNBekpLekhvesTJNMSxlNGT3lEqYkZbGqArPea4vHsbHcZYzMCNs6wyR+ILdUao8crZfmaa28bf4golJtngKoFx+YSYFKOFgeIwJZo5TDuHuA6cE/9IZKoD7h8h/wJTavM0cVf6Qr19ywAOsqFc5ZTiCTdL4gyJX+EeQ0QuyWflreYfwn2eyH+IhS2DGtckddZ5w0pRLNUKxtMwnZofVRgVZU36ndWY+XvzsWhBq+SP5fmV85Mvy5fFktppTcvmmH8Huqmz5nPbiNwIGa+jJ751o0qiy/hX5OslgQm1HqqcV1gRHaKJA/T7hM7wgOdyov8dAn2SXprJi8u0MGLzTfWL4pk1F+SfzPuoUbG2sWIY5KuoeJ1vDmzR+DHcYsb4v9AHqXjazwbPtGi0o/cOsps07qZpZ8+eDTYBAAD//8BCJiEAAAAGSURBVAMAHtB9IV3VaRcAAAAASUVORK5CYII=" width="14" height="14" alt="" class="popup-order-logo">';

  /* Order and booking links, labelled, under the photo. OpenTable shows the
     link only - openTableReviewScore is kept in the data but not shown, by
     choice. */
  function deliveryPlatformLinksNode(p) {
    if (! p.ubereats && ! p.skipthedishes && ! p.doordash && ! p.openTableUrl) return null;

    const wrap = el('div', 'popup-order-links');
    const add = (url, label, icon, verb) => {
      if (! url) return;

      const title = verb === 'book'
        ? 'Book a table at ' + p.name + ' on ' + label
        : 'Order ' + p.name + ' on ' + label;

      wrap.appendChild(linkNode(url, 'popup-order-link', {
        newTab: true, title, ariaLabel: title, icon, label,
      }));
    };

    // Booking first, then the delivery apps in the order the site owner set.
    add(p.openTableUrl, 'OpenTable', openTableIconSvg, 'book');
    add(p.ubereats, 'Uber Eats', uberEatsIconSvg);
    add(p.doordash, 'DoorDash', doorDashIconSvg);
    add(p.skipthedishes, 'SkipTheDishes', skipTheDishesIconSvg);

    return wrap;
  }
  // Decorative ring and star around a featured sidebar thumbnail (sr-only
  // text carries the meaning).
  const featuredThumbRingSvg = '<svg class="place-thumb-ring" viewBox="0 0 52 52" aria-hidden="true" focusable="false"><rect x="1.5" y="1.5" width="49" height="49" rx="11" fill="none" stroke="var(--brand)" stroke-width="2.5"/><circle cx="45" cy="45" r="8.5" fill="var(--brand)" stroke="var(--surface)" stroke-width="2"/><path d="M45 41 L46.1 43.5 L48.8 43.9 L46.9 45.8 L47.4 48.5 L45 47.2 L42.6 48.5 L43.1 45.8 L41.2 43.9 L43.9 43.5 Z" fill="#fff"/></svg>';
  // The star alone, for the featured carousel (its outline is a CSS border).
  const featuredStarBadgeSvg = '<svg class="featured-thumb-badge" viewBox="0 0 17 17" aria-hidden="true" focusable="false"><circle cx="8.5" cy="8.5" r="8.5" fill="var(--brand)" stroke="var(--surface)" stroke-width="2"/><path d="M8.5 4.6 L9.4 6.7 L11.7 7 L10 8.6 L10.4 10.9 L8.5 9.8 L6.6 10.9 L7 8.6 L5.3 7 L7.6 6.7 Z" fill="#fff"/></svg>';

  // ==========================================================================
  // MAP MARKERS & POPUPS
  // ==========================================================================

  /* ---- Place tags ----
     Category and cuisine chips on the card, the search suggestion and the
     sheet, in the filter row's words and glyphs. window.__hcTags (see
     App\Services\PlaceTags) lists the enabled categories and the cuisines
     some live place carries. tagsForPlace() mirrors PlaceTags::forPlace(),
     and the chip markup mirrors partials/place-tags.blade.php class for class
     so dom.patch adopts the server-rendered card unchanged. */
  const TAG_CATALOGUE = window.__hcTags || { categories: {}, cuisines: {} };

  function tagsForPlace(p) {
    const tags = [];
    const categories = TAG_CATALOGUE.categories || {};
    const cuisines = TAG_CATALOGUE.cuisines || {};
    const carried = Array.isArray(p.cuisines) ? p.cuisines : [];

    for (const slug in categories) {
      const category = categories[slug];

      if (category && category.field && p[category.field]) {
        tags.push({ kind: 'category', slug: slug, label: category.label || slug, icon: category.icon || 'cutlery', href: category.href || '' });
      }
    }

    for (const slug in cuisines) {
      if (carried.indexOf(slug) !== -1) {
        const cuisine = cuisines[slug];

        tags.push({ kind: 'cuisine', slug: slug, label: cuisine.label || slug, icon: cuisine.icon || 'cutlery', href: cuisine.href || '' });
      }
    }

    return tags;
  }

  /* A tag or badge glyph: an empty <i class="ci ci-NAME"> that main.css masks
     out of the one glyph strip (App\Support\IconStrip), or an <img> for an
     uploaded icon (a path). The server writes the same - partials/place-tags
     and partials/place-badge-icon - and the first render adopts its nodes, so
     the two must match to the byte (tests/browser/card-glyphs). An <svg><use>
     per glyph cost a shadow tree each.

     A chip passes no size and gets .place-tag-icon, which sizes it (12px; 13px
     on the sheet). A badge passes its size, which an <img> takes as its
     width/height and a glyph from the badge's CSS. */
  function tagIconNode(icon, size) {
    const className = size ? '' : 'place-tag-icon';

    if (icon.charAt(0) === '/') {
      const px = size || 12;
      const img = el('img', className || null);

      img.src = icon;
      img.alt = '';
      img.width = px;
      img.height = px;

      return img;
    }

    // A class from Site settings, but only ever a class: an unknown name has
    // no rule and draws nothing, as a <use> of a missing symbol did.
    return el('i', (className ? className + ' ' : '') + 'ci ci-' + icon);
  }

  /* The tag slugs a query lands on for one place (named cuisines it carries,
     named categories it is in), so the matching chips can be lit. */
  function searchTagHits(p, query) {
    const q = (query || '').toLowerCase().trim();

    if (!q) return [];

    const hits = [];
    const carried = Array.isArray(p.cuisines) ? p.cuisines : [];

    queryCuisines(q).forEach(slug => {
      if (carried.indexOf(slug) !== -1) hits.push(slug);
    });

    const fields = queryCategories(q);
    const categories = TAG_CATALOGUE.categories || {};

    for (const slug in categories) {
      const category = categories[slug];

      if (category && fields.indexOf(category.field) !== -1 && p[category.field]) hits.push(slug);
    }

    return hits;
  }

  /* The chips. null when the place has none, so nothing empty is appended.

     options.className  place-tags on a card, popup-tags on the sheet
     options.linked     real <a href> chips (the sheet); off inside a card,
                        which is itself an <a>
     options.query      the search text, to light the chips it landed on
     options.hitsFirst  lit chips first - the dropdown clips to one row

     A chip is also lit when it is the active filter. */
  function placeTagsNode(p, options) {
    const opts = options || {};
    let tags = tagsForPlace(p);

    if (!tags.length) return null;

    const hits = searchTagHits(p, opts.query);
    const lit = tag => hits.indexOf(tag.slug) !== -1
      || (tag.kind === 'cuisine' && tag.slug === activeCuisine)
      || (tag.kind === 'category' && !!activeAmenityCategories[tag.slug]);

    if (opts.hitsFirst) tags = tags.filter(lit).concat(tags.filter(tag => !lit(tag)));

    const wrap = el('div', opts.className || 'place-tags');

    tags.forEach(tag => {
      const chip = el(opts.linked ? 'a' : 'span', 'place-tag place-tag--' + tag.kind + (lit(tag) ? ' is-lit' : ''));

      chip.dataset.tagKind = tag.kind;
      chip.dataset.tag = tag.slug;

      if (opts.linked) {
        chip.href = tag.href;
        chip.title = 'Show every ' + tag.label + ' place';
      }

      chip.appendChild(tagIconNode(tag.icon));
      chip.appendChild(el('span', 'place-tag-label', tag.label));
      wrap.appendChild(chip);
    });

    return wrap;
  }

  /* A count as PHP's number_format() prints it on the server's cards:
     "1,039". Not toLocaleString(): the visitor's locale must not reformat a
     number the server printed, and the first locale-aware call on a page
     loads the browser's ICU data (about 30 ms on a phone) in the middle of
     the boot. Counts are whole numbers. */
  function groupThousands(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* The Google rating with its logo, matching facebookReviewNode(). compact
     gives "(1,039)" instead of "(1,039 ratings)", for the 236px featured card
     whose one-line meta row has no room for the word. */
  function ratingBadgeNode(p, withCount, compact) {
    if (typeof p.rating !== 'number') return document.createTextNode('New');

    const count = typeof p.ratingCount === 'number' ? groupThousands(p.ratingCount) : null;
    const badge = el('span', 'google-rating-badge');

    // Star and numeral are separate nodes: the numeral in star gold would
    // fail contrast.
    badge.appendChild(fromTrustedMarkup(googleMark));
    badge.appendChild(el('span', 'rating-star', '★'));
    badge.appendChild(document.createTextNode(
      ' ' + p.rating.toFixed(1)
        + (withCount && count ? ' (' + count + (compact ? ')' : ' ratings)') : '')
    ));

    return badge;
  }


  // Facebook's "% recommend" figure, as its own badge beside the Google
  // stars - it is a different scale. Only for places with that data.
  function facebookReviewNode(p) {
    if (typeof p.facebookReviewPercent !== 'number') return null;

    const pct = Math.round(p.facebookReviewPercent);
    const count = typeof p.facebookReviewCount === 'number' ? groupThousands(p.facebookReviewCount) : null;
    const badge = el('span', 'popup-facebook-review');

    badge.title = 'Facebook: ' + pct + '% recommend'
      + (count ? ' (' + count + (p.facebookReviewCount === 1 ? ' review' : ' reviews') + ')' : '');

    badge.appendChild(fromTrustedMarkup(facebookIconSvg));
    badge.appendChild(document.createTextNode(pct + '%' + (count ? ' (' + count + ')' : '')));

    return badge;
  }


  function buildPopupNode(p) {
    const frag = document.createDocumentFragment();
    const slug = placeSlug(p);



    // PHOTO, with the report button and any social links over it.
    const thumbWrap = el('div', 'popup-thumb-wrap');

    // 420px, not the 354 it measures on a phone: the sheet is wider on a
    // desktop.
    thumbWrap.appendChild(thumbNode(p, 'popup-thumb', null, 420));
    addCarousel(thumbWrap, p);
    // Labelled "Report": a bare ⓘ reads as "more information". The accessible
    // name stays the full sentence.
    thumbWrap.appendChild(buttonNode('popup-thumb-report-btn', {
      slug, ariaLabel: 'Report this listing', title: 'Report this listing',
      icon: reportIconSvg, label: 'Report',
    }));

    const social = socialLinksNode(p);

    if (social) thumbWrap.appendChild(social);

    frag.appendChild(thumbWrap);

    const order = deliveryPlatformLinksNode(p);

    if (order) frag.appendChild(order);

    // THE PAGE'S H1 WHILE THE SHEET IS ITS <main>: a place page ships the
    // sheet as <main> with an <h1> title (partials/ssr-place-modal), and the
    // morph keeps a node only if its tag matches. Elsewhere the page has its
    // own H1 and this stays a plain line.
    const sheetIsMain = (dom.id('place-modal-backdrop') || {}).tagName === 'MAIN';

    frag.appendChild(el(sheetIsMain ? 'h1' : 'div', 'popup-title', p.name));

    const closedNotice = closedNoticeNode(p, true);

    if (closedNotice) frag.appendChild(closedNotice);

    // RATING ROW
    const ratingRow = el('div', 'popup-rating');
    const ratingInner = el('span');

    ratingInner.appendChild(ratingBadgeNode(p, true));

    const facebook = facebookReviewNode(p);

    if (facebook) {
      ratingInner.appendChild(separatorNode());
      ratingInner.appendChild(facebook);
    }

    if (priceLabel(p)) {
      // Separator and price in one group, so a wrap cannot leave the "·" behind.
      const priceGroup = el('span', 'popup-price-group');

      priceGroup.appendChild(separatorNode());
      priceGroup.appendChild(el('span', 'price-tag', priceLabel(p)));
      ratingInner.appendChild(priceGroup);

      const prayer = prayerSpaceNode(p);
      const clothes = clothingNode(p);

      if (prayer) { ratingInner.appendChild(document.createTextNode(' ')); ratingInner.appendChild(prayer); }
      if (clothes) { ratingInner.appendChild(document.createTextNode(' ')); ratingInner.appendChild(clothes); }
    }

    // Open or closed now, last on the same line: the rating, the price and
    // whether it is open are what people decide on, so they read together.
    // Worded on the server and sent with the rest of the place
    // (OpeningHours::statusOf), so it arrives with the hours; nothing until then.
    // Separator and status in one group, as the phone's, so a wrap cannot
    // leave the "·" at a line's end.
    if (p.openStatus) {
      const openGroup = el('span', 'popup-open-group');

      openGroup.appendChild(separatorNode());
      openGroup.appendChild(openStatusNode(p.openStatus));
      ratingInner.appendChild(openGroup);
    }

    ratingRow.appendChild(ratingInner);
    frag.appendChild(ratingRow);

    // ADDRESS, with the phone beside it (sheet only).
    const address = el('div', 'popup-address', p.address);

    if (p.phone) {
      // Separator and number in one group so a wrap cannot split them (as
      // metaSegment() does on the cards).
      const phoneGroup = el('span', 'popup-phone-group');

      phoneGroup.appendChild(separatorNode());
      phoneGroup.appendChild(linkNode('tel:' + p.phone.replace(/[^\d+]/g, ''), 'popup-phone', {
        title: 'Call ' + p.name, icon: phoneIconSvg, text: p.phone,
      }));
      address.appendChild(phoneGroup);
    }

    frag.appendChild(address);

    // THE SERVICES on a line of their own, the last of the facts above the
    // row of controls - plain icon-and-word facts, not chips, since they are
    // not links. As in ssr-place-modal.
    const services = el('div', 'popup-services');
    const service = (word, icon) => {
      const item = el('span', 'popup-service');

      item.title = word + ' available';
      item.appendChild(fromTrustedMarkup(icon));
      item.appendChild(el('span', 'popup-service-label', word));
      services.appendChild(item);
    };

    if (p.dinein) service('Dine-in', dineInIconSvg);
    if (p.takeout) service('Takeout', takeoutIconSvg);
    if (p.delivery) service('Delivery', deliveryIconSvg);
    if (services.childNodes.length) frag.appendChild(services);

    // ONE ROW OF CONTROLS, wrapping: distance, the tags (links; the delegated
    // handler beside the cuisine row turns a click into an in-place filter),
    // then the actions - one height, one family - as in ssr-place-modal.
    const metaRow = el('div', 'popup-meta-row');

    if (userLocation) {
      const km = haversineKm(userLocation.lat, userLocation.lng, p.lat, p.lng);

      metaRow.appendChild(el('span', 'popup-distance', km.toFixed(1) + ' km away'));
    }

    const popupTags = placeTagsNode(p, { className: 'popup-tags', linked: true });

    if (popupTags) metaRow.appendChild(popupTags);

    const actions = el('div', 'popup-meta-actions');

    // Website and Share carry their words - neither glyph is self-explanatory.
    if (p.website) {
      actions.appendChild(linkNode(p.website, 'popup-icon-btn popup-icon-btn--labelled popup-website-btn', {
        newTab: true, ariaLabel: 'Visit website', title: 'Visit ' + p.name + '’s website',
        icon: websiteIconSvg, label: 'Website',
      }));
    }

    actions.appendChild(buttonNode('popup-icon-btn popup-icon-btn--labelled popup-share-btn', {
      slug, ariaLabel: 'Share', title: 'Share', icon: shareIconSvg, label: 'Share',
    }));

    metaRow.appendChild(actions);
    frag.appendChild(metaRow);

    // Filled later by loadPlaceReviews; the slug lets a late response check
    // it is still filling the right sheet. Above the description, as in
    // ssr-place-modal.blade.php.
    const reviewsSlot = el('div', 'popup-reviews-slot');

    reviewsSlot.dataset.reviewsSlot = slug;
    // For the score card, which renders asynchronously.
    if (p.rating) reviewsSlot.dataset.rating = p.rating;
    if (p.ratingCount) reviewsSlot.dataset.ratingCount = p.ratingCount;
    frag.appendChild(reviewsSlot);

    // ABOUT: the description and the hours in one panel, built like the
    // reviews one above it - as in ssr-place-modal.blade.php. Full markup
    // once fetched; the boot summary until then, so the panel is never blank.
    // The hours arrive with the rest of the place, like the full description.
    const aboutText = p.details
      ? detailsNode(p.details)
      : (p.summary ? el('div', 'popup-details popup-details--full', p.summary) : null);
    const aboutHours = p.hoursLines && p.hoursLines.length ? hoursNode(p.hoursLines) : null;

    if (aboutText || aboutHours) {
      const about = el('section', 'popup-about');
      const head = el('div', 'popup-about-head');
      const label = el('h2', 'popup-about-label', 'About');

      // The name is sr-only, as on "Reviews": "About <place>" to a reader.
      label.appendChild(el('span', 'sr-only', ' ' + p.name));
      head.appendChild(label);
      about.appendChild(head);
      if (aboutText) about.appendChild(aboutText);
      if (aboutHours) about.appendChild(aboutHours);
      frag.appendChild(about);
    }

    const links = el('div', 'popup-actions');

    links.appendChild(linkNode(googleMapsLink(p), 'popup-link popup-link-google', {
      newTab: true, title: 'Open ' + p.name + ' in Google Maps',
      icon: googleMapsIconSvg, label: 'Open Google Maps',
    }));
    links.appendChild(buttonNode('popup-link popup-link-apple', {
      slug, ariaLabel: 'Open in Apple Maps', title: 'Open ' + p.name + ' in Apple Maps',
      icon: appleMapsIconSvg, label: 'Open Apple Maps',
    }));

    frag.appendChild(links);

    return frag;
  }

  /* The marker pin: a flat teardrop with a white centre and one subtle
     vertical shade - the white centre is what makes it read as a pin at 28px.
     Colour comes from CSS (fill is currentColor; .marker-pin rules in
     main.css, off --brand), so the admin's brand colour moves the pins. The
     shade overlays black/white at low opacity, so it suits any pin colour. */
  const MARKER_PIN_PATH = 'M14 38.5C14 38.5 3 23.2 3 13.9A11 11 0 1 1 25 13.9C25 23.2 14 38.5 14 38.5Z';

  /* One shared <defs> for the shade, injected once for every pin. */
  (() => {
    // Parsed as one <svg>, so the gradient and its stops land in the SVG
    // namespace; the pins' fill="url(#hc-pin-shade)" finds it by id.
    const defs = fromTrustedMarkup(
      '<svg width="0" height="0" aria-hidden="true">' +
        '<defs>' +
          '<linearGradient id="hc-pin-shade" x1="0" y1="0" x2="0" y2="1">' +
            '<stop offset="0" stop-color="#fff" stop-opacity="0.22"/>' +
            '<stop offset="0.55" stop-color="#fff" stop-opacity="0"/>' +
            '<stop offset="1" stop-color="#000" stop-opacity="0.16"/>' +
          '</linearGradient>' +
        '</defs>' +
      '</svg>'
    ).firstChild;

    defs.style.position = 'absolute';

    dom.insert(document.body, defs);
  })();

  // Category slug (URL/DOM spelling) => the places.json field it filters on.
  //
  // Must list EVERY slug in App\Services\CategoryCatalogue, not just the
  // enabled ones - a missing slug filters on p[undefined] and silently
  // matches nothing. tests/Feature/CategoryFilterTest.php compares the lists.
  const AMENITY_FIELDS = {
    'dine-in': 'dinein',
    takeout: 'takeout',
    delivery: 'delivery',
    'prayer-space': 'prayerSpace',
    patio: 'patio',
    clothing: 'clothing',
    'place-of-worship': 'placeOfWorship',
    grocery: 'grocery',
    pharmacy: 'pharmacy',
    reservable: 'reservable',
    restroom: 'restroom',
    'live-music': 'liveMusic',
    'good-for-children': 'goodForChildren',
    'good-for-groups': 'goodForGroups',
    vegetarian: 'vegetarianOptions',
    breakfast: 'servesBreakfast',
    brunch: 'servesBrunch',
    lunch: 'servesLunch',
    dinner: 'servesDinner',
    dessert: 'servesDessert',
    // Both from Google's own type classification - see CategoryCatalogue.
    restaurant: 'restaurant',
    'halal-tagged': 'halalTagged',
    'curbside-pickup': 'curbsidePickup',
    coffee: 'servesCoffee',
    'dog-friendly': 'allowsDogs',
    'kids-menu': 'menuForChildren',
    'sports-viewing': 'goodForWatchingSports',
    'wheelchair-accessible': 'wheelchairAccessible',
    'free-parking': 'freeParking',
    'serves-beer': 'servesBeer',
    'serves-wine': 'servesWine',
    'serves-cocktails': 'servesCocktails'
  };

  const MARKER_PIN_SVG =
    '<svg width="28" height="40" viewBox="0 0 28 40" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="' + MARKER_PIN_PATH + '" fill="currentColor"/>' +
      '<path d="' + MARKER_PIN_PATH + '" fill="url(#hc-pin-shade)"/>' +
      '<circle cx="14" cy="13.9" r="4.6" fill="#fff"/>' +
    '</svg>';

  /* The pin's shape, parsed once and cloned into each pin, instead of every
     pin parsing its own copy of the markup. A wrapping <span> is what
     animates, not the <svg>: Safari is unreliable about transforms on an
     <svg> root. */
  const MARKER_SHAPE = fromTrustedMarkup(
    '<span class="marker-icon-shape">' + MARKER_PIN_SVG + '</span>'
  ).firstChild;

  /* A pin's markup is built when Leaflet draws the pin, not when the marker
     is made: a pin off screen or filtered out costs nothing, and one that
     leaves the view is not kept as detached DOM. Leaflet calls createIcon
     again each time the pin is drawn (and on setIcon), so each draw gets a
     fresh element. */
  const MarkerPinIcon = L.DivIcon.extend({
    createIcon(oldIcon) {
      const pin = document.createElement('div');

      pin.className = this.options.pinClass;

      if (this.options.pinOpen) {
        ['active-ring', 'active-ring ring-2'].forEach(cls => {
          const ring = document.createElement('span');

          ring.className = cls;
          pin.appendChild(ring);
        });
      }

      pin.appendChild(MARKER_SHAPE.cloneNode(true));

      // DivIcon appends an Element given as html; not kept past the draw.
      this.options.html = pin;

      const icon = L.DivIcon.prototype.createIcon.call(this, oldIcon);

      this.options.html = '';

      return icon;
    },
  });

  /* The pin ranking from Site settings. Empty is fine: every pin falls back
     to the ordinary colour. */
  const PIN_ORDER = Array.isArray(window.__hcPinOrder) ? window.__hcPinOrder : [];

  // The marker carries an is-<slug> class for every matching category, plus
  // one pin-<slug> colour class chosen below. Open and featured beat the lot.
  function createMarkerIcon(place, isFeatured, isOpen) {
    let variant = '';
    const matched = [];

    for (const slug in AMENITY_FIELDS) {
      if (place && place[AMENITY_FIELDS[slug]]) {
        variant += ' is-' + slug;
        matched.push(slug);
      }
    }

    // Cuisines rank too, prefixed so they cannot collide with a category of
    // the same name (`vegetarian` is both). Same prefix in the ranking, the
    // --marker-* token and the class.
    for (const tag of (place && place.cuisines) || []) {
      if (typeof tag === 'string' && tag !== '') matched.push('cuisine-' + tag);
    }

    // The colour is decided here, not by stylesheet order: PIN_ORDER is the
    // admin's ranking, strongest first (App\Services\PinRanking), and the
    // first match wins.
    for (const slug of PIN_ORDER) {
      if (matched.indexOf(slug) !== -1) {
        variant += ' pin-' + slug;
        break;
      }
    }

    if (isFeatured) variant += ' is-featured';
    if (isOpen) variant += ' is-open';
    // The open pin's two rings and the shape are added when it is drawn -
    // see MarkerPinIcon.
    return new MarkerPinIcon({
      className: '',
      pinClass: 'marker-pin' + variant,
      pinOpen: !!isOpen,
      // Anchored on the tip (y=38.5), not the box bottom - the rest is room
      // for the shadow.
      iconSize: [28, 40],
      iconAnchor: [14, 38.5],
      popupAnchor: [0, -35]
    });
  }

  // Whether a place gets the featured treatment (pin, thumbnail ring, popup
  // border). Reads featuredSelection so the map and the featured strip can
  // never disagree; pickFeaturedSelection() must run before buildMarkers().
  function isFeaturedForDisplay(p) {
    return !!featuredSelection && featuredSelection.includes(p);
  }

  // ==========================================================================
  // PLACE MODAL - a place's details centred over the map, instead of
  // Leaflet's auto-panning popup (see "PLACE MODAL" in the CSS)
  // ==========================================================================
  const placeModalBackdrop = dom.id('place-modal-backdrop');
  const placeModalEl = dom.id('place-modal');
  const placeModalContent = dom.id('place-modal-content');
  const placeModalCloseBtn = dom.id('place-modal-close');

  // The selected place, which drives the animated pin. Set by setActivePin()
  // and deliberately kept when the sheet merely closes, so the pin, sidebar
  // highlight and URL still mark it; only Reset view or history clears it.
  let activePinPlace = null;

  /* ---- Distance line from you to the place ----
     Curved for legibility (a straight line reads as a border or road), with
     the distance written on it. Drawn only with both a location fix and a
     target. */
  let flightLine = null;
  let flightLabel = null;
  let flightPoints = null;
  let flightLabelWired = false;
  // A hovered pin beats the selection while hovered; letting go falls back
  // to the selection, or to nothing.
  let hoveredPlace = null;

  function clearFlightPath() {
    if (flightLine) { map.removeLayer(flightLine); flightLine = null; }
    if (flightLabel) { map.removeLayer(flightLabel); flightLabel = null; }
    flightPoints = null;
  }

  /* The label sits at the middle of the currently visible part of the line
     (recomputed on pan/zoom), so it stays on screen when zoomed into one end. */
  function positionFlightLabel() {
    if (!flightLabel || !flightPoints || !flightPoints.length) return;

    // Padded inwards, so the pill is not half over the edge of the map.
    const bounds = map.getBounds().pad(-0.08);
    const visible = flightPoints.filter(pt => bounds.contains(L.latLng(pt[0], pt[1])));

    if (!visible.length) {
      // None of the line is on screen: fall back to the apex.
      flightLabel.setLatLng(flightPoints[Math.floor(flightPoints.length / 2)]);

      return;
    }

    flightLabel.setLatLng(visible[Math.floor(visible.length / 2)]);
  }

  /* A quadratic Bézier sampled as 48 segments (Leaflet only draws polylines). */
  function arcPoints(from, to) {
    // Control point offset perpendicular by a share of the length, so the
    // bow keeps its shape at any distance.
    const mid = { lat: (from.lat + to.lat) / 2, lng: (from.lng + to.lng) / 2 };
    const dLat = to.lat - from.lat;
    const dLng = to.lng - from.lng;
    // Longitude degrees are shorter than latitude ones this far north;
    // without the correction the bow leans as the line changes direction.
    const shrink = Math.cos(mid.lat * Math.PI / 180) || 1;
    const control = {
      lat: mid.lat - (dLng * shrink) * 0.14,
      lng: mid.lng + (dLat / shrink) * 0.14,
    };

    const points = [];

    for (let i = 0; i <= 48; i++) {
      const t = i / 48;
      const inv = 1 - t;

      points.push([
        inv * inv * from.lat + 2 * inv * t * control.lat + t * t * to.lat,
        inv * inv * from.lng + 2 * inv * t * control.lng + t * t * to.lng,
      ]);
    }

    return points;
  }

  function drawFlightPath() {
    clearFlightPath();

    const target = hoveredPlace || activePinPlace;

    if (!userLocation || !target) return;
    if (typeof target.lat !== 'number' || typeof target.lng !== 'number') return;

    const from = { lat: userLocation.lat, lng: userLocation.lng };
    // The pin's position, not the record's - fanned-out pins are offset, and
    // a line stopping short of its pin looks broken.
    const [toLat, toLng] = pinLatLng(target);
    const to = { lat: toLat, lng: toLng };
    const points = arcPoints(from, to);

    flightLine = L.polyline(points, {
      // Follows the theme (black would vanish in dark mode).
      color: flightInk(),
      weight: 2,
      opacity: 0.85,
      // Dashed: a straight-line measurement, not a route.
      dashArray: '5 5',
      interactive: false,
      className: 'flight-path',
    }).addTo(map);

    const km = haversineKm(from.lat, from.lng, to.lat, to.lng);

    flightPoints = points;

    flightLabel = L.marker(points[Math.floor(points.length / 2)], {
      interactive: false,
      keyboard: false,
      icon: L.divIcon({
        className: 'flight-distance',
        html: '<span>' + km.toFixed(1) + ' km</span>',
        iconSize: [72, 22],
        iconAnchor: [36, 11],
      }),
    }).addTo(map);

    // Bound once - re-binding per draw would stack a listener per hover.
    if (!flightLabelWired) {
      flightLabelWired = true;
      map.on('move zoomend', positionFlightLabel);
    }

    positionFlightLabel();
  }

  function flightInk() {
    return document.documentElement.classList.contains('dark') ? '#f2f2f2' : '#1a1a1a';
  }

  function setActivePin(p) {
    if (activePinPlace === p) return;
    if (activePinPlace && markers[placeSlug(activePinPlace)]) {
      const prev = activePinPlace;
      markers[placeSlug(prev)].setIcon(createMarkerIcon(prev, isFeaturedForDisplay(prev), false));
    }
    activePinPlace = p;
    if (p && markers[placeSlug(p)]) {
      markers[placeSlug(p)].setIcon(createMarkerIcon(p, isFeaturedForDisplay(p), true));
    }

    drawFlightPath();
  }

  /* Google reviews, fetched per place when its sheet opens (kept out of the
     boot payload - most visitors never read them). Answers are cached for
     the page's life, empty ones included. */
  const reviewsBySlug = new Map();
  // In-flight requests, so the two quick renders of a sheet opened from its
  // URL share one fetch.
  const reviewsInFlight = new Map();

  function loadPlaceReviews(slug) {
    if (reviewsBySlug.has(slug)) return Promise.resolve(reviewsBySlug.get(slug));
    if (reviewsInFlight.has(slug)) return reviewsInFlight.get(slug);

    // fetchWithToken refreshes an expired page token on a long-open tab.
    const request = fetchWithToken('/api/reviews/' + encodeURIComponent(slug))
      .then(res => (res.ok ? res.json() : Promise.reject(new Error('http ' + res.status))))
      .then(data => {
        const reviews = (data && Array.isArray(data.reviews)) ? data.reviews : [];

        // Empty answers are cached too - most places have none.
        reviewsBySlug.set(slug, reviews);

        return reviews;
      })
      // A failure is NOT cached, so the next open retries.
      .catch(() => [])
      .finally(() => reviewsInFlight.delete(slug));

    reviewsInFlight.set(slug, request);

    return request;
  }

  /* The rest of a place, fetched when its sheet opens. The boot payload is a
     projection (see App\Services\PlaceProjection); this adds the full
     description, phone, website, social/delivery links and so on. Merged
     into the place object, so everything holding a reference to it sees the
     full record and a re-open renders from memory. */
  const placeDetailBySlug = new Map();

  /* A place page seeds its own detail (#hc-place-detail, JSON), so it is not
     fetched again. */
  (() => {
    const seed = dom.id('hc-place-detail');
    if (!seed || !seed.dataset.slug) return;

    try {
      placeDetailBySlug.set(seed.dataset.slug, JSON.parse(seed.textContent || '{}'));
    } catch {
      /* A malformed seed just means the fetch below does its normal job. */
    }
  })();

  function loadPlaceDetail(p) {
    const slug = placeSlug(p);

    if (placeDetailBySlug.has(slug)) {
      Object.assign(p, placeDetailBySlug.get(slug));
      return Promise.resolve(p);
    }

    return fetchWithToken('/api/places/' + encodeURIComponent(slug))
      .then(res => (res.ok ? res.json() : Promise.reject(new Error('http ' + res.status))))
      .then(data => {
        const detail = (data && typeof data === 'object' && !data.error) ? data : {};

        placeDetailBySlug.set(slug, detail);
        Object.assign(p, detail);

        // The detail carries the place's SEO title and full description,
        // which its title and meta description are worded from; re-word them
        // if this place is still the open one.
        if (activeSlug === placeSlug(p)) updatePageMeta();

        return p;
      })
      // A failure (typically a 403 from an expired page token) is NOT cached,
      // so the next open asks again rather than showing the summary forever.
      .catch(() => p);
  }

  /* Words to mark in reviews (e.g. "halal" - a reviewer's unprompted
     confirmation), from Site settings via a meta tag. Server twin:
     App\Support\ReviewText. */
  const HIGHLIGHT_RE = (() => {
    const raw = dom.one('meta[name="hc-review-highlight"]')?.content || '';
    const words = raw.split(',').map(w => w.trim()).filter(Boolean)
      // Settings input is data: quote it, never trust it as a pattern.
      .map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

    return words.length ? new RegExp('\\b(?:' + words.join('|') + ')\\b', 'gi') : null;
  })();

  /**
   * Review text with the configured words marked, as nodes. A stranger's
   * text never becomes markup: runs go in as text nodes around each <mark>.
   */
  function halalMarkedNodes(text) {
    const fragment = document.createDocumentFragment();

    if (! HIGHLIGHT_RE) {
      fragment.appendChild(document.createTextNode(text));

      return fragment;
    }

    // Fresh lastIndex: the regex is global and shared across every review.
    HIGHLIGHT_RE.lastIndex = 0;

    let at = 0;
    let match;

    while ((match = HIGHLIGHT_RE.exec(text)) !== null) {
      if (match.index > at) {
        fragment.appendChild(document.createTextNode(text.slice(at, match.index)));
      }

      fragment.appendChild(el('mark', 'popup-review-halal', match[0]));
      at = match.index + match[0].length;

      // A zero-length match would spin here forever.
      if (match[0] === '') HIGHLIGHT_RE.lastIndex++;
    }

    if (at < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(at)));
    }

    return fragment;
  }

  /* The stand-in avatar: initials on a colour derived from the name.
     MUST AGREE with App\Support\ReviewAvatar, which renders the same reviews
     server-side - change both together; ReviewAvatarTest pins the values.
     Iterates code points (for..of), not UTF-16 units, to match PHP on
     non-ASCII names. */
  function avatarHue(name) {
    let hash = 0;

    for (const ch of String(name)) {
      hash = (hash * 31 + ch.codePointAt(0)) % 1000003;
    }

    return hash % 360;
  }

  function avatarInitials(name) {
    const words = String(name).trim().split(/\s+/).filter(Boolean);

    if (words.length === 0) return '';

    // Array.from, not [0], so an astral first character is not split in half.
    const first = Array.from(words[0])[0] || '';
    const last = words.length > 1 ? (Array.from(words[words.length - 1])[0] || '') : '';

    return (first + last).toUpperCase();
  }

  const AVATAR_CLASS = 'popup-review-avatar';

  /**
   * The avatar: photo stacked over initials (as in
   * partials/review-avatar.blade.php), so a dead photo removing itself
   * uncovers the initials. null when there is neither.
   */
  function reviewAvatarNode(review) {
    const name = review.author || '';
    const initials = avatarInitials(name);
    const photo = review.authorPhoto || '';

    if (!initials && !photo) return null;

    const stack = el('span', 'review-avatar-stack');

    if (initials) {
      const circle = el('span', AVATAR_CLASS + ' review-avatar--initials', initials);

      // The stylesheet derives background and text colour from the hue.
      circle.style.setProperty('--avatar-hue', String(avatarHue(name)));
      // Decorative: the full name follows.
      circle.setAttribute('aria-hidden', 'true');
      stack.appendChild(circle);
    }

    if (photo) {
      const img = el('img', AVATAR_CLASS);

      img.src = proxiedImageSrc(photo, { fallback: false });
      img.alt = '';
      img.addEventListener('error', () => dom.remove(img));
      stack.appendChild(img);
    }

    return stack;
  }

  function reviewNode(review) {
    const rating = Math.max(0, Math.min(5, parseInt(review.rating, 10) || 0));
    const wrap = el('div', 'popup-review');
    const head = el('div', 'popup-review-meta');
    const stars = el('span', 'popup-review-stars');

    stars.setAttribute('aria-label', rating + ' out of 5');

    for (let i = 0; i < 5; i++) {
      const star = el('span', 'popup-review-star' + (i < rating ? ' is-on' : ''), '★');

      star.setAttribute('aria-hidden', 'true');
      stars.appendChild(star);
    }

    // The quote first; stars, a small avatar and the name follow as a footer.
    const text = el('p', 'popup-review-text');

    if (review.language && review.language !== 'en') text.lang = review.language;

    text.appendChild(halalMarkedNodes(review.text || ''));
    wrap.appendChild(text);

    head.appendChild(stars);

    // Attribution (name and link back to Google) is required - see
    // App\Services\ReviewStore.
    if (review.author) {
      const avatar = reviewAvatarNode(review);

      if (avatar) head.appendChild(avatar);
      head.appendChild(el('span', 'popup-review-author', review.author));
    }
    if (review.relative) head.appendChild(el('span', 'popup-review-when', review.relative));

    if (review.uri) {
      head.appendChild(linkNode(review.uri, 'popup-review-link', {
        newTab: true, text: 'Read on Google',
      }));
    }

    wrap.appendChild(head);

    return wrap;
  }

  /* Five stars for a fractional score, snapped to the nearest half. Must
     match partials/star-row.blade.php. */
  function starRow(value) {
    const stars = document.createDocumentFragment();
    const halves = Math.round((Number(value) || 0) * 2);

    for (let i = 0; i < 5; i++) {
      const filled = halves - (i * 2);
      const state = filled >= 2 ? ' is-on' : (filled === 1 ? ' is-half' : '');
      const star = el('span', 'popup-score-star' + state, '★');

      star.setAttribute('aria-hidden', 'true');
      stars.appendChild(star);
    }

    return stars;
  }

  /* The card opening the reviews strip: average, stars and rating count. */
  function scoreCardNode(rating, count) {
    const value = Number(rating) || 0;

    if (value <= 0) return null;

    const card = el('div', 'popup-score');
    const shown = value.toFixed(1).replace(/\.0$/, '');
    const stars = el('span', 'popup-score-stars');

    stars.setAttribute('aria-label', value.toFixed(1) + ' out of 5');
    stars.appendChild(starRow(value));

    card.appendChild(el('span', 'popup-score-value', shown));
    card.appendChild(stars);

    const total = Number(count) || 0;

    if (total > 0) {
      card.appendChild(el('span', 'popup-score-count',
        groupThousands(total) + (total === 1 ? ' Rating' : ' Ratings')));
    }

    return card;
  }

  function fillReviewsSlot(slug) {
    loadPlaceReviews(slug).then(reviews => {
      // Matched by slug, so a slow answer for a previous sheet cannot land in
      // the current one.
      const slot = dom.one(`[data-reviews-slot="${slug}"]`, placeModalContent);
      if (!slot || !reviews.length) return;

      const wrap = el('div', 'popup-reviews');
      const head = el('div', 'popup-reviews-head');

      // "from Google" only when some are - admin-added reviews are not.
      // Mirrors place-reviews.blade.php, as does the heading below.
      const fromGoogle = reviews.some(r => !r.manual);

      const label = el('h2', 'popup-reviews-label', 'Reviews');
      const named = findPlaceBySlug(slug);
      if (named) label.appendChild(el('span', 'sr-only', ' of ' + named.name));
      head.appendChild(label);
      if (fromGoogle) head.appendChild(el('span', 'popup-reviews-source', 'from Google'));
      wrap.appendChild(head);

      const scroll = el('div', 'popup-reviews-scroll');

      scroll.setAttribute('role', 'group');
      scroll.setAttribute('aria-label', fromGoogle ? 'Recent Google reviews' : 'Recent reviews');

      const score = scoreCardNode(slot.dataset.rating, slot.dataset.ratingCount);

      if (score) scroll.appendChild(score);

      reviews.forEach(r => scroll.appendChild(reviewNode(r)));
      wrap.appendChild(scroll);

      // The scrollbar is hidden, so a mouse needs arrows (as server-rendered).
      const prev = el('button', 'popup-reviews-arrow popup-reviews-prev', '\u2039');
      const next = el('button', 'popup-reviews-arrow popup-reviews-next', '\u203a');

      [[prev, 'Scroll reviews left'], [next, 'Scroll reviews right']].forEach(([btn, label]) => {
        btn.type = 'button';
        btn.setAttribute('aria-label', label);
        btn.hidden = true;
        wrap.appendChild(btn);
      });

      // Patched in, not swapped: the reviews renderPlaceSheet carried into the
      // slot stay put (and keep their scroll), and a kept arrow keeps the
      // state its strip last gave it.
      const holder = document.createElement('div');

      holder.appendChild(wrap);
      dom.patch(slot, holder, { keepAttributes: (node) => node.classList.contains('popup-reviews-arrow') });

      // Only after insertion - an element not yet laid out reports no overflow.
      // The live nodes, which are the kept ones when the slot already held
      // reviews; attachScrollArrows wires a strip once.
      attachScrollArrows(
        dom.one('.popup-reviews-scroll', slot),
        dom.one('.popup-reviews-prev', slot),
        dom.one('.popup-reviews-next', slot)
      );
    });
  }

  /* Renders the sheet; openPlaceModal renders it again once the rest of the
     place is fetched. Scroll position survives a re-render. */
  function renderPlaceSheet(p) {
    // Both candidates are saved and restored - which one scrolls depends on
    // the layout (on a phone it is the content itself).
    const scrollers = [placeModalContent, placeModalContent.parentElement].filter(Boolean);
    const tops = scrollers.map((el) => el.scrollTop);

    // dom.patch, not replaceChildren: the morph adopts a server-rendered sheet
    // and touches only changed nodes on the second phase. A wrapper <div>,
    // because morphdom treats a DocumentFragment as its first child.
    const next = document.createElement('div');
    const slug = placeSlug(p);

    next.appendChild(buildPopupNode(p));

    // Carry this place's on-screen reviews into the new (empty) slot so the
    // morph does not strip them and make the sheet jump; fillReviewsSlot then
    // swaps in fresh ones. Same place only.
    const liveReviews = dom.one('.popup-reviews', placeModalContent);
    const nextSlot = dom.one('.popup-reviews-slot', next);
    const sameSheet = dom.one(`[data-reviews-slot="${slug}"], .popup-share-btn[data-slug="${slug}"]`, placeModalContent);

    if (liveReviews && nextSlot && sameSheet) {
      nextSlot.appendChild(liveReviews.cloneNode(true));
    }

    dom.patch(placeModalContent, next);
    fillReviewsSlot(slug);

    scrollers.forEach((el, i) => {
      if (tops[i]) el.scrollTop = tops[i];
    });
  }

  /* Closure pill on the card, search row and sheet, from Google's
     businessStatus: amber for temporary, red for permanent (removing the
     listing stays the admin's call). Same strings as App\Support\ClosedNotice;
     the server-rendered card and sheet carry the same pill. */
  const CLOSED_NOTICES = {
    CLOSED_TEMPORARILY: ['temporary', 'Temporarily closed', 'Google lists this place as temporarily closed'],
    CLOSED_PERMANENTLY: ['permanent', 'Permanently closed', 'Google lists this place as permanently closed']
  };

  function closedNoticeNode(p, block) {
    const closed = p && CLOSED_NOTICES[p.businessStatus];
    if (!closed) return null;

    const notice = el('span', 'closed-notice closed-notice--' + closed[0] + (block ? ' closed-notice--block' : ''), closed[1]);
    notice.title = closed[2];

    return notice;
  }

  function openPlaceModal(p) {
    // Merge already-known detail (e.g. the place page's seed) before the first
    // draw, so the sheet is not drawn with the summary and then redrawn.
    if (p.details === undefined && placeDetailBySlug.has(placeSlug(p))) {
      Object.assign(p, placeDetailBySlug.get(placeSlug(p)));
    }

    setActivePin(p);
    // Each open starts at the natural size, not expanded.
    placeModalEl.classList.remove('is-expanded');
    renderPlaceSheet(p);
    placeModalEl.classList.toggle('featured-place-modal', isFeaturedForDisplay(p));
    placeModalBackdrop.classList.add('visible');

    // On open, not per build (the sheet is rebuilt when reviews land).
    startCarousels();

    // `undefined`, not falsy: a genuinely empty description is '' and must
    // not be re-fetched on every open.
    if (p.details === undefined) {
      loadPlaceDetail(p).then(() => {
        if (activePinPlace === p && placeModalBackdrop.classList.contains('visible')) {
          renderPlaceSheet(p);
        }
      });
    }
    // However the sheet was opened, the sidebar highlight follows it
    // (scrolling into view happens in renderList - see pendingScrollSlug).
    setActiveCard(p);
  }

  // Hides the card only - the pin, sidebar highlight and URL stay (see
  // activePinPlace).
  function closePlaceModal() {
    placeModalBackdrop.classList.remove('visible');
    // The photo overlay goes with it - the Back button closes the sheet.
    photoOverlay.close();
    // An interval keeps running on a detached sheet.
    stopCarousels();
  }

  // A deliberate dismissal (close button, backdrop, Escape, swipe-down): the
  // place also leaves the URL, so a shared link never names a closed place.
  // Separate from closePlaceModal, which popstate and resetToInitialView also
  // call - rewriting the URL from popstate would corrupt history. The pin and
  // sidebar highlight stay selected.
  function dismissPlaceModal() {
    closePlaceModal();

    if (parseLocationPath().view !== 'place' && !location.hash) return;

    // PUSH, not replace or go back: replacing leaves two identical adjacent
    // entries (a dead back press), and stepping back drops the place from
    // history. Pushing gives map, place, map, place - back reopens what was
    // just closed.
    writePath(basePath(), true);
  }

  placeModalCloseBtn.addEventListener('click', (e) => {
    // The close control is an <a> on server-opened place pages (the no-JS
    // way out); with the script running, closing stays in-page.
    e.preventDefault();
    dismissPlaceModal();
  });
  placeModalBackdrop.addEventListener('click', (e) => {
    if (e.target === placeModalBackdrop) dismissPlaceModal();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && placeModalBackdrop.classList.contains('visible')) dismissPlaceModal();
  });

  // Swipe-down to dismiss on touch. Only takes over when the content is
  // scrolled to the top and the drag is clearly downward; otherwise the
  // drag scrolls the content as normal.
  let modalTouchStartY = null;
  let modalTouchStartX = null;
  let modalTouchDeltaY = 0;
  let modalTouchDragging = false;

  placeModalEl.addEventListener('touchstart', (e) => {
    if (!placeModalBackdrop.classList.contains('visible') || e.touches.length !== 1) return;
    modalTouchStartY = e.touches[0].clientY;
    modalTouchStartX = e.touches[0].clientX;
    modalTouchDeltaY = 0;
    modalTouchDragging = false;
  }, { passive: true });

  placeModalEl.addEventListener('touchmove', (e) => {
    if (modalTouchStartY === null || e.touches.length !== 1) return;
    const deltaY = e.touches[0].clientY - modalTouchStartY;
    const deltaX = e.touches[0].clientX - modalTouchStartX;
    if (!modalTouchDragging) {
      if (deltaY <= 0 || Math.abs(deltaX) > Math.abs(deltaY) || placeModalContent.scrollTop > 0) return;
      modalTouchDragging = true;
      placeModalEl.classList.add('is-dragging');
    }
    modalTouchDeltaY = Math.max(0, deltaY);
    placeModalEl.style.transform = 'translateY(' + modalTouchDeltaY + 'px)';
    e.preventDefault();
  }, { passive: false });

  function endModalTouchDrag() {
    if (!modalTouchDragging) { modalTouchStartY = null; return; }
    placeModalEl.classList.remove('is-dragging');
    // A quarter of the card's height, capped at 120px, so a nudge is not a
    // dismiss.
    const dismissThreshold = Math.min(120, placeModalEl.offsetHeight / 4);
    if (modalTouchDeltaY > dismissThreshold) dismissPlaceModal();
    placeModalEl.style.transform = '';
    modalTouchStartY = null;
    modalTouchStartX = null;
    modalTouchDeltaY = 0;
    modalTouchDragging = false;
  }

  placeModalEl.addEventListener('touchend', endModalTouchDrag);
  placeModalEl.addEventListener('touchcancel', endModalTouchDrag);

  /* ---- The grab handle ----
     Pointer events (so a mouse can drag too), bound to the handle only - on
     the whole sheet a mouse drag would fight text selection. */
  const sheetHandle = dom.id('place-sheet-handle');

  /* THE SHEET INSIDE THE MAP AREA WHERE ANCHORS ARE UNSUPPORTED. The server
     sends the sheet ahead of the list (a place page's own content leads the
     HTML - see partials/map-shell), and the stylesheet pins it over
     #map-wrapper with anchor positioning. Without that, its fallback is the
     splash's fixed strip sums, which cannot follow the list sliding shut or a
     strip being dismissed - so it goes where the stylesheet's plain rules
     place it: inside #map-wrapper, filling it. Before the splash lifts, so
     nobody sees the move. */
  const mapWrapperEl = dom.id('map-wrapper');

  if (mapWrapperEl && !(window.CSS && CSS.supports && CSS.supports('anchor-name: --a'))) {
    dom.insert(mapWrapperEl, placeModalBackdrop);
  }

  /* --sheet-reach: how far the sheet may climb on a phone, over the cuisine
     row and the featured carousel above it, for the in-#map-wrapper placement
     above (anchored, it climbs to the carousel's own top, else the row's).
     Measured with a ResizeObserver because their heights change with
     wrapping, font scale and rotation, and the carousel's drops to 0 when it
     is dismissed. */
  const cuisineRowEl = dom.id('cuisine-row');
  const featuredStripEl = dom.id('featured');

  if (cuisineRowEl) {
    const syncSheetReach = () => {
      const reach = cuisineRowEl.offsetHeight + (featuredStripEl ? featuredStripEl.offsetHeight : 0);

      placeModalBackdrop.style.setProperty('--sheet-reach', reach + 'px');
    };

    syncSheetReach();

    if (window.ResizeObserver) {
      const observer = new ResizeObserver(syncSheetReach);

      observer.observe(cuisineRowEl);
      if (featuredStripEl) observer.observe(featuredStripEl);
    } else {
      window.addEventListener('resize', syncSheetReach);
    }
  }

  /* Dragging the handle drives the sheet's height (it is bottom-anchored, so
     the top edge follows the pointer and content keeps its scroll). On
     release it snaps: above ~92% of the map area expanded, below ~35%
     closed, otherwise natural size. A click that never moved closes. */
  if (sheetHandle && window.PointerEvent) {
    let handleStartY = null;
    let handleStartH = 0;
    let handleMoved = false;

    const backdropHeight = () => placeModalBackdrop.clientHeight || 1;

    const settle = (state) => {
      placeModalEl.classList.remove('is-dragging');
      placeModalEl.style.height = '';
      placeModalEl.style.maxHeight = '';
      placeModalEl.classList.toggle('is-expanded', state === 'max');
      if (state === 'closed') dismissPlaceModal();
    };

    const handleMove = (e) => {
      if (handleStartY === null) return;

      const delta = e.clientY - handleStartY;

      // A few pixels of slop, so an unsteady click is still a click.
      if (Math.abs(delta) > 3) handleMoved = true;

      const h = Math.max(90, Math.min(handleStartH - delta, backdropHeight()));

      placeModalEl.style.maxHeight = 'none';
      placeModalEl.style.height = h + 'px';
    };

    const handleUp = () => {
      if (handleStartY === null) return;

      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);

      const frac = placeModalEl.offsetHeight / backdropHeight();

      if (!handleMoved) {
        settle('closed');
      } else if (frac > 0.92) {
        settle('max');
      } else if (frac < 0.35) {
        settle('closed');
      } else {
        settle('natural');
      }

      handleStartY = null;
      handleMoved = false;
    };

    sheetHandle.addEventListener('click', (e) => e.preventDefault());
    sheetHandle.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      handleStartY = e.clientY;
      handleStartH = placeModalEl.offsetHeight;
      handleMoved = false;
      placeModalEl.classList.add('is-dragging');
      // On window: the pointer leaves the 26px handle almost immediately.
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
      window.addEventListener('pointercancel', handleUp);
      e.preventDefault();
    });

    // Keyboard and assistive tech never fire the pointer sequence above.
    // Enter/Space keep closing; the arrows do what the drag does.
    sheetHandle.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        dismissPlaceModal();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        placeModalEl.classList.add('is-expanded');
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (placeModalEl.classList.contains('is-expanded')) {
          placeModalEl.classList.remove('is-expanded');
        } else {
          dismissPlaceModal();
        }
      }
    });
  }

  // ==========================================================================
  // PIN HOVER PREVIEW
  // ==========================================================================

  // A compact preview card when the mouse rests on a pin. Hover-capable fine
  // pointers only; on touch a tap opens the full sheet.
  //
  // One shared tooltip, not bindTooltip() per marker (which opens instantly
  // and would strobe across a dense cluster); a 120ms dwell filters
  // pass-throughs.
  const hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  // A bare star - the chip it sits in supplies the background.
  const HOVER_TIP_STAR_SVG = '<svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 3.5 14.6 9l6 .8-4.4 4.2 1.1 6-5.3-2.9-5.3 2.9 1.1-6L3.4 9.8l6-.8Z" fill="currentColor"/></svg>';

  // direction/offset are reset per open - the card flips below a pin near
  // the top of the map.
  const hoverTip = L.tooltip({
    direction: 'top',
    offset: [0, -46],
    opacity: 1,
    className: 'place-hover-tip',
  });
  let hoverTipTimer = null;

  // Photo-led, like the featured strip: a banner photo, then name and meta.
  function buildHoverTipNode(p) {
    const tip = el('div', 'hover-tip');

    const figure = el('div', 'hover-tip-figure');

    figure.appendChild(thumbNode(p, 'hover-tip-thumb', null, 220));

    // Flags on the photo, bottom-left: featured and prayer space only.
    const flags = el('div', 'hover-tip-flags');

    if (isFeaturedForDisplay(p)) {
      const star = el('span', 'hover-tip-flag hover-tip-flag--featured');

      star.appendChild(fromTrustedMarkup(HOVER_TIP_STAR_SVG));
      star.appendChild(document.createTextNode('Featured'));
      flags.appendChild(star);
    }

    if (p.prayerSpace) {
      const prayer = el('span', 'hover-tip-flag');

      prayer.appendChild(badgeIconNode('prayerSpace', 'prayer'));
      prayer.appendChild(document.createTextNode('Prayer space'));
      flags.appendChild(prayer);
    }

    if (flags.childElementCount) figure.appendChild(flags);

    tip.appendChild(figure);

    const body = el('div', 'hover-tip-body');

    body.appendChild(el('div', 'hover-tip-name', p.name));

    // Rating, price, distance - same order and separators as the sidebar cards.
    const meta = el('div', 'hover-tip-meta');

    meta.appendChild(ratingBadgeNode(p, true, true));

    if (priceLabel(p)) {
      meta.appendChild(separatorNode());
      meta.appendChild(el('span', 'price-tag', priceLabel(p)));
    }

    if (userLocation) {
      const km = haversineKm(userLocation.lat, userLocation.lng, p.lat, p.lng);

      meta.appendChild(separatorNode());
      meta.appendChild(el('span', 'hover-tip-distance', km.toFixed(1) + ' km'));
    }

    body.appendChild(meta);

    // The sidebar card's chips. Not linked: the card vanishes as the mouse
    // moves towards it.
    const tags = placeTagsNode(p, { className: 'place-tags hover-tip-tags' });

    if (tags) body.appendChild(tags);

    if (p.summary) body.appendChild(el('div', 'hover-tip-summary', p.summary));

    // No address - the sheet a click away has it.
    tip.appendChild(body);

    return tip;
  }

  // Slides the card back inside the map when a pin near an edge would leave
  // it overhanging. marginLeft, not transform (Leaflet positions tooltips by
  // transform); --tip-shift lets the CSS arrow stay over the pin.
  function keepHoverTipOnScreen() {
    const tipEl = hoverTip.getElement();

    if (!tipEl) return;

    tipEl.style.marginLeft = '';
    tipEl.style.setProperty('--tip-shift', '0px');

    const mapRect = map.getContainer().getBoundingClientRect();
    const tipRect = tipEl.getBoundingClientRect();
    // Clear of the zoom buttons and the map's own edge.
    const pad = 10;

    let shift = 0;

    if (tipRect.left < mapRect.left + pad) {
      shift = (mapRect.left + pad) - tipRect.left;
    } else if (tipRect.right > mapRect.right - pad) {
      shift = (mapRect.right - pad) - tipRect.right;
    }

    if (!shift) return;

    // Never so far that the arrow leaves the card.
    const limit = tipRect.width / 2 - 16;

    shift = Math.max(-limit, Math.min(limit, shift));

    tipEl.style.marginLeft = shift + 'px';
    tipEl.style.setProperty('--tip-shift', shift + 'px');
  }

  function closeHoverTip() {
    clearTimeout(hoverTipTimer);
    hoverTipTimer = null;
    map.closeTooltip(hoverTip); // removeLayer under the hood - no-op if closed
  }

  /* Places sharing a coordinate (Google gives a plaza's units one point)
     would hide each other's pins, so they are fanned out onto a 9m circle -
     for display only (pinLat/pinLng); stored coordinates are untouched.
     Ordered by name, so a pin lands in the same spot on every load - by
     cmpName, not localeCompare(), which would load the browser's ICU data
     during the boot and order by the visitor's locale. */
  const PIN_SPREAD_METRES = 9;

  function spreadCoincidentPins() {
    const groups = new Map();

    places.forEach(p => {
      if (typeof p.lat !== 'number' || typeof p.lng !== 'number') return;

      // Five decimals is about a metre - close enough to be the same doorway.
      const key = p.lat.toFixed(5) + ',' + p.lng.toFixed(5);

      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    });

    groups.forEach(group => {
      if (group.length < 2) return;

      group.sort(cmpName);
      group.forEach((p, i) => {
        const angle = (2 * Math.PI * i) / group.length;
        // Metres to degrees; longitude degrees shrink by cos(latitude).
        const dLat = (PIN_SPREAD_METRES * Math.cos(angle)) / 111320;
        const dLng = (PIN_SPREAD_METRES * Math.sin(angle)) / (111320 * Math.cos(p.lat * Math.PI / 180));

        p.pinLat = p.lat + dLat;
        p.pinLng = p.lng + dLng;
      });
    });
  }

  /** Where a place's pin is drawn, which is its own coordinate unless it shares one. */
  function pinLatLng(p) {
    return [typeof p.pinLat === 'number' ? p.pinLat : p.lat,
            typeof p.pinLng === 'number' ? p.pinLng : p.lng];
  }

  // One marker per place, created but not drawn: updateMarkerVisibility(),
  // run straight after in bootPlaces, puts on the map only the pins near the
  // view that pass the filters. Adding every pin here built and then tore
  // down the DOM of each one off screen or filtered out. A click opens the
  // place modal, never Leaflet's bindPopup() (which auto-pans the map); a
  // resting mouse gets the hover preview.
  function buildMarkers() {
    places.forEach(p => {
      // `title` gives each pin (a role=button divIcon) an accessible name. An
      // option, not a hand-set aria-label, because setIcon() rebuilds the
      // element and Leaflet re-applies options.title itself.
      const marker = L.marker(pinLatLng(p), {
        icon: createMarkerIcon(p, isFeaturedForDisplay(p), false),
        title: p.name,
        alt: p.name,
      });
      marker.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        closeHoverTip();
        pushHashForPlace(p);
        openPlaceModal(p);
      });
      if (hoverCapable) {
        marker.on('mouseover', () => {
          // The line draws at once; only the card waits for the dwell.
          hoveredPlace = p;
          drawFlightPath();
          clearTimeout(hoverTipTimer);
          hoverTipTimer = setTimeout(() => {
            // Flip below the pin only when there is too little room above
            // (the card is ~290px and the featured carousel covers the top)
            // AND more room below.
            const mapH = map.getSize().y;
            const headroom = map.latLngToContainerPoint(marker.getLatLng()).y;
            // mapH <= 0: the map has no size before first layout - fall back
            // to the headroom test alone.
            const flip = headroom < 350 && (mapH <= 0 || (mapH - headroom) > headroom);

            hoverTip.options.direction = flip ? 'bottom' : 'top';
            // -46 clears the 40px pin plus its bob animation; 12 sits just
            // under the tip.
            hoverTip.options.offset = flip ? [0, 12] : [0, -46];
            hoverTip.setContent(buildHoverTipNode(p));
            hoverTip.setLatLng(marker.getLatLng());
            map.openTooltip(hoverTip);
            keepHoverTipOnScreen();
          }, 120);
        });
        marker.on('mouseout', () => {
          hoveredPlace = null;
          // Back to the selected place, or no line.
          drawFlightPath();
          closeHoverTip();
        });
      }
      markers[placeSlug(p)] = marker;
    });
  }

  // Sheet clicks are handled by one delegated listener (below), since the
  // sheet's content is re-rendered and may be server-rendered.
  //
  // Every way out of the sheet, and its actions, is announced through
  // hcEvents (see main.js) for custom widgets; the browser still follows the
  // link as normal. Links are matched by class.
  const PLACE_LINK_EVENTS = [
    ['popup-link-google', 'google-maps-clicked'],
    ['popup-website-btn', 'website-clicked'],
    ['popup-phone', 'phone-clicked'],
    ['popup-thumb-social-btn', 'social-clicked'],
    ['popup-order-link', 'order-clicked'],
  ];

  function announcePlaceAction(event, p, url) {
    window.hcEvents.emit(event, {
      name: p ? p.name : null,
      slug: p ? placeSlug(p) : null,
      url: url || null,
    });
  }

  document.addEventListener('click', (e) => {
    const shareBtn = e.target.closest('.popup-share-btn');
    if (shareBtn) {
      const p = findPlaceBySlug(shareBtn.dataset.slug);
      if (p) {
        announcePlaceAction('share-clicked', p);
        sharePlace(p);
      }
      return;
    }
    const appleBtn = e.target.closest('.popup-link-apple');
    if (appleBtn) {
      const p = findPlaceBySlug(appleBtn.dataset.slug);
      if (p) {
        announcePlaceAction('apple-maps-clicked', p, appleMapsLink(p));
        window.open(appleMapsLink(p), '_blank', 'noopener');
      }
      return;
    }
    // Carousel arrows and dots - delegated, see addCarousel() for why.
    const carouselBtn = e.target.closest('.popup-thumb-arrow, .popup-thumb-dot');

    if (carouselBtn) {
      const wrap = carouselBtn.closest('.popup-thumb-wrap');

      if (wrap) {
        // A press stops the auto-advance for good.
        wrap.dataset.settled = '1';

        showCarouselFrame(wrap, carouselBtn.dataset.step
          ? currentCarouselFrame(wrap) + Number(carouselBtn.dataset.step)
          : Number(carouselBtn.dataset.frame || 0));
      }

      return;
    }

    const reportBtn = e.target.closest('.popup-thumb-report-btn');
    if (reportBtn) {
      const p = findPlaceBySlug(reportBtn.dataset.slug);
      if (p) openReportModal(p);
      return;
    }

    const link = e.target.closest('a[href]');
    if (!link) return;

    const found = PLACE_LINK_EVENTS.find(pair => link.classList.contains(pair[0]));
    // The links carry no slug; the open sheet is activeSlug's.
    if (found) announcePlaceAction(found[1], findPlaceBySlug(activeSlug), link.href);
  });

  const sidebar = dom.id('sidebar');
  const sidebarToggle = dom.id('sidebar-toggle');
  const sidebarCollapseBtn = dom.id('sidebar-collapse-btn');
  const sidebarReopenBtn = dom.id('sidebar-reopen-btn');

  // ==========================================================================
  // SIDEBAR TOGGLE (mobile/narrow layouts)
  // ==========================================================================

  // Finishes a running slide, so rapid toggling cannot strand the sidebar
  // mid-animation.
  let settleSidebarSlide = null;

  /* Whether the map's size depends on the sidebar (side by side on wide
   * screens; an overlay below 768px). On a phone an invalidateSize() would
   * needlessly re-rasterise the vector basemap mid-slide - enough memory to
   * get the tab killed on iOS. Read off the computed position so it cannot
   * drift from the stylesheet. Only valid BEFORE .sliding goes on (that
   * class sets position:absolute at every width). */
  function sidebarSharesWidthWithMap() {
    return getComputedStyle(sidebar).position !== 'absolute';
  }

  // State is the .collapsed class; the animation is a temporary .sliding
  // class in which the sidebar floats over the map and moves on transform
  // only. Never animate the width - Leaflet would re-render every frame; the
  // map resizes once instead.
  function setSidebarCollapsed(collapsed, animate = true) {
    const changed = sidebar.classList.contains('collapsed') !== collapsed;
    const mapResizes = sidebarSharesWidthWithMap();

    // Swap the icon, keep the text label (see topbar-map.blade.php): the
    // button's new content is built detached, and the patch changes only the
    // icon's paths and the label's words.
    const toggleLabel = sidebarToggle.querySelector('.sidebar-toggle-label');
    const toggleNext = sidebarToggle.cloneNode(false);

    toggleNext.appendChild(fromTrustedMarkup(collapsed ? UI_ICON_MENU : UI_ICON_CLOSE));
    if (toggleLabel) {
      const label = toggleLabel.cloneNode(false);

      label.textContent = collapsed ? 'Show list' : 'Hide list';
      toggleNext.appendChild(label);
    }
    dom.patch(sidebarToggle, toggleNext);
    sidebarToggle.setAttribute('aria-label', collapsed ? 'Show place list' : 'Hide place list');
    sidebarReopenBtn.classList.toggle('visible', collapsed);
    // Hidden while sliding too, or it would paint over the moving panel.
    sidebarCollapseBtn.classList.toggle('hidden', true);

    if (!changed || !animate) {
      sidebar.classList.toggle('collapsed', collapsed);
      sidebarCollapseBtn.classList.toggle('hidden', collapsed);
      if (mapResizes) map.invalidateSize();
    } else {
      if (settleSidebarSlide) settleSidebarSlide();

      // Float the panel; the map takes the full width in one step underneath.
      sidebar.classList.add('sliding');
      if (mapResizes) map.invalidateSize();
      void sidebar.offsetWidth; // commit the floating start state
      sidebar.classList.toggle('collapsed', collapsed);

      const settle = () => {
        settleSidebarSlide = null;
        sidebar.removeEventListener('transitionend', onEnd);
        clearTimeout(backstop);
        sidebar.classList.remove('sliding');
        sidebarCollapseBtn.classList.toggle('hidden', collapsed);
        if (mapResizes) map.invalidateSize();
      };
      const onEnd = e => {
        if (e.target === sidebar && e.propertyName === 'transform') settle();
      };
      sidebar.addEventListener('transitionend', onEnd);
      // Covers prefers-reduced-motion (transition:none fires no
      // transitionend) and any swallowed event.
      const backstop = setTimeout(settle, 420);
      settleSidebarSlide = settle;
    }

    // Opening the list scrolls the selected place into view (a pin tapped
    // while the list was collapsed could not scroll it then).
    if (!collapsed) {
      // Read only inside the timeout: the init call runs ABOVE the
      // `let activeSlug` / `let pendingScrollSlug` declarations, so touching
      // them synchronously would throw (TDZ). The delay also lets the expand
      // transition finish so the scroll lands on the right row.
      setTimeout(() => {
        // In order: a deferred scroll, the place opened this session, or the
        // URL (a page loaded straight at /places/<slug>/).
        const fromUrl = (location.pathname.match(/^\/places\/([a-z0-9-]+)\/?$/) || [])[1] || null;
        const slug = pendingScrollSlug || activeSlug || fromUrl;
        if (!slug) return;

        const target = dom.idLive('place-card-' + slug);
        if (target) {
          scrollCardIntoView(target);
          pendingScrollSlug = null;
        }
      }, 340);
    }
  }

  // The stand-in's --welcome-h (main.css, "THE PHONE'S FIRST PAINT"), read
  // before setSidebarCollapsed() changes anything, so it shares the style
  // flush that call's getComputedStyle() makes anyway.
  const mainEl = dom.id('main');
  const standInWelcomeH = mainEl && mainEl.classList.contains('phone-list-closed') && window.matchMedia
    ? getComputedStyle(mainEl).getPropertyValue('--welcome-h').trim()
    : '';

  // Collapsed by default on phones - except for bots, which get the list
  // open so it is crawlable.
  setSidebarCollapsed(!isLikelyBot() && window.innerWidth <= 768, false);
  // The real state is set, so the server's CSS-only stand-in steps aside
  // (see map-shell.blade.php). Its --welcome-h stays, inline, until
  // placeWelcome() measures the strip: dropping it flipped #main's inherited
  // value to unset and back, restyling and re-laying-out all of #main twice.
  if (standInWelcomeH) mainEl.style.setProperty('--welcome-h', standInWelcomeH);
  mainEl?.classList.remove('phone-list-closed');

  sidebarToggle.addEventListener('click', () => {
    setSidebarCollapsed(!sidebar.classList.contains('collapsed'));
  });

  sidebarCollapseBtn.addEventListener('click', () => {
    setSidebarCollapsed(true);
  });

  sidebarReopenBtn.addEventListener('click', () => {
    setSidebarCollapsed(false);
  });

  // Swipe left to close the sidebar overlay - phone widths only, so a
  // touchscreen laptop's persistent column is unaffected.
  let sidebarTouchStartX = null;
  let sidebarTouchStartY = null;

  sidebar.addEventListener('touchstart', e => {
    if (sidebar.classList.contains('collapsed') || e.touches.length !== 1) return;
    sidebarTouchStartX = e.touches[0].clientX;
    sidebarTouchStartY = e.touches[0].clientY;
  }, { passive: true });

  sidebar.addEventListener('touchend', e => {
    if (sidebarTouchStartX === null) return;
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - sidebarTouchStartX;
    const deltaY = touch.clientY - sidebarTouchStartY;
    sidebarTouchStartX = null;
    sidebarTouchStartY = null;
    // A clearly horizontal leftward swipe, not a scroll or a tap.
    if (window.innerWidth <= 768 && deltaX < -60 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
      setSidebarCollapsed(true);
    }
  }, { passive: true });

  const listEl = dom.id('place-list');
  const searchInput = dom.id('search');
  const filterBtns = dom.all('.filter-btn');

  // 'search-made' is announced once per search: when typing settles, on a
  // pick or Enter, or on arrival via ?search=. Never the same query twice in
  // a row; clearing the box resets. `results` counts matching places.
  const SEARCH_SETTLE_MS = 700;
  let searchSettleTimer = null;
  let lastSearchMade = '';

  function announceSearch(via) {
    clearTimeout(searchSettleTimer);
    searchSettleTimer = null;

    const query = searchInput ? searchInput.value.trim() : '';

    if (!query || query === lastSearchMade) return;
    lastSearchMade = query;

    const score = searchScorer(query);

    window.hcEvents.emit('search-made', {
      query: query,
      via: via,
      results: places.reduce((n, p) => n + (score(p) !== -1 ? 1 : 0), 0),
    });
  }

  function searchTyped() {
    clearTimeout(searchSettleTimer);
    if (!searchInput.value.trim()) {
      lastSearchMade = '';
      searchSettleTimer = null;
      return;
    }
    searchSettleTimer = setTimeout(() => announceSearch('typed'), SEARCH_SETTLE_MS);
  }

  // Animated placeholder that types and deletes cuisine names. Stops on
  // focus, resumes once the box is cleared. Off on articles pages, where the
  // search box is hidden.
  let searchDemoActive = !IS_ARTICLES_VIEW;
  // The one pending step. Every write costs the page a whole frame, so a
  // second chain would double the cost (and the typing speed).
  let searchDemoTimer = null;
  let runSearchPlaceholderStep = null; // set once animateSearchPlaceholder() below runs

  function stopSearchPlaceholderDemo() {
    if (!searchDemoActive) return;
    searchDemoActive = false;
    // Cleared now, or a resume inside its wait would run beside it.
    clearTimeout(searchDemoTimer);
    searchDemoTimer = null;
    searchInput.setAttribute('placeholder', 'Search for places…');
  }

  function resumeSearchPlaceholderDemo() {
    if (IS_ARTICLES_VIEW || searchDemoActive) return;
    searchDemoActive = true;
    if (runSearchPlaceholderStep) runSearchPlaceholderStep();
  }

  (function animateSearchPlaceholder() {
    const words = SEARCH_WORDS;
    const prefix = 'Search for ';
    let wordIndex = 0;
    let charIndex = 0;
    let deleting = false;

    function step() {
      clearTimeout(searchDemoTimer);
      searchDemoTimer = null;
      if (!searchDemoActive) return;
      if (searchInput.value) {
        searchDemoTimer = setTimeout(step, 1000); // paused while there's real input - keep checking
        return;
      }
      const word = words[wordIndex];
      let delay;
      if (!deleting) {
        charIndex++;
        searchInput.setAttribute('placeholder', prefix + word.slice(0, charIndex));
        delay = 90;
        if (charIndex === word.length) {
          deleting = true;
          delay = 1300;
        }
      } else {
        charIndex--;
        searchInput.setAttribute('placeholder', prefix + word.slice(0, charIndex));
        delay = 45;
        if (charIndex === 0) {
          deleting = false;
          wordIndex = (wordIndex + 1) % words.length;
          delay = 300;
        }
      }
      searchDemoTimer = setTimeout(step, delay);
    }

    runSearchPlaceholderStep = step;
    step();
  })();

  // The open place, by slug (names repeat across a chain's branches).
  let activeSlug = null;
  // Set when a place is freshly opened; renderList() scrolls to it once the
  // map and list have gone quiet (see scrollSettleTimer), since an earlier
  // re-render would abandon the scroll. A slug, not a DOM reference, because
  // the list is rebuilt.
  let pendingScrollSlug = null;
  let scrollSettleTimer = null;
  let sortMode = 'default';

  /* ==========================================================================
     THE "BEST" RANKING
     ==========================================================================
     A line-for-line mirror of App\Services\PlaceRanking (a Bayesian average -
     the reasoning lives there). The server renders the sidebar first, so any
     disagreement reshuffles the list on boot: change both together. The mean
     is summed in array order and the percentile is nearest-rank with a
     floor, so the two stay bit-for-bit identical. */
  const RANKING_PERCENTILE = 0.25;

  function isRated(p) {
    return typeof p.rating === 'number' && p.rating > 0 && (p.ratingCount || 0) > 0;
  }

  /* Fast food and chains - mirrors PlaceRanking::isFastFood(), ::isChain()
     and ::brand(). Each is marked down a step in "Best"; a fast-food chain
     loses both. A chain is CHAIN_MIN or more eateries (places with a cuisine)
     sharing a brand. */
  const FAST_FOOD_STEP = 0.2;
  const CHAIN_STEP = 0.2;
  const CHAIN_MIN = 3;
  const BRAND_JOINERS = ['of', 'on', 'n', 'e', 'and', 'the', 'de', 'la', 'el', 'al'];

  /* The name up to its branch, apostrophes dropped, ASCII case folded (as
     PHP's strtolower(), not toLowerCase(), which folds more), the rest a
     space, a leading "the" gone; then two words, or three when the second
     only joins ("House of Kabob"). Spaces, not \s, which PCRE reads
     differently. */
  function brandOf(name) {
    const head = String(name || '').split(/ +[-–—|]|[-–—|] |\(|,/)[0];
    const folded = head.replace(/['’]/g, '').replace(/[A-Z]/g, c => c.toLowerCase());
    const flat = folded.replace(/[^a-z0-9]+/g, ' ').trim();
    const words = flat === '' ? [] : flat.split(' ');

    if (words[0] === 'the') words.shift();

    return words.slice(0, words.length > 1 && BRAND_JOINERS.includes(words[1]) ? 3 : 2).join(' ');
  }

  function isFastFood(p) {
    return Array.isArray(p.cuisines) && p.cuisines.includes('fast-food');
  }

  function isChain(p, brands) {
    if (!Array.isArray(p.cuisines) || !p.cuisines.length) return false;

    return (brands[brandOf(p.name)] || 0) >= CHAIN_MIN;
  }

  /* Where and when - mirrors PlaceRanking::distanceKm(), ::distanceAdjustment()
     and ::hoursAdjustment(). With a context (the default view's centre and
     the time), a place loses up to DISTANCE_STEP with its distance from the
     centre, all of it at DISTANCE_FAR_KM, and gains or loses up to HOURS_STEP
     with its weekly hours (nothing at HOURS_PIVOT); places open now come
     first. */
  const DISTANCE_STEP = 0.2;
  const DISTANCE_FAR_KM = 30;
  const KM_PER_DEG_LAT = 110.574;
  const KM_PER_DEG_LNG = 111.32;
  const HOURS_STEP = 0.1;
  const HOURS_PIVOT = 84;

  /* On a flat map. The same steps in the same order as PHP - its deg2rad()
     is (degrees / 180) * pi - so both get the same number. */
  function distanceKm(p, lat, lng) {
    if (typeof p.lat !== 'number' || typeof p.lng !== 'number') return 0;

    const dx = (p.lng - lng) * Math.cos((lat / 180) * Math.PI) * KM_PER_DEG_LNG;
    const dy = (p.lat - lat) * KM_PER_DEG_LAT;

    return Math.sqrt(dx * dx + dy * dy);
  }

  function distanceAdjustment(km) {
    // 0 -, so the centre itself is 0, not -0.
    return 0 - DISTANCE_STEP * (Math.min(km, DISTANCE_FAR_KM) / DISTANCE_FAR_KM);
  }

  function hoursAdjustment(weekHours) {
    if (typeof weekHours !== 'number') return 0;

    return HOURS_STEP * Math.max(-1, Math.min(1, (weekHours - HOURS_PIVOT) / HOURS_PIVOT));
  }

  /* Open at `now` (Unix seconds), from what the boot payload says (see
     OpeningHours::facts()): open, until p.openUntil (none: around the clock),
     or closed, until p.openAt. Unknown hours are not open. Past a second
     change it is out of date until the next load - hours later, at least. */
  function isOpenAt(p, now) {
    if (p.openNow) return !p.openUntil || now < p.openUntil;

    return !!p.openAt && now >= p.openAt;
  }

  /* C, m and the chains, over ALL places, not the filtered set - filtering
     must not re-scale scores, or make a chain of what is left. See
     PlaceRanking::sorted(). */
  function rankingConstants(all) {
    const ratings = [];
    const counts = [];
    const tally = {};

    for (const p of all) {
      if (Array.isArray(p.cuisines) && p.cuisines.length) {
        const brand = brandOf(p.name);

        if (brand !== '') tally[brand] = (tally[brand] || 0) + 1;
      }

      if (!isRated(p)) continue;
      ratings.push(p.rating);
      counts.push(p.ratingCount);
    }

    // Byte order, like PHP's ksort(SORT_STRING), so the two compare equal.
    const brands = {};

    Object.keys(tally).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0)).forEach((brand) => {
      if (tally[brand] >= CHAIN_MIN) brands[brand] = tally[brand];
    });

    if (!ratings.length) return { mean: 0, smoothing: 1, brands };

    counts.sort((a, b) => a - b);

    const index = Math.floor(RANKING_PERCENTILE * (counts.length - 1));

    return {
      mean: ratings.reduce((sum, r) => sum + r, 0) / ratings.length,
      // Never zero, or one-review places top the list.
      smoothing: Math.max(1, counts[index]),
      brands,
    };
  }

  /* The last tie-break. Code-unit order on lowercased names, to match PHP's
     strcasecmp() - NOT localeCompare(), whose collation would disagree. */
  function cmpName(a, b) {
    const x = String(a.name || '').toLowerCase();
    const y = String(b.name || '').toLowerCase();

    return x < y ? -1 : x > y ? 1 : 0;
  }

  /* Price and closure - mirrors PlaceRanking::priceAdjustment() and
     ::closure(). "Best" adjusts by 0.1 per $ step from $$, up for pricier
     and down for cheaper (from the dollar range where there is one), no
     price counting as $; open ranks above
     temporarily closed, above permanently closed. "Top rated" uses the
     closure tiers but not the price. */
  const PRICE_STEP = 0.1;

  /* By the dollar range where Google gives one, else the $ level, else as $:
     the range's middle against $12.50 ($), $25 ($$), $50 ($$$) and $100+
     ($$$$), in a line between - PlaceRanking::priceAdjustment() step for
     step. */
  function priceAdjustment(p) {
    const range = p.priceRange;

    if (range && typeof range.from === 'number' && range.from > 0) {
      const from = range.from;
      // No `to`, or one at or below `from`, is a floor ("$100+").
      const to = typeof range.to === 'number' && range.to > from ? range.to : from;
      const middle = (from + to) / 2;
      const steps = middle <= 12.5 ? -1
        : middle <= 25 ? -1 + (middle - 12.5) / 12.5
        : middle <= 50 ? (middle - 25) / 25
        : middle <= 100 ? 1 + (middle - 50) / 50
        : 2;

      return steps * PRICE_STEP;
    }

    // No price at all is $: Math.max(1, ...).
    const level = Math.max(1, Math.min(String(p.price || '').trim().length, 4));

    return (level - 2) * PRICE_STEP;
  }

  function closure(p) {
    return p.businessStatus === 'CLOSED_TEMPORARILY' ? 1
      : p.businessStatus === 'CLOSED_PERMANENTLY' ? 2
      : 0;
  }

  /* A place to pray rather than to eat - mirrors PlaceRanking::isPrayerPlace():
     a place of worship, or a prayer space that sells no food (no cuisine tag,
     not a grocery). A restaurant or a grocer with a prayer room is food. */
  function isPrayerPlace(p) {
    if (p.placeOfWorship) return true;

    return !!p.prayerSpace && !(Array.isArray(p.cuisines) && p.cuisines.length) && !p.grocery;
  }

  /* The "Best" order (PlaceRanking::sorted): open before closed, then places
     to pray after the rest, then (with a context, {lat, lng, now}) open now
     before the rest, then the smoothed rating (less a step for fast food and
     for a chain, and with a context the distance and weekly hours), then
     review count, then name. Constants from `all`,
     never the filtered `list` - see rankingConstants(). Each place's keys
     are worked out once, not on every comparison (decorate, sort,
     undecorate); the sort is stable, so the order is the comparator's
     exactly. Returns a new array. */
  function sortBest(list, all, context) {
    const { mean, smoothing, brands } = rankingConstants(all);

    return list
      .map(p => ({
        p,
        closed: closure(p),
        prayer: isPrayerPlace(p) ? 1 : 0,
        shut: context && isOpenAt(p, context.now) ? 0 : 1,
        score: bestScore(p, mean, smoothing, brands, context),
        count: p.ratingCount || 0,
      }))
      .sort((a, b) =>
        a.closed - b.closed
        || a.prayer - b.prayer
        || a.shut - b.shut
        || b.score - a.score
        || b.count - a.count
        || cmpName(a.p, b.p)
      )
      .map(entry => entry.p);
  }

  /* "TOP RATED": the rating, weighed by how many reviews back it - the same
     smoothed average as Best, but against the MEDIAN review count rather than
     the 25th percentile, so the number of ratings comes first: a 5.0 from a
     handful of reviews sits below a 4.8 from thousands, and at one rating the
     place with more reviews is higher. Nothing else - no price, hours,
     distance, fast food or chains; that is Best. Browser-only: the server
     always renders Best. */
  const TOP_RATED_PERCENTILE = 0.5;

  /* The map's mean rating and its median review count (nearest rank, floor,
     like rankingConstants()), over ALL places, not the filtered list. */
  function topRatedConstants(all) {
    const ratings = [];
    const counts = [];

    for (const p of all) {
      if (!isRated(p)) continue;
      ratings.push(p.rating);
      counts.push(p.ratingCount);
    }

    if (!ratings.length) return { mean: 0, smoothing: 1 };

    counts.sort((a, b) => a - b);

    return {
      mean: ratings.reduce((sum, r) => sum + r, 0) / ratings.length,
      smoothing: Math.max(1, counts[Math.floor(TOP_RATED_PERCENTILE * (counts.length - 1))]),
    };
  }

  function topRatedScore(p, mean, smoothing) {
    if (!isRated(p)) return -1;

    const v = p.ratingCount;

    return (v / (v + smoothing)) * p.rating + (smoothing / (v + smoothing)) * mean;
  }

  /* Open before closed (the closure tiers), then the weighed rating, then
     review count, then name. Returns a new array. */
  function sortTopRated(list, all) {
    const { mean, smoothing } = topRatedConstants(all);

    return list
      .map(p => ({ p, closed: closure(p), score: topRatedScore(p, mean, smoothing), count: p.ratingCount || 0 }))
      .sort((a, b) =>
        a.closed - b.closed
        || b.score - a.score
        || b.count - a.count
        || cmpName(a.p, b.p)
      )
      .map(entry => entry.p);
  }

  function bestScore(p, mean, smoothing, brands, context) {
    if (!isRated(p)) return -1;

    const v = p.ratingCount;

    return (v / (v + smoothing)) * p.rating + (smoothing / (v + smoothing)) * mean + priceAdjustment(p)
      - (isFastFood(p) ? FAST_FOOD_STEP : 0)
      - (isChain(p, brands || {}) ? CHAIN_STEP : 0)
      + (context ? distanceAdjustment(distanceKm(p, context.lat, context.lng)) : 0)
      + (context ? hoursAdjustment(p.weekHours) : 0);
  }
  // Which category filters are active, keyed by slug. Derived from
  // AMENITY_FIELDS so the two can never disagree (the URL handlers gate on
  // `slug in activeAmenityCategories`).
  const activeAmenityCategories = Object.fromEntries(
    Object.keys(AMENITY_FIELDS).map(slug => [slug, false])
  );

  let userLocation = null;

  // ==========================================================================
  // URL HASH DEEP-LINKING
  // ==========================================================================

  function slugify(name) {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // A place's URL slug. The server sends `slug` only when it differs from
  // the slugified name (see PlaceProjection::summary).
  function placeSlug(p) {
    return p.slug || slugify(p.name);
  }

  /* The slugs the server left out, filled in once as the data arrives, so
     the pins, the list and every lookup stop slugifying the same names on
     each pass. The same value placeSlug() gave; a slug the server sent is
     never touched. */
  function fillPlaceSlugs(list) {
    list.forEach(p => {
      if (!p.slug) p.slug = slugify(p.name);
    });
  }

  function findPlaceBySlug(slug) {
    if (!slug) return null;
    return places.find(p => placeSlug(p) === slug) || null;
  }

  // The canonical shareable URL for a place.
  function placeUrl(p) {
    return location.origin + PATHS.place(placeSlug(p));
  }

  // The place the URL points at: the /places/<slug>/ path, or a legacy
  // #slug fragment. ?p=slug is no longer read, on either side.
  function getRequestedSlug() {
    const fromPath = parseLocationPath();
    if (fromPath.view === 'place') return fromPath.slug;
    return location.hash.slice(1);
  }

  // Honours ?search= (the JSON-LD SearchAction target Google may send
  // visitors to): fills the box, opens suggestions and focuses it. Call once
  // places has loaded.
  function applySearchFromUrl() {
    const query = new URLSearchParams(location.search).get('search');
    if (!searchInput || !query) return;
    searchInput.value = query;
    renderSuggestions();
    searchInput.focus();
    announceSearch('url');
  }

  // Sets (or, with null/'', removes) one query parameter, keeping the rest.
  function setUrlParam(key, value, push) {
    const params = new URLSearchParams(location.search);
    if (value === null || value === '') {
      params.delete(key);
    } else {
      params.set(key, value);
    }
    const query = params.toString();
    const newUrl = location.pathname + (query ? '?' + query : '');
    try {
      if (push) {
        history.pushState(null, '', newUrl);
      } else {
        history.replaceState(null, '', newUrl);
      }
    } catch (err) {
      location.search = query;
    }
  }

  const toastEl = dom.id('toast');
  let toastTimer = null;

  // ==========================================================================
  // TOAST NOTIFICATIONS
  // ==========================================================================

  // duration in ms (omit for the default); Infinity makes the toast sticky
  // until hideToast().
  function showToast(message, duration) {
    dom.text(toastEl, message);
    toastEl.classList.add('visible');
    clearTimeout(toastTimer);
    if (duration !== Infinity) {
      toastTimer = setTimeout(() => toastEl.classList.remove('visible'), duration || 2200);
    }
  }

  function hideToast() {
    clearTimeout(toastTimer);
    toastEl.classList.remove('visible');
  }

  // ==========================================================================
  // SHARE / COPY LINK
  // ==========================================================================

  // Fallback for browsers without navigator.clipboard.
  function legacyCopyToClipboard(text) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    dom.insert(document.body, textarea);
    textarea.focus({ preventScroll: true });
    textarea.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (err) {
      ok = false;
    }
    dom.remove(textarea);
    return ok;
  }

  // The native share sheet where available, else copy a link.
  function sharePlace(p) {
    const url = placeUrl(p);
    // `text` leads with the name: many share targets drop `title`.
    const shareData = {
      title: p.name + ' | ' + SITE_NAME,
      text: p.name + ' \u2013 check it out on ' + SITE_NAME + '!',
      url: url
    };

    // No canShare() pre-check - it has wrongly rejected valid payloads.
    if (navigator.share) {
      navigator.share(shareData).catch(err => {
        if (err && err.name === 'AbortError') return; // user cancelled the native sheet
        copyShareLink(url, p.name);
      });
      return;
    }
    copyShareLink(url, p.name);
  }

  function copyShareLink(url, name) {
    const message = 'Check out ' + name + ' on ' + SITE_NAME + ': ' + url;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(message)
        .then(() => showToast('Link copied to clipboard'))
        .catch(() => {
          if (legacyCopyToClipboard(message)) {
            showToast('Link copied to clipboard');
          } else {
            window.prompt('Copy this link:', message);
          }
        });
    } else if (legacyCopyToClipboard(message)) {
      showToast('Link copied to clipboard');
    } else {
      window.prompt('Copy this link:', message);
    }
  }

  // ==========================================================================
  // SUBMIT / GET-FEATURED MODAL
  // ==========================================================================

  const SUBMISSION_ENDPOINT = '/api/submission.php';

  const submitModalBackdrop = dom.id('submit-modal-backdrop');
  const submitModalTitle = dom.id('submit-modal-title');
  const submitModalSubtitle = dom.id('submit-modal-subtitle');
  const submitModalForm = dom.id('submit-modal-form');
  const submitModalError = dom.id('submit-modal-error');
  const submitModalResult = dom.id('submit-modal-result');
  const submitModalSubmitBtn = dom.id('submit-modal-submit-btn');
  const submitModalClose = dom.id('submit-modal-close');

  let currentRequestType = 'new_restaurant';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const CONFETTI_COLORS = ['#10b981', '#f1c40f', '#e74c3c', '#3498db', '#9b59b6', '#e67e22'];

  // Full-screen confetti, animated in CSS (.confetti-piece).
  function launchConfetti() {
    if (prefersReducedMotion) return;

    const container = document.createElement('div');
    container.className = 'confetti-container';

    const pieceCount = 60;
    for (let i = 0; i < pieceCount; i++) {
      const piece = document.createElement('span');
      piece.className = 'confetti-piece';
      const size = 6 + Math.random() * 6;
      const duration = 2.2 + Math.random() * 1.6;
      piece.style.left = (Math.random() * 100) + 'vw';
      piece.style.width = size + 'px';
      piece.style.height = (size * 0.4) + 'px';
      piece.style.background = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
      piece.style.animationDuration = duration + 's';
      piece.style.animationDelay = (Math.random() * 0.4) + 's';
      piece.style.setProperty('--drift', ((Math.random() - 0.5) * 160) + 'px');
      piece.style.setProperty('--rotate-start', (Math.random() * 360) + 'deg');
      container.appendChild(piece);
    }

    dom.insert(document.body, container);
    setTimeout(() => dom.remove(container), 4200);
  }

  // How long celebrateBeforeClose waits before closing - enough for
  // launchModalConfetti()'s pieces (~1.1s + 0.15s stagger) to finish.
  const MODAL_CONFETTI_CLOSE_DELAY = 1200;

  // Confetti clipped to a modal card, for showAppModal's celebrateBeforeClose.
  function launchModalConfetti(hostEl) {
    if (prefersReducedMotion) return;

    const fallDistance = hostEl.getBoundingClientRect().height + 40;
    const container = document.createElement('div');
    container.className = 'modal-confetti-container';

    const pieceCount = 30;
    for (let i = 0; i < pieceCount; i++) {
      const piece = document.createElement('span');
      piece.className = 'confetti-piece';
      const size = 5 + Math.random() * 5;
      const duration = 0.7 + Math.random() * 0.4;
      piece.style.left = (Math.random() * 100) + '%';
      piece.style.width = size + 'px';
      piece.style.height = (size * 0.4) + 'px';
      piece.style.background = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
      piece.style.animationDuration = duration + 's';
      piece.style.animationDelay = (Math.random() * 0.15) + 's';
      piece.style.setProperty('--drift', ((Math.random() - 0.5) * 90) + 'px');
      piece.style.setProperty('--rotate-start', (Math.random() * 360) + 'deg');
      piece.style.setProperty('--fall-distance', fallDistance + 'px');
      container.appendChild(piece);
    }

    dom.insert(hostEl, container);
    setTimeout(() => dom.remove(container), MODAL_CONFETTI_CLOSE_DELAY + 500);
  }

  /**
   * Draws the result card from the submission endpoint's
   * {ok, title, message, items, email}. Shared by both forms.
   */
  function showSubmissionResult(target, data, isError) {
    const card = el('div', 'response-card');

    // Its own element: playSubmitSuccessAnimation swaps it for the pin.
    card.appendChild(el('div', 'response-icon', isError ? '⚠️' : '✅'));
    card.appendChild(el('div', 'response-title', data.title || ''));

    const body = el('div', 'response-body');

    if (data.message) body.appendChild(document.createTextNode(data.message));

    // The address is its own field so the server never composes markup.
    if (data.email) {
      body.appendChild(document.createTextNode(' We’ve also sent a confirmation to '));
      body.appendChild(el('strong', null, data.email));
      body.appendChild(document.createTextNode('.'));
    }

    if (data.items && data.items.length) {
      const list = el('ul', 'response-list');

      data.items.forEach(item => list.appendChild(el('li', null, item)));
      body.appendChild(list);
    }

    card.appendChild(body);

    target.className = 'submit-modal-result' + (isError ? ' inline' : '');
    target.style.display = 'block';

    // Patched, so a second result (a failed send, then a retry) changes the
    // card in place rather than rebuilding it.
    const next = document.createElement('div');

    next.appendChild(card);
    dom.patch(target, next);
  }

  /** For a network failure or an unreadable response. */
  function submissionFallback(title) {
    return { title, message: 'Please check your connection and try again.', items: [], email: null };
  }

  // ==========================================================================
  // FIELD-LEVEL VALIDATION, SHARED BY BOTH FORMS
  // ==========================================================================
  /* Not reportValidity(): its bubble is browser chrome, invisible to screen
     readers and agents reading the accessibility tree. The constraints stay
     on the elements (modals.blade.php); failures are written into each
     input's aria-describedby slot and flagged with aria-invalid, which the
     CSS also keys its red border off. */

  function fieldErrorSlot(field) {
    const id = field.getAttribute('aria-describedby');

    return id ? dom.idLive(id) : null;
  }

  function clearFieldError(field) {
    const slot = fieldErrorSlot(field);

    field.removeAttribute('aria-invalid');
    if (slot) dom.text(slot, '');
  }

  function showFieldError(field, message) {
    const slot = fieldErrorSlot(field);

    field.setAttribute('aria-invalid', 'true');
    if (slot) dom.text(slot, message);
  }

  /* minlength/maxlength checked by hand: the browser only enforces them on
     typed values, and the WebMCP tools assign values from script. Returns a
     message, or null when the field is fine. */
  function lengthProblem(field) {
    const value = field.value || '';
    const min = parseInt(field.getAttribute('minlength'), 10);
    const max = parseInt(field.getAttribute('maxlength'), 10);

    // Empty is `required`'s business, not "too short".
    if (value && min && value.length < min) return 'Please use at least ' + min + ' characters (currently ' + value.length + ').';
    if (max && value.length > max) return 'Please use no more than ' + max + ' characters (currently ' + value.length + ').';

    return null;
  }

  function fieldIsValid(field) {
    return field.checkValidity() && !lengthProblem(field);
  }

  /* The browser's wording, except a pattern mismatch (title= carries the
     human version) and a length it did not check (lengthProblem). */
  function fieldMessage(field) {
    if (field.validity.patternMismatch && field.title) return field.title;

    return field.validationMessage || lengthProblem(field) || 'Please check this field.';
  }

  // willValidate leaves out hidden inputs, which have no error slot.
  function validatableFields(form) {
    return dom.all('input, textarea, select', form).filter(f => f.willValidate);
  }

  /** True when everything passes; otherwise marks each bad field and
      focuses the first. */
  function validateForm(form) {
    let firstBad = null;

    validatableFields(form).forEach(field => {
      if (fieldIsValid(field)) {
        clearFieldError(field);

        return;
      }

      showFieldError(field, fieldMessage(field));
      if (!firstBad) firstBad = field;
    });

    if (firstBad) firstBad.focus();

    return !firstBad;
  }

  function resetFormValidation(form) {
    if (!form) return;

    validatableFields(form).forEach(clearFieldError);
  }

  /* Clears an error as soon as the field is valid; flags on blur only when
     the field has something in it (empty is not yet wrong). */
  function wireFieldValidation(form) {
    if (!form) return;

    validatableFields(form).forEach(field => {
      field.addEventListener('input', () => {
        if (field.getAttribute('aria-invalid') === 'true' && fieldIsValid(field)) clearFieldError(field);
      });
      field.addEventListener('blur', () => {
        if (field.value !== '' && !fieldIsValid(field)) showFieldError(field, fieldMessage(field));
      });
    });
  }

  // On a genuine success: a bobbing pin in place of the icon, plus confetti.
  function playSubmitSuccessAnimation() {
    const iconEl = dom.one('.response-icon', submitModalResult);

    if (iconEl) {
      const holder = document.createElement('div');

      holder.appendChild(el('span', 'success-emoji', '📍'));
      dom.patch(iconEl, holder);
    } else {
      dom.insert(submitModalResult, el('span', 'success-emoji', '📍'), submitModalResult.firstChild);
    }

    launchConfetti();
  }

  // The shared form modal. requestType ('new_restaurant' from the welcome
  // banner, 'feature_request' from the featured strip) picks the copy and
  // the field set. See SubmitForm and partials/modals.blade.php.
  const SUBMIT_COPY = window.__hcSubmitCopy || {};

  function openSubmitModal(requestType) {
    currentRequestType = requestType;
    const isFeature = requestType === 'feature_request';

    // Copy comes from Site settings; the fallbacks are for a page whose
    // config predates the key.
    const copy = SUBMIT_COPY[requestType] || {};

    dom.text(submitModalTitle, copy.title
      || (isFeature ? 'Get Featured' : 'Submit a Place'));
    dom.text(submitModalSubtitle, copy.subtitle
      || (isFeature
        ? 'Own or manage a halal spot in Calgary? Tell us about it and we\u2019ll be in touch.'
        : 'Know a halal spot we\u2019re missing? Tell us about it.'));

    // The other type's field set is hidden AND disabled - a hidden input
    // still posts, and the server validates per type.
    dom.all('[data-submit-set]', submitModalForm).forEach((set) => {
      const on = set.dataset.submitSet === requestType;

      set.hidden = !on;
      dom.all('input, textarea, select', set).forEach((field) => { field.disabled = !on; });
    });

    submitModalForm.reset();
    // reset() leaves our aria-invalid flags and messages.
    resetFormValidation(submitModalForm);
    submitModalForm.style.display = '';
    submitModalResult.style.display = 'none';
    // '' empties it: no content to keep.
    dom.text(submitModalResult, '');
    dom.text(submitModalError, '');
    submitModalSubmitBtn.disabled = false;
    dom.text(submitModalSubmitBtn, 'Send');

    submitModalBackdrop.classList.add('visible');

    // Preload reCAPTCHA so it is ready by Send; submit retries the load and
    // reports any failure.
    loadRecaptcha().catch(() => {});
  }

  function closeSubmitModal() {
    submitModalBackdrop.classList.remove('visible');
  }

  // Absent on articles pages.
  const welcomeBannerBtn = dom.id('welcome-banner-btn');
  if (welcomeBannerBtn) welcomeBannerBtn.addEventListener('click', () => openSubmitModal('new_restaurant'));

  // The only way into the feature form.
  const featuredCta = dom.id('featured-cta');
  if (featuredCta) featuredCta.addEventListener('click', () => openSubmitModal('feature_request'));

  /* On a phone the welcome banner (the only "Submit a place") moves out of
     the collapsible sidebar to the top of #main, and back above 768px. The
     node is moved, not copied, so its listener and ids stay unique.
     --welcome-h is its measured height for the stylesheet's offsets; Leaflet
     is told on each change, as it only re-measures on window resize. */
  const welcomeBanner = dom.id('welcome-banner');
  const mainShell = dom.id('main');
  const sidebarShell = dom.id('sidebar');

  if (welcomeBanner && mainShell && sidebarShell && window.matchMedia) {
    const phoneWidth = window.matchMedia('(max-width: 768px)');
    let welcomeHeight = -1;

    const measureWelcome = () => {
      const height = phoneWidth.matches ? welcomeBanner.offsetHeight : 0;

      if (height === welcomeHeight) return;

      welcomeHeight = height;

      if (height > 0) {
        mainShell.style.setProperty('--welcome-h', height + 'px');
      } else {
        mainShell.style.removeProperty('--welcome-h');
      }

      if (map) map.invalidateSize();
    };

    const placeWelcome = () => {
      const home = phoneWidth.matches ? mainShell : sidebarShell;

      // Moved, not copied (see above): insert places the same node.
      if (welcomeBanner.parentElement !== home) dom.insert(home, welcomeBanner, home.firstChild);

      measureWelcome();
    };

    placeWelcome();

    if (phoneWidth.addEventListener) {
      phoneWidth.addEventListener('change', placeWelcome);
    } else if (phoneWidth.addListener) {
      phoneWidth.addListener(placeWelcome);
    }

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(measureWelcome).observe(welcomeBanner);
    }
  }

  /* ---- Dismissable strips (announcements bar, featured carousel) ----
     A cross (data-dismiss-strip) sets [hidden] - the nodes stay, so other
     code can keep patching them - and Leaflet is told the map grew.
     Remembered in sessionStorage (guarded: storage can throw), so the strips
     come back on the next visit. #strip-restore brings every hidden strip
     back at once. */
  (function () {
    const strips = dom.all('[data-dismiss-strip]');
    if (!strips.length) return;

    const restore = dom.id('strip-restore');
    const restoreLabel = dom.id('strip-restore-label');
    const LABELS = { announcements: 'announcements', featured: 'featured' };

    const key = (name) => 'hc:strip-dismissed:' + name;
    const remembered = (name) => {
      try { return sessionStorage.getItem(key(name)) === '1'; } catch (e) { return false; }
    };
    const remember = (name, dismissed) => {
      try {
        if (dismissed) sessionStorage.setItem(key(name), '1');
        else sessionStorage.removeItem(key(name));
      } catch (e) { /* no storage: this visit only */ }
    };

    // Each strip and its name, once, so the pill can ask what is hidden.
    const entries = strips.map((btn) => ({
      btn: btn,
      name: btn.getAttribute('data-dismiss-strip'),
      strip: btn.parentElement
    })).filter((e) => e.strip && e.name);

    const syncRestore = () => {
      if (!restore) return;

      const hidden = entries.filter((e) => e.strip.hidden).map((e) => LABELS[e.name] || e.name);

      restore.hidden = hidden.length === 0;
      if (restoreLabel && hidden.length) dom.text(restoreLabel, 'Show ' + hidden.join(' & '));
    };

    const set = (entry, dismissed) => {
      entry.strip.hidden = dismissed;
      // Keep boot.js's pre-paint class (html.hc-hide-<name>) in step.
      document.documentElement.classList.toggle('hc-hide-' + entry.name, dismissed);
      remember(entry.name, dismissed);
    };

    entries.forEach((entry) => {
      if (remembered(entry.name)) entry.strip.hidden = true;

      entry.btn.addEventListener('click', () => {
        set(entry, true);
        syncRestore();
        if (map) map.invalidateSize();
      });
    });

    if (restore) {
      restore.addEventListener('click', () => {
        entries.forEach((entry) => set(entry, false));
        syncRestore();
        if (map) map.invalidateSize();
      });
    }

    syncRestore();
  })();

  /* The reCAPTCHA site key, emitted only when the Site setting is on. Empty
     means off: the form submits with no token and the server skips the check
     (see SubmissionController::passesBotCheck()). */
  const RECAPTCHA_SITE_KEY = dom.one('meta[name="hc-recaptcha-key"]')?.content || '';

  /* Loaded lazily on the first modal open, so Google is not told about every
     page view. One shared promise so racing calls inject one script; reset on
     error so the next attempt retries. */
  const RECAPTCHA_SRC = dom.one('meta[name="hc-recaptcha-src"]')?.content || '';
  let recaptchaLoading = null;

  function loadRecaptcha() {
    if (!RECAPTCHA_SITE_KEY || !RECAPTCHA_SRC) return Promise.resolve();
    if (window.grecaptcha && window.grecaptcha.execute) return Promise.resolve();

    if (!recaptchaLoading) {
      recaptchaLoading = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = RECAPTCHA_SRC;
        s.async = true;
        s.onload = () => {
          // execute() throws before ready().
          if (window.grecaptcha && window.grecaptcha.ready) {
            grecaptcha.ready(resolve);
          } else {
            resolve();
          }
        };
        s.onerror = () => {
          recaptchaLoading = null;
          reject(new Error('recaptcha-unavailable'));
        };
        dom.insert(document.head, s);
      });
    }

    return recaptchaLoading;
  }

  /* Resolves to a token, or '' when reCAPTCHA is off. Rejects only when it
     is on and the script could not load (blocked or offline). */
  function recaptchaToken(action) {
    if (!RECAPTCHA_SITE_KEY) return Promise.resolve('');

    return loadRecaptcha().then(() => {
      if (!window.grecaptcha || !window.grecaptcha.execute) {
        throw new Error('recaptcha-unavailable');
      }

      return grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
    });
  }

  // Absent on articles pages.
  if (submitModalBackdrop) {
    submitModalClose.addEventListener('click', closeSubmitModal);
    submitModalBackdrop.addEventListener('click', e => {
      if (e.target === submitModalBackdrop) closeSubmitModal();
    });
  }

  wireFieldValidation(submitModalForm);

  if (submitModalForm) submitModalForm.addEventListener('submit', e => {
    e.preventDefault();
    dom.text(submitModalError, '');

    if (!validateForm(submitModalForm)) return;

    submitModalSubmitBtn.disabled = true;
    dom.text(submitModalSubmitBtn, 'Sending\u2026');

    recaptchaToken('submit_restaurant')
      .then(token => {
        const formData = new FormData(submitModalForm);
        formData.set('request_type', currentRequestType);
        formData.set('g-recaptcha-response', token);
        return fetch(SUBMISSION_ENDPOINT, {
          method: 'POST',
          body: formData,
          headers: tokenHeaders(),
        });
      })
      // A non-JSON answer (proxy, outage) must not leave the form stuck on
      // "Sending...".
      .then(res => res.json().then(
        data => ({ ok: res.ok, data }),
        () => ({ ok: false, data: null })
      ))
      .then(({ ok, data }) => {
        // Success replaces the form; failure shows above it so the visitor
        // can fix and resend without losing what they typed.
        if (ok) {
          submitModalForm.style.display = 'none';
          showSubmissionResult(submitModalResult, data || {}, false);
          playSubmitSuccessAnimation();
        } else {
          showSubmissionResult(submitModalResult, data || submissionFallback('Could not send that'), true);
          submitModalSubmitBtn.disabled = false;
          dom.text(submitModalSubmitBtn, 'Send');
        }
      })
      .catch(err => {
        // A blocked script gets its own, actionable wording.
        dom.text(submitModalError, err && err.message === 'recaptcha-unavailable'
          ? 'Couldn\u2019t load reCAPTCHA - please disable any ad/script blockers for this site and try again.'
          : 'Something went wrong sending that - please try again.');
        submitModalSubmitBtn.disabled = false;
        dom.text(submitModalSubmitBtn, 'Send');
      });
  });

  // ==========================================================================
  // "REPORT THIS LISTING" MODAL
  // ==========================================================================
  // Same pattern as the submit modal, with its own elements and state.
  // Opened from the report button on the sheet's photo.

  const reportModalBackdrop = dom.id('report-modal-backdrop');
  const reportModalSubtitle = dom.id('report-modal-subtitle');
  const reportModalForm = dom.id('report-modal-form');
  const reportModalError = dom.id('report-modal-error');
  const reportModalResult = dom.id('report-modal-result');
  const reportModalSubmitBtn = dom.id('report-modal-submit-btn');
  const reportModalClose = dom.id('report-modal-close');
  const rfListingNameInput = dom.id('rf-listing-name');
  const rfPageUrlInput = dom.id('rf-page-url');

  function openReportModal(p) {
    dom.text(reportModalSubtitle, 'Let us know what’s wrong with ' + p.name + '.');
    reportModalForm.reset();
    resetFormValidation(reportModalForm);
    reportModalForm.style.display = '';
    reportModalResult.style.display = 'none';
    dom.text(reportModalResult, '');
    dom.text(reportModalError, '');
    reportModalSubmitBtn.disabled = false;
    dom.text(reportModalSubmitBtn, 'Send report');
    // Hidden, auto-captured fields.
    rfListingNameInput.value = p.name;
    rfPageUrlInput.value = window.location.href;
    reportModalBackdrop.classList.add('visible');

    loadRecaptcha().catch(() => {});
  }

  function closeReportModal() {
    reportModalBackdrop.classList.remove('visible');
  }

  if (reportModalBackdrop) {
    reportModalClose.addEventListener('click', closeReportModal);
    reportModalBackdrop.addEventListener('click', e => {
      if (e.target === reportModalBackdrop) closeReportModal();
    });
  }

  wireFieldValidation(reportModalForm);

  if (reportModalForm) reportModalForm.addEventListener('submit', e => {
    e.preventDefault();
    dom.text(reportModalError, '');

    if (!validateForm(reportModalForm)) return;

    reportModalSubmitBtn.disabled = true;
    dom.text(reportModalSubmitBtn, 'Sending…');

    recaptchaToken('report_listing')
      .then(token => {
        const formData = new FormData(reportModalForm);
        formData.set('request_type', 'report_listing');
        formData.set('g-recaptcha-response', token);
        return fetch(SUBMISSION_ENDPOINT, {
          method: 'POST',
          body: formData,
          headers: tokenHeaders(),
        });
      })
      .then(res => res.json().then(
        data => ({ ok: res.ok, data }),
        () => ({ ok: false, data: null })
      ))
      .then(({ ok, data }) => {
        if (ok) {
          reportModalForm.style.display = 'none';
          showSubmissionResult(reportModalResult, data || {}, false);
        } else {
          showSubmissionResult(reportModalResult, data || submissionFallback('Could not send that'), true);
          reportModalSubmitBtn.disabled = false;
          dom.text(reportModalSubmitBtn, 'Send report');
        }
      })
      .catch(err => {
        dom.text(reportModalError, err && err.message === 'recaptcha-unavailable'
          ? 'Couldn\u2019t load reCAPTCHA - please disable any ad/script blockers for this site and try again.'
          : 'Something went wrong sending that - please try again.');
        reportModalSubmitBtn.disabled = false;
        dom.text(reportModalSubmitBtn, 'Send report');
      });
  });

  /* ---- Announcement lightbox ----
     Shows an announcement's full artwork. Clicks are delegated (the strip is
     server-rendered and morphed). The src is set on open and cleared on
     close, so the full-size image is only fetched when wanted. Absent on
     articles pages and when announcements are off. */
  const announcementLightbox = dom.id('announcement-lightbox-backdrop');
  const announcementLightboxImg = dom.id('announcement-lightbox-img');

  function openAnnouncementLightbox(url) {
    if (!announcementLightbox || !url) return;

    // /announcements/<id>/open counts the click and redirects to the proxied
    // image, so it is not proxied again.
    announcementLightboxImg.src = url.startsWith('/announcements/')
      ? url
      : '/api/image.php?url=' + encodeURIComponent(url);
    announcementLightbox.classList.add('visible');
  }

  function closeAnnouncementLightbox() {
    if (!announcementLightbox) return;

    announcementLightbox.classList.remove('visible');
    // Free the decoded full-size image.
    announcementLightboxImg.removeAttribute('src');
  }

  /* Announcement clicks are announced through hcEvents for the analytics
     widget. The site's own count comes from the /announcements/<id>/open
     request instead (AnnouncementClickController), with no tracking script. */
  document.addEventListener('click', e => {
    const card = e.target.closest && e.target.closest('.announcement-card[data-announcement-id]');

    if (!card || !window.hcEvents) return;

    window.hcEvents.emit('announcement-clicked', {
      id: card.dataset.announcementId,
      title: card.dataset.announcementTitle || '',
      kind: card.tagName === 'A' ? 'link' : 'picture',
      url: card.getAttribute('href') || card.getAttribute('data-announcement-image') || null,
    });
  });

  if (announcementLightbox) {
    document.addEventListener('click', e => {
      const card = e.target.closest('[data-announcement-image]');

      if (!card) return;

      e.preventDefault();
      openAnnouncementLightbox(card.getAttribute('data-announcement-image'));
    });

    dom.id('announcement-lightbox-close').addEventListener('click', closeAnnouncementLightbox);

    // A click anywhere, picture included, closes it.
    announcementLightbox.addEventListener('click', closeAnnouncementLightbox);

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') closeAnnouncementLightbox();
    });
  }

  // For deliberate selections: pushes the place's path, so back steps
  // through previously viewed places.
  function pushHashForPlace(p) {
    const slug = placeSlug(p);
    if (getRequestedSlug() === slug) return;
    writePath(PATHS.place(slug), true);
  }

  // Opens a place from the URL (page load, back/forward) without writing
  // the URL.
  function openPlaceFromSlug(slug) {
    const p = findPlaceBySlug(slug);
    if (!p || !markers[placeSlug(p)]) return false;
    panThen(p.lat, p.lng, () => {
      openPlaceModal(p);
      scrollCardIntoViewWhenReady(slug);
    });
    return true;
  }

  /* Scrolling a card into view WITHOUT scrollIntoView(), which also scrolls
     the document - on a phone that pushed the header off screen with no way
     back (the map view locks body overflow). The list's own scroller is set
     directly, using getBoundingClientRect (offsetTop depends on which
     ancestor is positioned). */
  function scrollingAncestor(el) {
    let node = el.parentElement;

    while (node && node !== document.body) {
      const overflowY = getComputedStyle(node).overflowY;

      if ((overflowY === 'auto' || overflowY === 'scroll')
          && node.scrollHeight > node.clientHeight) {
        return node;
      }

      node = node.parentElement;
    }

    return null;
  }

  function scrollCardIntoView(el) {
    const box = scrollingAncestor(el);

    // No scrollable list: do nothing - never fall back to scrollIntoView.
    if (!box) return;

    const elRect = el.getBoundingClientRect();
    const boxRect = box.getBoundingClientRect();

    // Centre the card, clamped at the list's ends.
    const delta = (elRect.top - boxRect.top) - (box.clientHeight - elRect.height) / 2;
    const top = Math.max(0, Math.min(box.scrollTop + delta, box.scrollHeight - box.clientHeight));

    box.scrollTo({ top, behavior: 'smooth' });
  }

  // Polls (every 150ms, up to ~1.5s) for the card, then scrolls to it. For
  // opens from the URL, where renderList's settle timer may already have
  // given up on an earlier mid-pan render.
  function scrollCardIntoViewWhenReady(slug, attempt) {
    const el = dom.idLive('place-card-' + slug);
    if (el) {
      scrollCardIntoView(el);
      return;
    }
    if ((attempt || 0) < 10) {
      setTimeout(() => scrollCardIntoViewWhenReady(slug, (attempt || 0) + 1), 150);
    }
  }

  // ==========================================================================
  // DISTANCE CALCULATIONS
  // ==========================================================================

  // Great-circle distance in kilometres.
  function haversineKm(lat1, lng1, lat2, lng2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.asin(Math.sqrt(a));
  }

  // Re-renders everything that shows a distance after the location changes.
  function refreshDistanceDisplays() {
    if (activePinPlace) {
      // renderPlaceSheet keeps the scroll position - this runs just as a
      // reader has started scrolling.
      renderPlaceSheet(activePinPlace);
    }
    buildFeatured();
    renderList();
  }

  const userIcon = L.divIcon({
    className: '',
    html: '<div class="user-marker-wrap"><span class="user-pulse-ring"></span><span class="user-pulse-ring ring-2"></span><div class="user-dot"></div></div>',
    iconSize: [22, 22],
    iconAnchor: [11, 11]
  });

  let userMarker = null;

  const modalBackdrop = dom.id('app-modal-backdrop');
  const modalEl = dom.id('app-modal');
  const modalTitleText = dom.id('app-modal-title-text');
  const modalMessage = dom.id('app-modal-message');
  const modalPrimaryBtn = dom.id('app-modal-primary');
  const modalSecondaryBtn = dom.id('app-modal-secondary');

  // ==========================================================================
  // APP MODAL (confirmations & alerts)
  // ==========================================================================

  // opts: title; `message` (text) or `messageHtml` (trusted markup from this
  // file, for a real link); primary/secondary labels and callbacks; `hero`
  // for the illustration; `celebrateBeforeClose`.
  function showAppModal(opts) {
    modalEl.classList.toggle('has-hero', !!opts.hero);
    dom.text(modalTitleText, opts.title);
    if (opts.messageHtml) {
      const message = document.createElement('div');

      message.appendChild(fromTrustedMarkup(opts.messageHtml));
      dom.patch(modalMessage, message);
    } else {
      dom.text(modalMessage, opts.message);
    }

    dom.text(modalPrimaryBtn, opts.primaryLabel || 'Got it');
    modalPrimaryBtn.onclick = () => {
      // celebrateBeforeClose: confetti on the modal, then close once it has
      // fallen (immediately under reduced motion).
      if (opts.celebrateBeforeClose && !prefersReducedMotion) {
        modalPrimaryBtn.disabled = true;
        modalSecondaryBtn.disabled = true;
        launchModalConfetti(modalEl);
        setTimeout(() => {
          modalPrimaryBtn.disabled = false;
          modalSecondaryBtn.disabled = false;
          hideAppModal();
          if (opts.onPrimary) opts.onPrimary();
        }, MODAL_CONFETTI_CLOSE_DELAY);
        return;
      }
      hideAppModal();
      if (opts.onPrimary) opts.onPrimary();
    };

    if (opts.secondaryLabel) {
      dom.text(modalSecondaryBtn, opts.secondaryLabel);
      modalSecondaryBtn.style.display = '';
      modalSecondaryBtn.onclick = () => {
        hideAppModal();
        if (opts.onSecondary) opts.onSecondary();
      };
    } else {
      modalSecondaryBtn.style.display = 'none';
    }

    modalOnDismiss = opts.onDismiss || null;
    modalBackdrop.classList.add('visible');
  }

  // Called when the modal is closed by its backdrop rather than a button.
  let modalOnDismiss = null;

  function hideAppModal() {
    modalBackdrop.classList.remove('visible');
  }

  // Absent on articles pages, where showAppModal() is never reached.
  if (modalBackdrop) modalBackdrop.addEventListener('click', e => {
    if (e.target !== modalBackdrop) return;
    hideAppModal();
    if (modalOnDismiss) modalOnDismiss();
  });

  // ==========================================================================
  // GEOLOCATION
  // ==========================================================================

  // On each fix: store it, draw the "you are here" marker, refresh
  // distances, and switch to "closest" on the very first fix.
  function handlePositionSuccess(pos, onSuccess) {
    const firstFix = !userLocation;
    userLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude };
    if (!userMarker) {
      // `title` for an accessible name, as with the place pins.
      userMarker = L.marker([userLocation.lat, userLocation.lng], { icon: userIcon, title: 'Your location', alt: 'Your location' })
        .addTo(map)
        .bindPopup('Your location');
    } else {
      userMarker.setLatLng([userLocation.lat, userLocation.lng]);
    }
    refreshDistanceDisplays();
    // A place may already be selected.
    drawFlightPath();
    if (!viewIncludesUser && dataReady) {
      viewIncludesUser = true;
      applyFitView(true);
    }
    if (firstFix && sortMode === 'default') {
      sortMode = 'closest';
      filterBtns.forEach(b => b.classList.toggle('active', b.dataset.sort === 'closest'));
      // The re-sort moves the open place; re-arm the scroll to it.
      if (activeSlug) pendingScrollSlug = activeSlug;
      if (dataReady) renderList();
    }
    if (onSuccess) onSuccess();
  }

  // A GeolocationPositionError as trusted markup. Permission-denied gets
  // per-platform steps (iOS has no per-site toggle in the browser).
  function describeLocationError(err) {
    if (err.code === err.PERMISSION_DENIED) {
      return 'Location is blocked for this site.\n\n' +
        '<strong>Android (Chrome, Edge, etc.):</strong> tap the lock/info icon next to the address bar &rarr; Permissions &rarr; Location &rarr; Allow.\n\n' +
        '<strong>iPhone/iPad &mdash; Safari:</strong> on iOS, this isn\'t a per-site toggle in the browser itself. Open the Settings app &rarr; Privacy &amp; Security &rarr; Location Services &rarr; Safari Websites &rarr; choose "Ask Next Time Or When I Share" (or "While Using the App"). ' +
        '<a href="https://support.apple.com/guide/personal-safety/manage-location-services-settings-ips9bf20ad2f/web" target="_blank" rel="noopener">Apple\'s guide</a>\n\n' +
        '<strong>iPhone/iPad &mdash; Chrome:</strong> same idea, but under the Chrome entry instead. Open the Settings app &rarr; scroll down and tap Chrome &rarr; Location &rarr; choose "Ask" (or "While Using the App"). ' +
        '<a href="https://support.google.com/chrome/answer/142065" target="_blank" rel="noopener">Google\'s guide</a>\n\n' +
        'Then come back and reload this page.';
    }
    if (err.code === err.TIMEOUT) {
      return 'Getting your location took too long. Check your connection or device location settings and try again.';
    }
    if (err.code === err.POSITION_UNAVAILABLE) {
      return 'Your device couldn\'t determine your location. Make sure Location Services are turned on for your browser, that you have a clear GPS or network signal, and try again.';
    }
    return 'We couldn\'t get your location. You can still browse and sort by rating.';
  }

  // Best-effort permission pre-check; cb('unknown') where the Permissions API
  // cannot answer (older Safari).
  function checkGeoPermission(cb) {
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' })
        .then(status => cb(status.state))
        .catch(() => cb('unknown'));
    } else {
      cb('unknown');
    }
  }

  // Asks for the location: a fast low-accuracy fix first (most reliable on
  // mobile), then high-accuracy GPS if that fails. A sticky toast shows
  // throughout.
  //
  // Unless the browser has already granted location, OUR interstitial
  // always comes before the browser's dialog, whoever asked - only its
  // Enable button reaches getCurrentPosition().
  function requestGeolocation(onSuccess, opts) {
    // quiet: no error modal (for the volunteered request nobody asked for).
    // direct: skip the interstitial - passed only by its own Enable button.
    const quiet = !!(opts && opts.quiet);
    const direct = !!(opts && opts.direct);
    const via = (opts && opts.via) || (quiet ? 'volunteered' : 'asked');
    if (!navigator.geolocation) {
      announceLocation('location-result', { result: 'unsupported', via: via });
      showAppModal({
        title: 'Location not supported',
        message: 'Your browser doesn\'t support location access, so distances and the "Closest" sort won\'t be available.'
      });
      return;
    }

    // Outside a secure context getCurrentPosition fails without a prompt.
    if (!window.isSecureContext) {
      showAppModal({
        title: 'Location needs a secure connection',
        message: 'Location only works when this site is loaded over a secure https:// connection (or on localhost). Open the site using its https:// address to enable it.'
      });
      return;
    }

    checkGeoPermission(state => {
      // Already denied: explain straight away - some mobile browsers take
      // ages (or forever) to fire the error callback.
      if (state === 'denied') {
        announceLocation('location-result', { result: 'denied', via: via });
        if (! quiet) {
          showAppModal({
            title: 'Couldn\'t get your location',
            messageHtml: describeLocationError({ code: 1, PERMISSION_DENIED: 1 })
          });
        }
        return;
      }

      // Not yet granted ('prompt' or 'unknown'): our interstitial first.
      if (state !== 'granted' && ! direct) {
        showLocationInterstitial(onSuccess, via);
        return;
      }

      // A GPS lock can take seconds; without feedback it looks broken.
      showToast('Getting your location\u2026', Infinity);

      const onFinalSuccess = pos => {
        hideToast();
        announceLocation('location-result', { result: 'granted', via: via });
        handlePositionSuccess(pos, onSuccess);
      };
      const onFinalError = err => {
        hideToast();
        announceLocation('location-result', {
          result: err && err.code === 1 ? 'denied' : 'unavailable',
          via: via
        });
        if (quiet) return;
        showAppModal({ title: 'Couldn\'t get your location', messageHtml: describeLocationError(err) });
      };

      // Network-based first: high accuracy first often times out on phones,
      // especially indoors.
      navigator.geolocation.getCurrentPosition(
        onFinalSuccess,
        err => {
          if (err.code === err.PERMISSION_DENIED) {
            onFinalError(err);
            return;
          }
          // GPS, in case network positioning is unavailable.
          navigator.geolocation.getCurrentPosition(
            onFinalSuccess,
            onFinalError,
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
          );
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
      );
    });
  }

  filterBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const mode = btn.dataset.sort;
      if (mode === 'closest' && !userLocation) {
        requestGeolocation(() => {
          applySortMode(mode, btn);
        });
        return;
      }
      applySortMode(mode, btn);
    });
  });

  /* Switches the sort and jumps (not glides - the rows were all replaced)
     back to the top of the list, so the new first place is visible. */
  function applySortMode(mode, btn) {
    sortMode = mode;
    filterBtns.forEach(b => b.classList.toggle('active', b === btn));
    resetListWindow();
    renderList();

    // #place-list is its own scroll container.
    if (listEl) listEl.scrollTop = 0;
  }

  let hasAskedForLocation = false;

  // The location prompt waits for one real interaction: crawlers that
  // render pages (Googlebot included) pass the UA and webdriver checks but
  // never move a mouse or tap.
  let hasHadRealInteraction = false;
  // Set when scheduleLocationPrompt() ran before any interaction;
  // markRealInteraction() then offers the prompt.
  let locationPromptWaitingForInteraction = false;
  // No 'scroll': this capture-phase listener would also catch the list's
  // programmatic scrolling during boot. Real scrolls start with touchstart,
  // wheel or a key.
  const REAL_INTERACTION_EVENTS = ['pointerdown', 'pointermove', 'touchstart', 'keydown', 'wheel'];
  function markRealInteraction() {
    hasHadRealInteraction = true;
    REAL_INTERACTION_EVENTS.forEach(evt => window.removeEventListener(evt, markRealInteraction, true));
    if (locationPromptWaitingForInteraction) {
      locationPromptWaitingForInteraction = false;
      offerLocationPrompt();
    }
  }
  REAL_INTERACTION_EVENTS.forEach(evt => window.addEventListener(evt, markRealInteraction, { capture: true, passive: true }));

  function isLikelyBot() {
    return IS_BOT || !!navigator.webdriver || !navigator.geolocation;
  }

  // Narrower than isLikelyBot(): no UA check, whose false positives (in-app
  // browsers) would hide the prompt from real people. The interaction wait
  // does the rest. Also skipped on articles pages, which have no map.
  function isLikelyBotForLocationPrompt() {
    return !!navigator.webdriver || !navigator.geolocation || document.body.classList.contains('articles-mode');
  }

  // Offers the prompt on the first real interaction, however late - no
  // deadline.
  function scheduleLocationPrompt() {
    if (isLikelyBotForLocationPrompt()) return;
    if (hasHadRealInteraction) {
      offerLocationPrompt();
    } else {
      locationPromptWaitingForInteraction = true;
    }
  }

  /* The location prompt's copy, and whether we volunteer it at all, come
     from Site settings (window.__hcLocationPrompt). Switching it off only
     stops US asking; the Closest sort still asks, via the same interstitial. */
  const LOCATION_PROMPT = window.__hcLocationPrompt || {};

  /* Our interstitial, shown before the browser's dialog for every request
     not already granted. Enable re-enters requestGeolocation() with
     `direct`; Not now changes nothing.

     No "agreed once, skip next time" memory: we cannot tell whether the
     browser will ask again (Safari re-asks per session), so only the
     Permissions API's 'granted' skips it.

     A second call while it is up re-renders it with the later callbacks -
     so a first click on Closest (arriving just after the volunteered
     prompt's pointerdown) wins with its "then sort by distance". */
  /* LOCATION EVENTS for custom widgets (hcEvents, see main.js):
       location-prompt  { action: 'shown'|'accepted'|'declined'|'dismissed', via }
       location-result  { result: 'granted'|'denied'|'unavailable'|'unsupported', via }
     via is 'volunteered' (offered after the first interaction) or 'asked'
     (the visitor tapped something that needs their location). */
  function announceLocation(name, detail) {
    window.hcEvents.emit(name, detail);
  }

  function showLocationInterstitial(onSuccess, via) {
    // Asked this visit - the volunteered prompt must not reappear.
    hasAskedForLocation = true;
    announceLocation('location-prompt', { action: 'shown', via: via });

    showAppModal({
      hero: true,
      title: LOCATION_PROMPT.title || 'See what\'s nearby',
      message: LOCATION_PROMPT.message
        || 'Turn on location and we\u2019ll sort every halal spot by distance, so you always know what\u2019s just around the corner.',
      // Fallbacks for a page without the config block.
      primaryLabel: LOCATION_PROMPT.primary || 'Enable location',
      secondaryLabel: LOCATION_PROMPT.secondary || 'Not now',
      celebrateBeforeClose: true,
      onPrimary: () => {
        announceLocation('location-prompt', { action: 'accepted', via: via });
        requestGeolocation(onSuccess, { direct: true, via: via });
      },
      onSecondary: () => announceLocation('location-prompt', { action: 'declined', via: via }),
      onDismiss: () => announceLocation('location-prompt', { action: 'dismissed', via: via })
    });
  }

  /* The prompt we volunteer, once per visit, after the first real
     interaction. requestGeolocation() does the rest: 'granted' fixes
     silently, 'denied' stays quiet, anything else gets the interstitial. */
  function offerLocationPrompt() {
    if (LOCATION_PROMPT.enabled === false) return;
    if (hasAskedForLocation || userLocation || isLikelyBotForLocationPrompt()) return;
    hasAskedForLocation = true;

    requestGeolocation(undefined, { quiet: true });
  }

  // ==========================================================================
  // SIDEBAR LIST
  // ==========================================================================

  /* The homepage's own tags, restored once no place or filter is active -
     from window.__hcHomeMeta (PageResolver::homeMeta()). Not read off the
     DOM, which on a /places/slug/ load carries that place's tags; the DOM is
     only the fallback for a config block that failed to parse. */
  const HOME_META = window.__hcHomeMeta || {};
  const metaAttr = (selector, attr = 'content') => {
    const el = document.querySelector(selector);
    return el ? (el.getAttribute(attr) || '') : '';
  };
  const SITE_TITLE = HOME_META.title || document.title;
  const SITE_DESCRIPTION = HOME_META.description || metaAttr('meta[name="description"]');
  const SITE_OG_DESCRIPTION = HOME_META.ogDescription || metaAttr('meta[property="og:description"]');
  const SITE_IMAGE = HOME_META.image || metaAttr('meta[property="og:image"]');
  const SITE_IMAGE_ALT = HOME_META.imageAlt || metaAttr('meta[property="og:image:alt"]');
  const SITE_IMAGE_TYPE = HOME_META.imageType || 'image/gif';
  const CANONICAL_BASE = (HOME_META.url || (location.origin + '/')).replace(/\/?$/, '/');
  // What a place's own title and meta description are worded from - see
  // placeMetaWords().
  const PLACE_META = placeMetaConfig(window.__hcPlaceMeta, SITE_NAME);

  // ---- URL shape ----
  // /places/{slug}/, /articles/{slug}/, /{category}/, /cuisine/{cuisine}/.
  // ?search= is the only query param kept across path writes. An open place
  // keeps the active filter in JS state only; closing returns to the
  // filter's path (or /).
  const PATHS = {
    home: () => '/',
    place: (slug) => '/places/' + slug + '/',
    category: (slug) => '/' + slug + '/',
    // Must match App\Support\Urls::cuisine(), or the client rewrites the URL
    // the server just served.
    cuisine: (cuisine) => '/cuisine/' + cuisine + '/'
  };

  // Current URL as {view, slug}. Mirrors the server's route table.
  function parseLocationPath() {
    const seg = location.pathname.split('/').filter(Boolean);
    if (!seg.length) return { view: 'places', slug: '' };
    if (seg[0] === 'places') return { view: seg[1] ? 'place' : 'places', slug: seg[1] || '' };
    if (seg[0] === 'articles') return { view: seg[1] ? 'article' : 'articles', slug: seg[1] || '' };
    if (seg[0] === 'cuisine') return { view: 'cuisine', slug: '', cuisine: seg[1] || '' };
    // Any other two-segment path is a server 404 - nothing to filter by.
    if (seg[1]) return { view: 'other', slug: '' };
    return { view: 'category', slug: seg[0] };
  }

  // The query kept across every path write (only ?search=). Add a param
  // here to make it survive navigation.
  function preservedQuery() {
    const q = new URLSearchParams(location.search).get('search');

    return q ? '?search=' + encodeURIComponent(q) : '';
  }

  // Where the map returns to when no place is open: the active filter's
  // path, or /.
  function basePath() {
    const active = Object.keys(activeAmenityCategories).find(k => activeAmenityCategories[k]);

    if (activeCuisine) return PATHS.cuisine(activeCuisine);

    return active ? PATHS.category(active) : PATHS.home();
  }

  /* Whether a URL was already written in the current task; cleared by a
     microtask, so it spans exactly one synchronous handler. */
  let urlWrittenThisTask = false;

  function writePath(path, push) {
    const url = path + preservedQuery();

    // One user action, one history entry: the first write in a task decides
    // whether to push and later writes in the same task replace it (e.g.
    // choosing a cuisine first clears categories). Never push a duplicate of
    // the current entry - it would make a dead back press.
    if (push && (urlWrittenThisTask || url === location.pathname + location.search)) {
      push = false;
    }

    if (!urlWrittenThisTask) {
      urlWrittenThisTask = true;
      Promise.resolve().then(() => { urlWrittenThisTask = false; });
    }

    try {
      if (push) history.pushState(null, '', url);
      else history.replaceState(null, '', url);
    } catch (err) {
      location.assign(url);
    }
  }

  // The tags updatePageMeta() keeps in sync with the active place/filter -
  // browsers' own share sheets read them off the live page.
  const metaEls = {
    title: dom.one('head > title'),
    description: dom.one('meta[name="description"]'),
    canonical: dom.one('link[rel="canonical"]'),
    ogTitle: dom.one('meta[property="og:title"]'),
    ogDescription: dom.one('meta[property="og:description"]'),
    ogUrl: dom.one('meta[property="og:url"]'),
    ogImage: dom.one('meta[property="og:image"]'),
    ogImageType: dom.one('meta[property="og:image:type"]'),
    ogImageAlt: dom.one('meta[property="og:image:alt"]'),
    twitterTitle: dom.one('meta[name="twitter:title"]'),
    twitterDescription: dom.one('meta[name="twitter:description"]'),
    twitterImage: dom.one('meta[name="twitter:image"]'),
    twitterImageAlt: dom.one('meta[name="twitter:image:alt"]')
  };

  // The original sr-only heading/intro, captured at load for restoring. The
  // map's own pages only: a place page has none, its H1 being the sheet's
  // title (see buildPopupNode), so there this keeps nothing in step.
  const pageH1El = dom.id('page-h1');
  const pageIntroEl = dom.id('page-intro');
  const DEFAULT_PAGE_H1 = pageH1El ? pageH1El.textContent : '';
  const DEFAULT_PAGE_INTRO = pageIntroEl ? pageIntroEl.textContent : '';

  // Keeps the sr-only <h1>/intro in step with the open place, as the server
  // renders it for a place URL. null restores the originals.
  function updateSeoPlaceHeader(p) {
    if (!pageH1El || !pageIntroEl) return;
    if (!p) {
      dom.text(pageH1El, DEFAULT_PAGE_H1);
      dom.text(pageIntroEl, DEFAULT_PAGE_INTRO);
      return;
    }
    dom.text(pageH1El, p.name);
    dom.text(pageIntroEl, detailsText(p.details) || p.summary || p.address || '');
  }

  // The one chokepoint for the active place, however it became active:
  // highlight, title, meta tags, URL and the 'place-viewed' event. null
  // restores the page's own title and tags.
  function setActiveCard(p) {
    const slug = p ? placeSlug(p) : null;
    // Opening a place calls this twice (focusPlace(), then openPlaceModal()),
    // so 'place-viewed' fires only when the place actually changes.
    const placeChanged = slug !== activeSlug;

    activeSlug = slug;
    updateSeoPlaceHeader(p);
    updatePageMeta();
    if (p) {
      pendingScrollSlug = slug;
      // A stale ?search= no longer describes the page. The search box itself
      // is left alone - someone may be mid-typing.
      setUrlParam('search', null, false);
      // Announced through hcEvents (main.js) for the custom widgets, not
      // reported to any vendor here. No `path`: the URL has not moved yet
      // and would still name the previous place.
      if (placeChanged) {
        window.hcEvents.emit('place-viewed', { name: p.name, slug: slug });
      }
    }
    renderList();
  }

  // ---- Search ----
  // Words people type for a category, which match the place's flag rather
  // than its prose. Matched loosely in both directions (plurals land too).
  // Keep in step with PlaceSearch.
  const SEARCH_CATEGORY_WORDS = {
    prayerSpace: ['prayer space', 'prayer room', 'prayer', 'musalla', 'musallah', 'namaz', 'salah', 'pray'],
    placeOfWorship: ['mosque', 'masjid', 'islamic centre', 'islamic center', 'place of worship', 'worship'],
    clothing: ['clothing', 'clothes', 'abaya', 'hijab', 'thobe', 'modest wear', 'islamic clothing'],
    grocery: ['grocery', 'groceries', 'butcher', 'meat shop', 'supermarket', 'halal meat'],
    pharmacy: ['pharmacy', 'pharmacies', 'chemist', 'drugstore', 'drug store', 'prescription', 'prescriptions', 'dispensary', 'pharmacist'],
    dinein: ['dine in', 'dine-in', 'sit down', 'eat in', 'restaurant'],
    takeout: ['takeout', 'take out', 'takeaway', 'take-away', 'pickup', 'pick up'],
    delivery: ['delivery', 'deliver', 'delivers'],
    patio: ['patio', 'outdoor seating', 'outdoors', 'outside', 'terrace'],
  };

  // The category fields a query is asking for, if any.
  function queryCategories(q) {
    const hits = [];

    for (const field in SEARCH_CATEGORY_WORDS) {
      const matched = SEARCH_CATEGORY_WORDS[field].some(word =>
        // Two characters minimum, so a stray letter selects nothing.
        q.length >= 2 && (word.includes(q) || q.includes(word))
      );

      if (matched) hits.push(field);
    }

    // Every enabled category's own label matches too, so the word on a tag
    // is always one the search answers to. Mirrored in PlaceSearch.
    const categories = TAG_CATALOGUE.categories || {};

    for (const slug in categories) {
      const label = String(categories[slug].label || '').toLowerCase();
      const field = categories[slug].field;

      if (q.length >= 2 && label && field && (label.includes(q) || q.includes(label)) && hits.indexOf(field) === -1) hits.push(field);
    }

    return hits;
  }

  // The cuisine tags a query names, matched loosely on their labels.
  // Mirrors PlaceSearch::queryCuisines.
  function queryCuisines(q) {
    const hits = [];
    const cuisines = TAG_CATALOGUE.cuisines || {};

    if (q.length < 2) return hits;

    for (const slug in cuisines) {
      const label = String(cuisines[slug].label || '').toLowerCase();

      if (label && (label.includes(q) || q.includes(label))) hits.push(slug);
    }

    return hits;
  }

  // How well a place answers a query: 0 is best, -1 is no match. Tiers, in
  // order: name, cuisine tag, category flag, address, prose. Cuisine beats
  // category - what a place IS beats what it does.
  const SEARCH_RANK = { NAME_START: 0, NAME: 1, CUISINE: 2, CATEGORY: 3, ADDRESS: 4, DETAILS: 5 };

  /* searchScore for one query over many places. Which cuisines and
     categories the query names depends only on the query, so it is worked
     out once here rather than once per place, every keystroke. The scorer
     lives for one pass over the places; nothing is kept between keystrokes. */
  function searchScorer(query) {
    if (!query) return () => SEARCH_RANK.NAME;

    const q = query.toLowerCase().trim();
    const cuisineHits = queryCuisines(q);
    const categoryHits = queryCategories(q);

    return p => {
      const name = (p.name || '').toLowerCase();

      if (name.startsWith(q)) return SEARCH_RANK.NAME_START;
      if (name.includes(q)) return SEARCH_RANK.NAME;

      // Cuisine and category read the record's tags and flags, not its prose.
      const cuisines = Array.isArray(p.cuisines) ? p.cuisines : [];

      if (cuisineHits.some(slug => cuisines.indexOf(slug) !== -1)) return SEARCH_RANK.CUISINE;

      if (categoryHits.some(field => p[field])) return SEARCH_RANK.CATEGORY;

      if ((p.address || '').toLowerCase().includes(q)) return SEARCH_RANK.ADDRESS;
      if (p.summary && p.summary.toLowerCase().includes(q)) return SEARCH_RANK.DETAILS;

      return -1;
    };
  }

  // One place, one query - for matchesSearch and the WebMCP tools.
  function searchScore(p, query) {
    return searchScorer(query)(p);
  }

  function matchesSearch(p, query) {
    return searchScore(p, query) !== -1;
  }

  /* "Does this place survive the active filters" (categories AND cuisine) -
     shared by the list, the pins and the redirect guard so they never drift
     apart. A pass over every place reads the active category fields once,
     with activeCategoryFields(), and asks passesFilters() per place; never
     hand passesFilters to .filter() itself, which would pass the index as
     the fields. */
  function activeCategoryFields() {
    return Object.keys(activeAmenityCategories)
      .filter(key => activeAmenityCategories[key])
      .map(key => AMENITY_FIELDS[key]);
  }

  function passesFilters(p, fields) {
    if (!passesActiveCuisine(p)) return false;

    for (let i = 0; i < fields.length; i++) {
      if (!p[fields[i]]) return false;
    }

    return true;
  }

  // The answer for one place (the redirect guard, the WebMCP tools).
  function passesActiveCategories(p) {
    return passesFilters(p, activeCategoryFields());
  }

  /* The cuisine filter: what a place IS, combined with the categories (what
     it offers) by AND. Folded into passesFilters. */
  let activeCuisine = null;

  /* Arrows for a horizontal strip whose scrollbar is hidden (a mouse wheel
     cannot scroll sideways). Shared by the cuisine row and the reviews
     strip; the arrows start hidden in the markup so they never flash.
     Returns sync, for strips whose contents arrive later.

     A strip is wired once: dom.patch keeps a node it can match, so the same
     strip and arrows come round again, and binding them again would stack a
     second set of listeners (and a resize listener) on each pass. A repeat
     call only re-measures. */
  const wiredStrips = new WeakMap();

  function attachScrollArrows(scroller, prev, next) {
    if (!scroller || !prev || !next) return () => {};

    const wired = wiredStrips.get(scroller);

    if (wired && wired.prev === prev && wired.next === next) {
      wired.sync();

      return wired.sync;
    }

    function sync() {
      const max = scroller.scrollWidth - scroller.clientWidth;
      // 4px of slack for sub-pixel layout.
      const scrollable = max > 4;

      prev.hidden = !scrollable || scroller.scrollLeft <= 2;
      next.hidden = !scrollable || scroller.scrollLeft >= max - 2;
    }

    function nudge(direction) {
      // Most of a screenful.
      scroller.scrollBy({
        left: direction * Math.max(160, scroller.clientWidth * 0.8),
        behavior: 'smooth',
      });
    }

    prev.addEventListener('click', () => nudge(-1));
    next.addEventListener('click', () => nudge(1));
    scroller.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);

    // Re-measure on resize: a strip measured before layout (e.g. reviews
    // landing in a sheet still animating open) reports no overflow.
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(sync).observe(scroller);
    }

    wiredStrips.set(scroller, { prev, next, sync });
    sync();

    return sync;
  }

  /* Assigned by the cuisine row once built, so the "show all" / reset paths
     can clear it. A no-op where there is no row. */
  let clearCuisineRow = () => {};

  /* Sets the row to whatever the URL says, including none (back/forward
     needs to turn one ON). Assigned by the row, like clearCuisineRow. */
  let setCuisineRow = () => {};

  /* A tag's label, read from the server-rendered row rather than a second
     copy of the catalogue. */
  function cuisineLabel(slug) {
    const label = dom.one('.cuisine-tag[data-cuisine="' + slug + '"] .cuisine-tag-label');

    return label ? label.textContent.trim() : slug;
  }

  function passesActiveCuisine(p) {
    if (!activeCuisine) return true;

    const tags = p.cuisines;

    return Array.isArray(tags) && tags.indexOf(activeCuisine) !== -1;
  }

  /* How far beyond the screen a pin is still drawn, as a fraction of the
   * viewport per side. Pins are re-evaluated only when a pan ENDS, so this
   * margin has to cover the pan itself. */
  const MARKER_BOUNDS_PAD = 0.5;

  /* Pins on the map: those passing the filters AND near the screen, to keep
   * the DOM small. Off-screen removal is not a filter - the list, counts and
   * search are untouched, and the marker object survives (only its DOM
   * goes). The open place is exempt: a pan towards it needs its pin first. */
  function updateMarkerVisibility() {
    const bounds = map.getBounds().pad(MARKER_BOUNDS_PAD);
    const fields = activeCategoryFields();

    places.forEach(p => {
      const slug = placeSlug(p);
      const marker = markers[slug];
      if (!marker) return;
      const shouldShow = passesFilters(p, fields)
        && (slug === activeSlug || bounds.contains(marker.getLatLng()));
      const isShown = map.hasLayer(marker);
      if (shouldShow && !isShown) {
        marker.addTo(map);
      } else if (!shouldShow && isShown) {
        map.removeLayer(marker);
      }
    });
  }

  /** Moves the highlight to the open place without touching anything else. */
  function highlightActiveCard() {
    const wanted = activeSlug ? 'place-card-' + activeSlug : null;

    Array.prototype.forEach.call(listEl.children, card => {
      card.classList.toggle('active', card.id === wanted && wanted !== null);
    });
  }

  /**
   * Scrolls to the just-opened place once the list stops being rebuilt -
   * the timer resets on each render. 450ms leaves margin past Leaflet's
   * ~250ms pan animation.
   */
  function scheduleCardScroll() {
    if (!pendingScrollSlug) return;

    const slug = pendingScrollSlug;

    clearTimeout(scrollSettleTimer);
    scrollSettleTimer = setTimeout(() => {
      if (pendingScrollSlug !== slug) return; // superseded by a newer request

      // Collapsed: keep the slug pending for setSidebarCollapsed() to honour
      // when the list opens.
      if (sidebar.classList.contains('collapsed')) return;

      const target = dom.idLive('place-card-' + slug);

      if (target) {
        scrollCardIntoView(target);
        pendingScrollSlug = null;
      }
      // Not built yet: stay pending for the next render's check.
    }, 450);
  }

  // What the list on screen was last built from; see renderList().
  let lastListSignature = null;

  /* The list is built as a window - a prefix of the sorted list - grown by
   * this many cards as the reader nears the end (60 is ~15 phone screens).
   * Keeps the DOM small enough for iOS not to kill the tab. renderList keeps
   * the active card inside the window whatever its position. */
  const LIST_WINDOW_STEP = 60;
  let listWindow = LIST_WINDOW_STEP;
  // How many places passed the filters last render - the window's ceiling.
  let lastVisibleCount = 0;

  /* Called by the filter, sort and search paths (which change what the list
   * contains) - not inferred in renderList, which also runs for pans and
   * must keep a window the reader has scrolled into. */
  function resetListWindow() {
    listWindow = LIST_WINDOW_STEP;
  }

  // Grows the window a screenful before the end, so the list never visibly
  // stops. Passive: it only reads offsets.
  listEl.addEventListener('scroll', () => {
    if (!dataReady) return;
    // Everything that passes the filters is already built.
    if (listEl.childElementCount >= lastVisibleCount) return;
    // Not within a screenful of the end yet.
    if (listEl.scrollTop + listEl.clientHeight < listEl.scrollHeight - listEl.clientHeight) return;

    listWindow = listEl.childElementCount + LIST_WINDOW_STEP;
    renderList();
  }, { passive: true });

  // Rebuilds the sidebar list: every place passing the filters, sorted -
  // never filtered by the viewport. Thumbnails lazy-load via window.hcLazy,
  // rooted at #place-list because the list scrolls in its own container.
  function renderList() {
    // Until the data lands, the server-rendered list IS the list (otherwise
    // an early call would patch it to "No places found.").
    if (!dataReady) return;

    // Read before it is cleared below: the first render after the server's
    // full list.
    const firstRender = !!listEl && listEl.hasAttribute('data-ssr');

    // data-ssr let the stylesheet skip off-screen card layout for first
    // paint; skipped cards report estimated heights, which would misdirect
    // scrollCardIntoView.
    if (listEl) listEl.removeAttribute('data-ssr');

    let visible = places.slice();

    const fields = activeCategoryFields();
    const anyCategoryActive = fields.length > 0;
    visible = visible.filter(p => passesFilters(p, fields));

    if (sortMode === 'rating') {
      // The rating weighed by how many reviews back it, the count first - see
      // sortTopRated(). Constants from all `places`, never `visible`.
      visible = sortTopRated(visible, places);
    } else if (sortMode === 'closest' && userLocation) {
      visible = visible.slice().sort((a, b) =>
        haversineKm(userLocation.lat, userLocation.lng, a.lat, a.lng) -
        haversineKm(userLocation.lat, userLocation.lng, b.lat, b.lng)
      );
    } else if (sortMode === 'default') {
      // "Best". Constants from all `places`, never `visible` - see
      // rankingConstants(). Distance from the configured home view's centre
      // (PlaceRepository::rankingContext() uses the same Site settings), open
      // now as of this moment.
      visible = sortBest(visible, places, { lat: startLat, lng: startLng, now: Date.now() / 1000 });
    }

    // Only a window is built (see LIST_WINDOW_STEP), always including the
    // active place so it can be highlighted and scrolled to.
    lastVisibleCount = visible.length;

    const activeIndex = activeSlug ? visible.findIndex(p => placeSlug(p) === activeSlug) : -1;
    const windowSize = Math.max(listWindow, activeIndex + 1);
    const windowed = visible.length > windowSize ? visible.slice(0, windowSize) : visible;

    // When nothing the rows are built from has changed (a pan, or opening a
    // place), only the highlight moves. The fingerprint must cover order,
    // filters and the location distances are measured from.
    const signature = [
      sortMode,
      visible.length,
      windowed.length,
      Object.keys(activeAmenityCategories).filter(k => activeAmenityCategories[k]).join(','),
      userLocation ? userLocation.lat.toFixed(5) + ',' + userLocation.lng.toFixed(5) : '',
      visible.length ? visible[0].name + '|' + visible[visible.length - 1].name : '',
    ].join('~');

    // Before the early return, so a skipped rebuild still leaves the banner
    // right.
    renderCategoryBanner();

    const alreadyRendered = signature === lastListSignature
      && listEl.childElementCount === windowed.length
      && windowed.length > 0;

    if (alreadyRendered) {
      highlightActiveCard();
      scheduleCardScroll();

      return;
    }

    lastListSignature = signature;

    // Drop the lazy-load observer (it holds strong references to removed
    // cards) and re-arm it after the patch. Loaded images have lost their
    // data-src, so nothing is re-fetched.
    window.hcLazy.reset('#place-list');

    /* THE SERVER'S FULL LIST, CUT TO THE WINDOW FIRST. The first render
       inherits every server card but builds only the window; the patch would
       discard the rest anyway, after morphdom had indexed and walked each of
       them. Dropping them here leaves the patch the window alone. The cards
       in the window are still adopted and morphed below - the server card is
       not the script's card (no Facebook, prayer-space or clothing badge), so
       they must never be kept as they are. Whatever is not a card in the
       window goes too (the search note, the template's edge whitespace), as
       the patch would remove it. */
    if (firstRender) {
      const keep = new Set(windowed.map(p => 'place-card-' + placeSlug(p)));

      for (let node = listEl.firstChild; node;) {
        const following = node.nextSibling;

        if (node.nodeType !== 1 || !keep.has(node.id)) dom.remove(node);
        node = following;
      }
    }

    // Built detached, then dom.patch()ed in: unchanged cards keep their
    // nodes, so scroll position, loaded thumbnails and focus survive. Keyed
    // on each card's id, so a re-sort moves cards rather than rewriting them.
    const next = document.createElement('div');

    if (visible.length === 0) {
      const empty = el('div', null, anyCategoryActive
        ? 'No places match the selected categories.'
        : 'No places found.');

      empty.id = 'empty-state';
      next.appendChild(empty);
    } else {
      windowed.forEach(p => {
        // An <a> with no listeners, matching the server card so morphdom
        // adopts it (a kept node never gets the new tree's listeners). Clicks
        // are delegated - see delegateCards(); the href is the no-JS and
        // new-tab fallback.
        const card = document.createElement('a');
        card.id = 'place-card-' + placeSlug(p);
        card.dataset.slug = placeSlug(p);
        card.href = '/places/' + placeSlug(p) + '/';
        card.className = 'place-card' + (placeSlug(p) === activeSlug ? ' active' : '');
        card.setAttribute('aria-label', 'View ' + p.name + ' on the map');
        card.title = p.name;

        const text = el('div', 'place-card-text');

        text.appendChild(el('div', 'place-name', p.name));

        const closed = closedNoticeNode(p, false);

        if (closed) text.appendChild(closed);

        // Google rating with its count, then Facebook, then price.
        const meta = el('div', 'place-meta');
        const rating = el('span', 'place-rating');

        rating.appendChild(ratingBadgeNode(p, true));
        meta.appendChild(rating);

        const facebook = facebookReviewNode(p);

        if (facebook) meta.appendChild(metaSegment(facebook));

        if (priceLabel(p)) {
          meta.appendChild(metaSegment(
            el('span', 'price-tag', priceLabel(p)),
            prayerSpaceNode(p),
            clothingNode(p),
          ));
        }

        text.appendChild(meta);
        text.appendChild(el('div', 'place-address', p.address));

        // Lit by the active filter only - the typed search narrows the
        // dropdown, not this list.
        const tags = placeTagsNode(p);

        if (tags) text.appendChild(tags);

        if (p.summary) {
          text.appendChild(el('div', 'place-details', p.summary));
        }

        const inner = el('div', 'place-card-inner');

        // Direct src (native loading=lazy), not placeholder/data-src: the SSR
        // card ships the real URL, and swapping it would refetch and flicker.
        inner.appendChild(thumbWithRingNode(p, 'place-thumb', 'place-thumb-frame'));
        inner.appendChild(text);

        // The distance sits under the photo (the grid places it by area).
        // Last in the DOM, so a card without one has the same shape minus
        // the pill.
        if (userLocation) {
          const km = haversineKm(userLocation.lat, userLocation.lng, p.lat, p.lng);

          inner.appendChild(el('span', 'place-distance place-card-distance', km.toFixed(1) + ' km'));
        }

        card.appendChild(inner);
        next.appendChild(card);
      });
    }

    dom.patch(listEl, next);

    // After the patch, against the live list (kept and inserted nodes alike).
    if (visible.length > 0) window.hcLazy.scan(listEl);

    scheduleCardScroll();
  }

  /* A move never changes what the list holds (it is never filtered by the
     viewport): every filter, sort, location, place and window change calls
     renderList() itself. So a move only moves the highlight and re-arms the
     pending scroll - renderList's early-return work, without re-filtering
     and re-sorting every place. 'moveend' only: Leaflet fires it straight
     after every zoomend, so 'moveend zoomend' ran each handler twice per
     zoom. A new path that changes the list's inputs must call renderList();
     the next pan will not repair it. */
  map.on('moveend', function listFollowsMove() {
    if (!dataReady) return;
    highlightActiveCard();
    scheduleCardScroll();
  });
  // The pins follow the view.
  map.on('moveend', updateMarkerVisibility);

  const searchSuggestions = dom.id('search-suggestions');

  // ==========================================================================
  // SEARCH AUTOCOMPLETE
  // ==========================================================================

  /* The dropdown must sit above the place sheet, but it lives in #topbar's
     stacking context (z-index 1000, below the sheet's 1200). So the topbar
     itself is raised while the dropdown is open - via a class, not :has(). */
  const searchTopbar = dom.id('topbar');

  function setSuggestionsOpen(open) {
    searchSuggestions.classList.toggle('visible', open);

    if (searchTopbar) searchTopbar.classList.toggle('search-open', open);
  }

  // Guarded: the document-wide click-away calls this on articles pages too,
  // where there is no dropdown.
  function closeSuggestions() {
    if (!searchSuggestions) return;
    setSuggestionsOpen(false);
    // '' empties it (and writes nothing when it is already empty).
    dom.text(searchSuggestions, '');
  }

  // Pans, then runs cb. Already centred there, panTo() may never fire
  // moveend, so cb runs at once.
  function panThen(lat, lng, cb) {
    const target = L.latLng(lat, lng);
    if (map.getCenter().distanceTo(target) < 1) {
      cb();
    } else {
      map.once('moveend', cb);
      map.panTo(target);
    }
  }

  // What every "open this place" click does. setActiveCard runs now too, so
  // the sidebar does not wait for the pan.
  function focusPlace(p) {
    setActiveCard(p);
    pushHashForPlace(p);
    panThen(p.lat, p.lng, () => {
      openPlaceModal(p);
    });
  }

  /* Card delegation - one listener per container. Cards come from the server
     and from renderList()/buildFeatured(), and the marquee clones them, so
     per-card listeners would be missing on adopted and cloned cards. A
     modified or non-left click is left to the browser (new tab). */
  function placeFromCardSlug(slug) {
    return findPlaceBySlug(slug);
  }

  function delegateCards(container) {
    if (!container) return;

    container.addEventListener('click', (e) => {
      const card = e.target.closest('a[data-slug]');

      if (!card || !container.contains(card)) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

      const p = placeFromCardSlug(card.dataset.slug);

      if (!p) return;
      e.preventDefault();
      focusPlace(p);
    });

    container.addEventListener('keydown', (e) => {
      const card = e.target.closest('a[data-slug]');

      if (!card) return;

      if (e.key === ' ') {
        // Enter activates a link natively; Space does not.
        e.preventDefault();
        card.click();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        // Roving focus through the list.
        e.preventDefault();
        const sibling = e.key === 'ArrowDown' ? card.nextElementSibling : card.previousElementSibling;

        if (sibling && sibling.matches('a[data-slug]')) {
          // preventScroll, then scroll the list ourselves: focus()
          // otherwise scrolls every ancestor including the document.
          sibling.focus({ preventScroll: true });
          if (sibling.classList.contains('place-card')) scrollCardIntoView(sibling);
        }
      }
    });

    // Thumbnail error fallback, delegated too (error does not bubble, but
    // it does capture): per-image listeners would miss adopted SSR images.
    container.addEventListener('error', (e) => {
      const img = e.target;

      if (!img || img.tagName !== 'IMG' || !img.classList.contains('place-thumb') && !img.classList.contains('featured-thumb')) return;
      if (img.src === defaultThumbSrc) return;
      img.src = defaultThumbSrc;
      img.classList.add(img.classList.contains('place-thumb') ? 'place-thumb-fallback' : 'featured-thumb-fallback');
    }, true);
  }

  delegateCards(dom.id('place-list'));
  delegateCards(dom.id('featured-row'));

  function selectSuggestion(p) {
    // Announce the query before it is wiped; the pick itself arrives as
    // 'place-viewed' from focusPlace().
    announceSearch('suggestion');
    lastSearchMade = '';
    searchInput.value = '';
    // Setting .value fires no 'input', so resume the placeholder demo here.
    resumeSearchPlaceholderDemo();
    closeSuggestions();
    searchInput.blur();
    focusPlace(p);
  }



  // Which field a suggestion's match line shows, in searchScore's order (a
  // cuisine hit is shown by lighting its chip instead).
  function matchedField(p, query) {
    const q = query.toLowerCase();
    if (p.name.toLowerCase().includes(q)) return 'name';
    if (queryCuisines(q).some(slug => Array.isArray(p.cuisines) && p.cuisines.indexOf(slug) !== -1)) return 'cuisine';
    if (queryCategories(q).some(field => p[field])) return 'category';
    if (p.address.toLowerCase().includes(q)) return 'address';
    if (p.summary && p.summary.toLowerCase().includes(q)) return 'details';
    return null;
  }

  // The label shown on a suggestion found by its category.
  const CATEGORY_MATCH_LABELS = {
    prayerSpace: 'Prayer space',
    placeOfWorship: 'Place of worship',
    clothing: 'Clothing',
    grocery: 'Grocery',
    dinein: 'Dine-in',
    takeout: 'Takeout',
    delivery: 'Delivery',
    patio: 'Patio',
  };

  function categoryMatchLabel(p, query) {
    const fields = queryCategories(query.toLowerCase()).filter(field => p[field]);
    const categories = TAG_CATALOGUE.categories || {};
    // For enabled categories the fixed list does not cover.
    const rowLabel = field => {
      for (const slug in categories) {
        if (categories[slug].field === field) return categories[slug].label;
      }

      return null;
    };

    return fields.length
      ? fields.map(f => CATEGORY_MATCH_LABELS[f] || rowLabel(f)).filter(Boolean).join(' &middot; ')
      : '';
  }

  // The arrow-key highlight (-1 = none); reset on every rebuild.
  let highlightedSuggestionIndex = -1;

  // Suggestions shown: six, then a dozen more per "more" press (a category
  // search can match most of the map).
  const SUGGESTION_PAGE = 6;
  const SUGGESTION_MORE = 12;
  let suggestionLimit = SUGGESTION_PAGE;
  let lastSuggestionQuery = '';

  // Rebuilds the autocomplete dropdown from the current search text.
  function renderSuggestions() {
    const query = searchInput.value.trim();
    highlightedSuggestionIndex = -1;

    // Only a new query resets the limit ("more" re-renders the same query).
    if (query !== lastSuggestionQuery) {
      lastSuggestionQuery = query;
      suggestionLimit = SUGGESTION_PAGE;
    }

    if (!query) {
      closeSuggestions();
      return;
    }

    const scored = [];
    const score = searchScorer(query);

    places.forEach(p => {
      const rank = score(p);
      if (rank !== -1) scored.push({ place: p, rank });
    });

    // Rank first, then the chosen sort - so the place named what was typed
    // is never buried under nearer ones that merely mention it. Names break
    // ties as PlaceSearch's strcasecmp does (cmpName), and keep ICU's
    // first-use cost out of the first keystroke.
    const within = (sortMode === 'closest' && userLocation)
      ? (a, b) => haversineKm(userLocation.lat, userLocation.lng, a.place.lat, a.place.lng) -
                  haversineKm(userLocation.lat, userLocation.lng, b.place.lat, b.place.lng)
      : (a, b) => cmpName(a.place, b.place);

    scored.sort((a, b) => (a.rank - b.rank) || within(a, b));

    const total = scored.length;
    const matches = scored.slice(0, suggestionLimit).map(s => s.place);

    if (matches.length === 0) {
      const none = document.createElement('div');

      none.appendChild(el('div', 'suggestion-empty', 'No matching places'));
      dom.patch(searchSuggestions, none);
      setSuggestionsOpen(true);

      return;
    }

    // Built detached, then patched in (as renderList), so rows that still
    // match keep their nodes across keystrokes and "Show more" - no thumbnail
    // flicker, no scroll reset.
    const list = document.createElement('div');

    matches.forEach(p => {
      const item = document.createElement('div');
      // The patch key, so rows match by place rather than position.
      item.id = 'suggestion-' + placeSlug(p);
      item.className = 'suggestion-item';
      item.setAttribute('role', 'button');
      item.setAttribute('tabindex', '0');
      item.setAttribute('aria-label', 'View ' + p.name + ' on the map');
      item.title = p.name;

      const field = matchedField(p, query);
      const summary = p.summary || '';

      const text = el('div', 'suggestion-text');

      const nameEl = el('div', 'suggestion-name');
      nameEl.appendChild(field === 'name' ? highlightNodes(p.name, query) : document.createTextNode(p.name));
      text.appendChild(nameEl);

      const closed = closedNoticeNode(p, false);

      if (closed) text.appendChild(closed);

      // Lit chips first - the row clips to one line.
      const tags = placeTagsNode(p, { query: query, hitsFirst: true });

      if (tags) text.appendChild(tags);

      // Why this place matched, when no lit chip says so (e.g. "delivery").
      if (field === 'category' && !(tags && tags.querySelector('.is-lit'))) {
        text.appendChild(el('div', 'suggestion-why', categoryMatchLabel(p, query)));
      }

      const meta = el('div', 'suggestion-meta');
      const rating = el('span', 'suggestion-rating');

      rating.appendChild(ratingBadgeNode(p, false));
      meta.appendChild(rating);

      if (priceLabel(p)) {
        meta.appendChild(separatorNode());
        meta.appendChild(el('span', 'price-tag', priceLabel(p)));

        const prayer = prayerSpaceNode(p);
        const clothes = clothingNode(p);

        if (prayer) { meta.appendChild(document.createTextNode(' ')); meta.appendChild(prayer); }
        if (clothes) { meta.appendChild(document.createTextNode(' ')); meta.appendChild(clothes); }
      }

      if (userLocation) {
        const km = haversineKm(userLocation.lat, userLocation.lng, p.lat, p.lng);

        meta.appendChild(separatorNode());
        meta.appendChild(el('span', 'suggestion-distance', km.toFixed(1) + ' km'));
      }

      text.appendChild(meta);

      const addressEl = el('div', 'suggestion-meta');
      addressEl.appendChild(field === 'address' ? highlightNodes(p.address, query) : document.createTextNode(p.address));
      text.appendChild(addressEl);

      if (summary) {
        const detailsEl = el('div', 'suggestion-details');
        detailsEl.appendChild(field === 'details' ? highlightNodes(summary, query) : document.createTextNode(summary));
        text.appendChild(detailsEl);
      }

      item.appendChild(thumbWithRingNode(p, 'suggestion-thumb', 'suggestion-thumb-frame'));
      item.appendChild(text);

      // mousedown, not click: it fires before the input's blur closes the
      // dropdown.
      item.addEventListener('mousedown', e => {
        e.preventDefault();
        selectSuggestion(p);
      });
      item.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectSuggestion(p);
        }
      });
      list.appendChild(item);
    });

    if (total > matches.length) {
      const remaining = total - matches.length;
      const more = document.createElement('div');
      // Keyed, so the patch keeps this node (and its focus) as the list grows.
      more.id = 'suggestion-more';
      more.className = 'suggestion-more';
      more.setAttribute('role', 'button');
      more.setAttribute('tabindex', '0');
      more.textContent = 'Show ' + Math.min(SUGGESTION_MORE, remaining) + ' more'
        + ' (' + remaining + ' left)';

      const expand = (e, viaKeyboard) => {
        // As with the items, the input's blur would close the dropdown first.
        e.preventDefault();

        suggestionLimit += SUGGESTION_MORE;
        renderSuggestions();

        // Re-found: after the last page there is no button any more.
        // preventScroll, or focus() scrolls the document too.
        const button = dom.one('.suggestion-more', searchSuggestions);

        if (!button) return;

        button.focus({ preventScroll: true });

        // A click keeps the scroll position, so the new rows appear under
        // the reader's eye. Only keyboard users follow the button, to keep
        // the focus ring visible.
        if (! viaKeyboard) return;

        const rowBottom = button.offsetTop + button.offsetHeight;
        const visibleBottom = searchSuggestions.scrollTop + searchSuggestions.clientHeight;

        if (rowBottom > visibleBottom) {
          searchSuggestions.scrollTop = rowBottom - searchSuggestions.clientHeight;
        }
      };

      more.addEventListener('mousedown', e => expand(e, false));
      more.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') expand(e, true);
      });

      list.appendChild(more);
    }

    dom.patch(searchSuggestions, list);
    setSuggestionsOpen(true);
  }

  // Absent on articles pages.
  if (searchInput) {
    searchInput.addEventListener('input', () => {
      renderSuggestions();
      searchTyped();
      if (!searchInput.value) resumeSearchPlaceholderDemo();
      // Keep ?search= in step with the box (replace, not push, per keystroke).
      setUrlParam('search', searchInput.value || null, false);
    });

    searchInput.addEventListener('focus', () => {
      stopSearchPlaceholderDemo();
      if (searchInput.value.trim()) renderSuggestions();
    });

    // Combobox keys with focus kept in the input: Down/Up walk, Enter picks,
    // Escape dismisses. Items still handle their own Enter/Space when tabbed to.
    searchInput.addEventListener('keydown', (e) => {
      const items = dom.all('.suggestion-item', searchSuggestions);

      // Enter with no results is still a search made.
      if (!items.length) {
        if (e.key === 'Enter') announceSearch('enter');
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (e.key === 'ArrowDown') {
          highlightedSuggestionIndex = highlightedSuggestionIndex < items.length - 1 ? highlightedSuggestionIndex + 1 : 0;
        } else {
          highlightedSuggestionIndex = highlightedSuggestionIndex > 0 ? highlightedSuggestionIndex - 1 : items.length - 1;
        }
        items.forEach((item, i) => item.classList.toggle('highlighted', i === highlightedSuggestionIndex));
        items[highlightedSuggestionIndex].scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter') {
        if (highlightedSuggestionIndex >= 0 && highlightedSuggestionIndex < items.length) {
          e.preventDefault();
          items[highlightedSuggestionIndex].dispatchEvent(new MouseEvent('mousedown'));
        } else {
          announceSearch('enter');
        }
      } else if (e.key === 'Escape') {
        closeSuggestions();
      }
    });
  }

  // Click-away closes the dropdown - except in the app modal (e.g. the
  // location prompt), which can appear over a ?search= dropdown on load.
  document.addEventListener('click', e => {
    if (!e.target.closest('#search-wrap') && !e.target.closest('#app-modal-backdrop')) closeSuggestions();
  });

  const resetBar = dom.id('reset-bar');
  const sidebarResetBtn = dom.id('sidebar-reset-btn');
  let initialViewSet = false;
  let viewIncludesUser = false;

  // Phrases for the filter buttons' title/aria-label.
  const AMENITY_LABELS = {
    'dine-in': 'dine-in',
    takeout: 'takeout',
    delivery: 'delivery',
    'prayer-space': 'a prayer space',
    patio: 'a patio',
    clothing: 'Muslim clothing',
    'place-of-worship': 'a place of worship',
    restaurant: 'restaurant',
    'halal-tagged': "Google's own halal tag"
  };

  // CategoryCatalogue's label for each slug, for the tab title and meta
  // description. Every slug in AMENITY_FIELDS needs one; CategoryFilterTest
  // checks the lists agree.
  const AMENITY_TITLE_LABELS = {
    'dine-in': 'Dine-in',
    takeout: 'Takeout',
    delivery: 'Delivery',
    'prayer-space': 'Prayer Space',
    patio: 'Patio',
    clothing: 'Clothing',
    'place-of-worship': 'Places of Worship',
    grocery: 'Grocery',
    pharmacy: 'Pharmacy',
    reservable: 'Takes Reservations',
    restroom: 'Restroom',
    'live-music': 'Live Music',
    'good-for-children': 'Good for Children',
    'good-for-groups': 'Good for Groups',
    vegetarian: 'Vegetarian Options',
    breakfast: 'Breakfast',
    brunch: 'Brunch',
    lunch: 'Lunch',
    dinner: 'Dinner',
    dessert: 'Dessert',
    restaurant: 'Restaurant',
    'halal-tagged': 'Halal-Tagged by Google',
    'curbside-pickup': 'Curbside Pickup',
    coffee: 'Serves Coffee',
    'dog-friendly': 'Dog Friendly',
    'kids-menu': 'Kids Menu',
    'sports-viewing': 'Good for Watching Sports',
    'wheelchair-accessible': 'Wheelchair Accessible',
    'free-parking': 'Free Parking',
    'serves-beer': 'Serves Beer',
    'serves-wine': 'Serves Wine',
    'serves-cocktails': 'Serves Cocktails'
  };

  // A no-op for a tag missing from the DOM.
  function setMetaContent(el, value, attr) {
    if (el) el.setAttribute(attr || 'content', value);
  }

  // A category filter's words when the server sent none for it. The page
  // names only the site, so no city or subject goes in here (white-label).
  function categoryFallbackMeta(labelText, siteName) {
    return {
      title: labelText + ' | ' + siteName,
      description: 'Places on this map filtered to ' + labelText + ', on ' + siteName + '\'s interactive map with addresses, hours and directions.',
      ogDescription: 'Places on this map filtered to ' + labelText + ' on ' + siteName + '.',
    };
  }

  // A cuisine's words when the server sent none for it - the same idea.
  function cuisineFallbackMeta(cuisine, siteName) {
    return {
      title: cuisine + ' | ' + siteName,
      description: 'Places on this map tagged ' + cuisine + ', on ' + siteName + '\'s interactive map with addresses, hours and directions.',
      ogDescription: 'Places on this map tagged ' + cuisine + ' on ' + siteName + '.',
    };
  }

  // SeoTemplates::render(): fills {tokens} in order, leaves unknown ones
  // literal and collapses the gaps an empty self-separating token leaves -
  // PHP's way: its \s{2,} (no /u) and trim() are ASCII only.
  function fillSeoTemplate(template, tokens) {
    let out = template;
    Object.keys(tokens).forEach((key) => {
      out = out.split('{' + key + '}').join(tokens[key]);
    });
    return phpTrim(out.replace(/[\t\n\v\f\r ]{2,}/g, ' '));
  }

  // A place's share description: the server's Place template
  // (window.__hcPlaceShare, the site's tokens already in), finished with the
  // place's. Without it, a sentence naming only the site.
  function placeShareDescription(template, siteName, tokens) {
    return fillSeoTemplate(template || '{name}{detail}. Find it on {site}.{loadingMessage}', Object.assign({ site: siteName }, tokens));
  }

  /* ---- A place's title and meta description, as its page has them ----
     PageResolver::placeMeta() fills the One place templates (Site settings)
     with the place's tokens; a pin opened without a reload is worded from
     the same inputs, which the server hands over as window.__hcPlaceMeta
     (PageResolver::placeMetaForScript): the title and description templates
     with the site's tokens filled, the kinds of place behind {about} as
     [fields, cuisine tags, phrase], the fallback phrase and the prayer-space
     rule. tests/js/page-meta holds these helpers to the server's words for
     every live place. */

  // The server's inputs, or - from a page that sent none - a sentence that
  // names only the site and what any place's page has.
  function placeMetaConfig(sent, siteName) {
    if (sent && typeof sent.title === 'string' && typeof sent.description === 'string' && Array.isArray(sent.kinds)) {
      return sent;
    }

    return {
      title: '{name} | ' + siteName,
      description: '{name}: {about}{rating}.{summary}',
      kinds: [],
      fallback: 'reviews, hours and directions',
      prayer: null,
    };
  }

  // {about}: the phrase of the first kind the place belongs to - in it by a
  // field the map's category filter tests (custom categories included) or a
  // cuisine tag it carries - else the fallback. Twin of PlaceKinds::phrase().
  function placeKindPhrase(p, kinds, fallback) {
    const carried = Array.isArray(p.cuisines) ? p.cuisines : [];

    for (const [fields, cuisines, phrase] of kinds) {
      if (fields.some(field => p[field]) || cuisines.some(tag => carried.indexOf(tag) !== -1)) return phrase;
    }

    return fallback;
  }

  // {loadingMessage}: the sentence for a prayer space that is something else
  // as well (rule.fields, rule.cuisines), never for a place of worship or a
  // prayer space alone, nor when the summary already names it (rule.phrase,
  // any case). Twin of SeoTemplates::prayerSpaceSentence().
  function prayerSpaceSentence(p, rule, summary) {
    if (!rule || !p.prayerSpace || p.placeOfWorship) return '';
    if (rule.phrase && String(summary || '').toLowerCase().indexOf(String(rule.phrase).toLowerCase()) !== -1) return '';

    const carried = Array.isArray(p.cuisines) ? p.cuisines : [];
    const alsoElse = rule.fields.some(field => p[field]) || rule.cuisines.some(tag => carried.indexOf(tag) !== -1);

    return alsoElse ? rule.sentence : '';
  }

  // {rating}: the star rating alone (a review count goes stale), only with
  // reviews behind it; one decimal, less a trailing ".0", as number_format().
  function googleRating(p) {
    const rating = typeof p.rating === 'number' ? p.rating : parseFloat(p.rating);

    if (!Number.isFinite(rating) || !(Math.trunc(Number(p.ratingCount) || 0) > 0)) return '';

    return ', rated ' + (Math.round(rating * 10) / 10) + ' on Google';
  }

  /* The place's tokens, in PageResolver::placeMeta()'s order (a phrase may
     use the tokens after {about}). The summary needs the full description,
     which arrives with the sheet's fields (loadPlaceDetail re-runs
     updatePageMeta); until then the boot payload's opening stands in. */
  function placeMetaTokens(p, meta) {
    const loaded = p.details !== undefined;
    const described = loaded ? !!p.details : !!p.summary;
    const summary = !described ? '' : (loaded ? detailsSummary(p.details) : summaryCut(String(p.summary), 155));
    const detail = described ? summary : String(p.address || '');

    return {
      about: placeKindPhrase(p, meta.kinds, meta.fallback),
      name: String(p.name),
      detail: detail !== '' ? ' - ' + detail : '',
      address: String(p.address || ''),
      summary: described ? ' ' + summary : '',
      rating: googleRating(p),
      loadingMessage: prayerSpaceSentence(p, meta.prayer, summary),
    };
  }

  // Title, meta description and share description for one place. Its own
  // SEO title, when set, beats the template.
  function placeMetaWords(p, meta, shareTemplate, siteName) {
    const tokens = placeMetaTokens(p, meta);

    return {
      title: phpTrim(String(p.seoTitle || '')) || fillSeoTemplate(meta.title, tokens),
      description: fillSeoTemplate(meta.description, tokens),
      ogDescription: placeShareDescription(shareTemplate, siteName, tokens),
    };
  }

  // Keeps the tab title and meta tags in step with what is showing: a place
  // beats a filter, which beats the site defaults. Mirrors what the server
  // renders for those URLs, so a pin opened without a reload still shares
  // correctly.
  function updatePageMeta() {
    let title, description, ogDescription, url, image, imageType, imageAlt;
    const p = activeSlug ? findPlaceBySlug(activeSlug) : null;
    if (p) {
      const activeName = p.name;
      // seoTitle and the full description arrive with the detail
      // (loadPlaceDetail re-runs this).
      ({ title, description, ogDescription } = placeMetaWords(p, PLACE_META, window.__hcPlaceShare, SITE_NAME));
      url = CANONICAL_BASE.replace(/\/$/, '') + PATHS.place(activeSlug);
      // og-image.php brands the place's photo, always as a JPEG.
      image = p.image ? CANONICAL_BASE + 'og-image.php?p=' + activeSlug : SITE_IMAGE;
      imageType = p.image ? 'image/jpeg' : 'image/gif';
      imageAlt = p.image ? activeName + ' | ' + SITE_NAME : SITE_IMAGE_ALT;
    } else {
      const activeKeys = Object.keys(activeAmenityCategories).filter(key => activeAmenityCategories[key]);
      if (activeCuisine) {
        // A cuisine page's own meta and canonical - without them a JS-rendering
        // crawler would see /cuisine/x/ claim to be /. The words are the
        // server's (PageResolver::cuisineMetaAll, window.__hcCuisineMeta);
        // the sentence is only a fallback.
        const own = (window.__hcCuisineMeta || {})[activeCuisine]
          || cuisineFallbackMeta(cuisineLabel(activeCuisine), SITE_NAME);
        title = own.title;
        description = own.description;
        ogDescription = own.ogDescription;
        url = CANONICAL_BASE.replace(/\/$/, '') + PATHS.cuisine(activeCuisine);
        image = SITE_IMAGE;
        imageType = 'image/gif';
        imageAlt = SITE_IMAGE_ALT;
      } else if (activeKeys.length) {
        const labelText = activeKeys.map(key => AMENITY_TITLE_LABELS[key]).join(', ');
        // The server's words (PageResolver::categoryMeta,
        // window.__hcCategoryMeta); the sentence is only a fallback.
        const own = (window.__hcCategoryMeta || {})[activeKeys[0]] || categoryFallbackMeta(labelText, SITE_NAME);
        title = own.title;
        description = own.description;
        ogDescription = own.ogDescription;
        url = CANONICAL_BASE.replace(/\/$/, '') + PATHS.category(activeKeys[0]);
        image = SITE_IMAGE;
        imageType = 'image/gif';
        imageAlt = SITE_IMAGE_ALT;
      } else {
        title = SITE_TITLE;
        description = SITE_DESCRIPTION;
        ogDescription = SITE_OG_DESCRIPTION;
        url = CANONICAL_BASE;
        image = SITE_IMAGE;
        imageType = SITE_IMAGE_TYPE;
        imageAlt = SITE_IMAGE_ALT;
      }
    }
    // The <title>'s text, like any other text (not document.title =).
    if (metaEls.title) dom.text(metaEls.title, title);
    setMetaContent(metaEls.description, description);
    setMetaContent(metaEls.canonical, url, 'href');
    setMetaContent(metaEls.ogTitle, title);
    setMetaContent(metaEls.ogDescription, ogDescription);
    setMetaContent(metaEls.ogUrl, url);
    setMetaContent(metaEls.ogImage, image);
    setMetaContent(metaEls.ogImageType, imageType);
    setMetaContent(metaEls.ogImageAlt, imageAlt);
    setMetaContent(metaEls.twitterTitle, title);
    setMetaContent(metaEls.twitterDescription, ogDescription);
    setMetaContent(metaEls.twitterImage, image);
    setMetaContent(metaEls.twitterImageAlt, imageAlt);
  }

  // ==========================================================================
  // THE FILTERED-MAP BANNER
  // ==========================================================================

  // Says the map shows a filtered subset. Server-rendered with the same
  // markup (partials/category-banner.blade.php) and adopted, so it does not
  // flash in.
  const categoryBanner = dom.id('category-banner');
  const categoryBannerIcon = dom.id('category-banner-icon');
  const categoryBannerText = dom.id('category-banner-text');

  function renderCategoryBanner() {
    if (!categoryBanner) return;

    const active = Object.keys(activeAmenityCategories).find(key => activeAmenityCategories[key]) || null;

    // A cuisine filter shows the same banner.
    if (!active && !activeCuisine) {
      categoryBanner.classList.remove('visible');
      categoryBanner.hidden = true;

      return;
    }

    // The glyph is copied from the filter's own button (or cuisine chip),
    // which already has whatever icon the admin chose.
    const btnIcon = active
      ? dom.id('sidebar-' + active + '-category-btn')
      : dom.one('.cuisine-tag[data-cuisine="' + activeCuisine + '"] .cuisine-tag-icon');
    const glyph = btnIcon ? btnIcon.querySelector('svg, img') : null;

    if (glyph && categoryBannerIcon) {
      const copy = glyph.cloneNode(true);
      // 16px, a touch smaller than the button's, to suit the banner's text.
      if (copy.tagName.toLowerCase() === 'svg') {
        copy.setAttribute('width', '16');
        copy.setAttribute('height', '16');
      }
      // Patched: the glyph the server drew for this filter is adopted, and a
      // switch between filters changes its paths rather than rebuilding it.
      const icon = document.createElement('div');

      icon.appendChild(copy);
      dom.patch(categoryBannerIcon, icon);
    }

    if (categoryBannerText) {
      // No count, by design - see partials/category-banner.blade.php.
      const parts = [document.createTextNode('Showing places filtered to ')];

      // One filter at a time: a cuisine or a category, never both.
      if (activeCuisine) {
        parts.push(el('strong', '', cuisineLabel(activeCuisine)));
      } else if (active) {
        parts.push(el('strong', '', AMENITY_TITLE_LABELS[active] || AMENITY_LABELS[active] || active));
      }

      const text = document.createElement('div');

      parts.forEach((part) => text.appendChild(part));
      dom.patch(categoryBannerText, text);
    }

    categoryBanner.hidden = false;
    // Next frame, so un-hiding gives the transition a start state.
    requestAnimationFrame(() => categoryBanner.classList.add('visible'));
  }

  // "Show all" is a real link (works without JS); with JS it clears the
  // filter in place. Modified clicks fall through to the browser.
  if (categoryBanner) {
    const clear = dom.id('category-banner-clear');

    if (clear) {
      clear.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        clearAmenityCategories();
      });
    }
  }

  // Every filter off - map, buttons, URL and banner back to "everything".
  function clearAmenityCategories() {
    Object.keys(activeAmenityCategories).forEach(key => { activeAmenityCategories[key] = false; });
    amenityCategoryBtns.forEach(btn => updateAmenityCategoryBtnUI(btn, btn.dataset.amenity));
    clearCuisineRow();
    updateCategoriesUrlParam();
    updatePageMeta();
    resetListWindow();
    renderList();
    updateMarkerVisibility();
  }

  /* CUISINE ROW
     Each tag is a real <a href> (works without JS, crawlable); a click
     filters in place instead. Single-select - two cuisines at once would
     match almost nothing. Clicking the active tag clears it. */
  (() => {
    const row = dom.id('cuisine-row');
    if (!row) return;

    const scroller = dom.id('cuisine-scroll');
    // Cuisine tags only: category filters share the row and the .cuisine-tag
    // class but carry data-amenity and have their own handler.
    const tags = dom.all('.cuisine-tag[data-cuisine]', row);

    function paint() {
      tags.forEach(tag => {
        const on = tag.dataset.cuisine === activeCuisine;
        tag.classList.toggle('is-active', on);
        tag.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    function choose(slug) {
      const turningOn = activeCuisine !== slug;

      // One filter at a time, so clear the categories - BEFORE setting
      // activeCuisine, since resetAmenityCategories() also clears the cuisine.
      if (turningOn) resetAmenityCategories();

      activeCuisine = turningOn ? slug : null;
      paint();
      updateCategoriesUrlParam();
      resetListWindow();
      renderList();
      updateMarkerVisibility();
      updatePageMeta();
    }

    row.addEventListener('click', (event) => {
      const tag = event.target.closest ? event.target.closest('.cuisine-tag[data-cuisine]') : null;
      if (!tag) return;

      // Modified clicks fall through to the browser (new tab).
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      event.preventDefault();
      choose(tag.dataset.cuisine);
      announceCategoryClick('cuisine', tag.dataset.cuisine, activeCuisine === tag.dataset.cuisine);
    });

    setCuisineRow = (slug) => {
      const wanted = slug && tags.some(t => t.dataset.cuisine === slug) ? slug : null;

      if (activeCuisine === wanted) return;

      activeCuisine = wanted;
      paint();
    };

    clearCuisineRow = () => {
      if (activeCuisine === null) return;
      activeCuisine = null;
      paint();
      // No re-render here - every caller does that itself.
    };

    // A /cuisine/x/ link opens already filtered.
    const fromUrl = parseLocationPath().cuisine || '';

    if (fromUrl && tags.some(t => t.dataset.cuisine === fromUrl)) {
      activeCuisine = fromUrl;
      paint();
    }

    attachScrollArrows(scroller, dom.id('cuisine-prev'), dom.id('cuisine-next'));
  })();

  /* A tag chip on the sheet (a real link to its listing) closes the sheet
     and presses the row's own tag, so the row's rules run exactly once. A
     tag the row does not offer falls through to the link. */
  document.addEventListener('click', (event) => {
    const chip = event.target.closest ? event.target.closest('.popup-tags .place-tag[data-tag]') : null;

    if (!chip) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    const rowTag = chip.dataset.tagKind === 'cuisine'
      ? dom.one('#cuisine-row .cuisine-tag[data-cuisine="' + chip.dataset.tag + '"]')
      : dom.idLive('sidebar-' + chip.dataset.tag + '-category-btn');

    if (!rowTag) return;

    event.preventDefault();
    closeActivePlace();
    rowTag.click();
    if (rowTag.scrollIntoView) rowTag.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  });

  /* ---- Full review popover ----
     Review text is CSS-clamped; a click on a clamped card shows the whole
     review. Delegated, because cards come from the server and from
     reviewNode(). Clamping is measured at click time (scrollHeight vs
     clientHeight); unclamped cards do not open. */
  (() => {
    let box = null;
    let openFor = null;

    function element() {
      if (box) return box;

      box = el('div', 'review-popover');
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-label', 'Full review');
      box.hidden = true;
      dom.insert(document.body, box);

      return box;
    }

    function close() {
      if (openFor) {
        openFor.setAttribute('aria-expanded', 'false');
        openFor = null;
      }

      if (!box) return;

      box.classList.remove('is-open');
      box.hidden = true;
    }

    function isClamped(textEl) {
      return !!textEl && textEl.scrollHeight - textEl.clientHeight > 2;
    }

    function open(card) {
      const textEl = dom.one('.popup-review-text', card);
      const tip = element();

      close();

      // Cloned, not moved, so a re-render underneath cannot lose a review.
      const body = el('div', 'review-popover-text');

      body.appendChild(textEl.cloneNode(true));

      const meta = dom.one('.popup-review-meta', card);

      // Built detached, then patched in: the next review reads into the nodes
      // the last one left.
      const next = document.createElement('div');

      next.appendChild(body);

      if (meta) {
        const foot = meta.cloneNode(true);

        // No duplicate ids from the clone.
        foot.querySelectorAll('[id]').forEach(n => n.removeAttribute('id'));
        foot.classList.add('review-popover-meta');
        next.appendChild(foot);
      }

      dom.patch(tip, next);

      tip.hidden = false;
      tip.classList.remove('is-above');
      tip.style.left = '0px';
      tip.style.top = '0px';

      const target = card.getBoundingClientRect();
      const self = tip.getBoundingClientRect();
      const EDGE = 10;
      const GAP = 8;
      // Below the card, or above when there is no room (as dom.js tooltips).
      const below = target.bottom + GAP + self.height <= window.innerHeight - EDGE;
      const top = below ? target.bottom + GAP : target.top - GAP - self.height;
      let left = target.left + (target.width / 2) - (self.width / 2);

      left = Math.max(EDGE, Math.min(left, window.innerWidth - self.width - EDGE));

      if (!below) tip.classList.add('is-above');

      tip.style.left = Math.round(left) + 'px';
      tip.style.top = Math.round(Math.max(EDGE, top)) + 'px';

      // Force a reflow so the fade starts from the new position.
      void tip.offsetHeight;
      tip.classList.add('is-open');

      openFor = card;
      card.setAttribute('aria-expanded', 'true');
    }

    // Capture phase, to catch the strip's own sideways scroll.
    document.addEventListener('scroll', close, { passive: true, capture: true });
    window.addEventListener('resize', close);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && openFor) {
        const card = openFor;

        close();
        card.focus();
      }
    });

    document.addEventListener('click', (e) => {
      if (box && box.contains(e.target)) return;

      const card = e.target.closest ? e.target.closest('.popup-review') : null;

      if (!card) {
        close();

        return;
      }

      // Links inside the card do their own thing.
      if (e.target.closest('a')) return;

      if (card === openFor) {
        close();

        return;
      }

      if (!isClamped(dom.one('.popup-review-text', card))) {
        close();

        return;
      }

      open(card);
    });

    // Keyboard: only clamped cards become buttons, marked on hover/focus so
    // neither renderer needs to know.
    const markIfClamped = (e) => {
      const card = e.target.closest ? e.target.closest('.popup-review') : null;

      if (!card || card.hasAttribute('role')) return;
      if (!isClamped(dom.one('.popup-review-text', card))) return;

      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.setAttribute('aria-expanded', 'false');
      card.classList.add('is-expandable');
    };

    document.addEventListener('pointerover', markIfClamped, true);
    document.addEventListener('focusin', markIfClamped, true);
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;

      const card = e.target.closest ? e.target.closest('.popup-review[role="button"]') : null;

      if (!card) return;

      e.preventDefault();
      card === openFor ? close() : open(card);
    });
  })();

  /* Arrows for a server-rendered review strip, which may never be rebuilt
     (fillReviewsSlot wires its own). */
  (() => {
    const strip = dom.one('.popup-reviews-scroll');

    if (!strip) return;

    attachScrollArrows(strip, dom.one('.popup-reviews-prev'), dom.one('.popup-reviews-next'));
  })();

  // One filter button's state and labels. is-active is the cuisine row's
  // class - the category tags live in that row.
  function updateAmenityCategoryBtnUI(btn, amenity) {
    const isActive = activeAmenityCategories[amenity];
    const label = AMENITY_LABELS[amenity];
    btn.classList.toggle('is-active', isActive);
    btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    const text = isActive
      ? 'Showing only places with ' + label + ' - click to show all places'
      : 'Filter to places with ' + label;
    btn.setAttribute('aria-label', text);
    btn.title = text;
  }

  const amenityCategoryBtns = dom.all('.amenity-category-btn');

  // Unused: the ?categories= query param is retired.
  const AMENITY_CATEGORY_PARAM = 'categories';

  /* Writes the active filter's path. Pushes: a filter is a navigation, and
     back must undo it (writePath drops duplicate pushes). basePath() so an
     active cuisine is not dropped. */
  function updateCategoriesUrlParam() {
    writePath(basePath(), true);
  }

  // Applies a /{category}/ path on load, so a shared link opens filtered.
  // ?categories= is ignored on both sides.
  function applyCategoriesFromUrl() {
    let matched = null;
    const fromPath = parseLocationPath();
    if (fromPath.view === 'category' && fromPath.slug in activeAmenityCategories) {
      matched = fromPath.slug;
    }
    if (!matched) return;
    activeAmenityCategories[matched] = true;
    amenityCategoryBtns.forEach(btn => updateAmenityCategoryBtnUI(btn, btn.dataset.amenity));
    updatePageMeta();
  }

  amenityCategoryBtns.forEach(btn => {
    const amenity = btn.dataset.amenity;
    btn.addEventListener('click', (e) => {
      // Real links (crawlable, no-JS); with JS the filter toggles in place.
      e.preventDefault();
      // Filtering shows the whole set, so no place stays open or in the URL.
      closeActivePlace();
      // One filter at a time: drop any cuisine.
      clearCuisineRow();
      // Single-select: the active button clears; any other replaces it.
      // Never combine categories - combinations gave crawlers endless URLs.
      const turningOn = !activeAmenityCategories[amenity];
      Object.keys(activeAmenityCategories).forEach(key => { activeAmenityCategories[key] = false; });
      activeAmenityCategories[amenity] = turningOn;
      amenityCategoryBtns.forEach(b => updateAmenityCategoryBtnUI(b, b.dataset.amenity));
      updateCategoriesUrlParam();
      updatePageMeta();
      resetListWindow();
      renderList();
      updateMarkerVisibility();
      announceCategoryClick('category', amenity, turningOn);
    });
  });

  // A row tag pressed (directly or via a sheet chip) - never a filter that
  // arrived in the URL. `active`: whether the press turned it on.
  function announceCategoryClick(kind, slug, active) {
    const known = (kind === 'cuisine' ? TAG_CATALOGUE.cuisines : TAG_CATALOGUE.categories)[slug];

    window.hcEvents.emit('category-clicked', {
      kind: kind,
      slug: slug,
      label: known ? known.label : slug,
      active: !!active,
    });
  }

  /* On iOS the URL bar showing/hiding does not reliably fire window resize
     (which Leaflet tracks), only visualViewport resize - without this,
     grey bands and mis-hit pins. Debounced, as the bar animates. See also
     unstickShell() in main.js. */
  if (window.visualViewport) {
    let resizeSettle = null;

    window.visualViewport.addEventListener('resize', () => {
      clearTimeout(resizeSettle);
      resizeSettle = setTimeout(() => map.invalidateSize(), 120);
    });
  }

  // Deselects the open place (sheet, pin, card) and takes it out of the URL,
  // leaving pan/zoom alone.
  function closeActivePlace() {
    closePlaceModal();
    setActivePin(null);
    setActiveCard(null);
    if (parseLocationPath().view === 'place' || location.hash) {
      // Pushes; a filter write later in the same click coalesces into this
      // entry (see writePath).
      writePath(basePath(), true);
    }
  }

  // Every filter off, with buttons, URL and pins in step. Used by
  // resetToInitialView() and choosing a cuisine.
  function resetAmenityCategories() {
    let anyWasActive = activeCuisine !== null;
    clearCuisineRow();
    Object.keys(activeAmenityCategories).forEach(key => {
      if (activeAmenityCategories[key]) anyWasActive = true;
      activeAmenityCategories[key] = false;
    });
    amenityCategoryBtns.forEach(btn => updateAmenityCategoryBtnUI(btn, btn.dataset.amenity));
    if (anyWasActive) {
      updateCategoriesUrlParam();
      updatePageMeta();
      updateMarkerVisibility();
    }
    return anyWasActive;
  }

  // ==========================================================================
  // MAP VIEWPORT / RESET CONTROL
  // ==========================================================================

  // With a location, home centres on the user at this zoom - not a box
  // fitted to every pin, whose centre can leave the user off screen.
  const USER_CENTERED_ZOOM = 12;

  // The home/reset view, recomputed on every call (never a stale snapshot).
  // Without a location it is Site settings' default view, not a fitted
  // bounding box (outlying towns would drag that off-centre).
  function computeHomeView() {
    if (userLocation) {
      return {
        center: L.latLng(userLocation.lat, userLocation.lng),
        zoom: Math.min(USER_CENTERED_ZOOM, map.getMaxZoom())
      };
    }
    return {
      center: L.latLng(startLat, startLng),
      zoom: Math.min(startZoom, map.getMaxZoom())
    };
  }

  // Applies the home view, re-measuring the container first - a stale size
  // cache would misplace the centre.
  function applyFitView(animate) {
    const view = computeHomeView();
    if (!view) return;
    map.invalidateSize();
    map.setView(view.center, view.zoom, { animate: !!animate });
  }

  function setInitialView() {
    if (initialViewSet || !dataReady) return;
    initialViewSet = true;
    applyFitView(false);
  }

  // "Reset view" shows only while the map is away from home.
  function checkResetBarVisibility() {
    const view = computeHomeView();
    if (!view) return;
    const center = map.getCenter();
    const zoom = map.getZoom();
    const centerMoved = center.distanceTo(view.center) > 50; // metres
    const zoomChanged = zoom !== view.zoom;
    resetBar.classList.toggle('visible', centerMoved || zoomChanged);
  }

  // The reset button and the logo: a full return to the as-loaded state -
  // home view, no place, no filters, clean URL. map.stop() first, as a
  // leftover animation can make it seem stuck.
  function resetToInitialView() {
    const view = computeHomeView();
    if (!view) return;
    map.stop();
    closePlaceModal();
    setActivePin(null);
    if (location.hash || location.search || parseLocationPath().view !== 'places') {
      history.replaceState(null, '', PATHS.home());
    }
    if (searchInput.value) {
      searchInput.value = '';
      resumeSearchPlaceholderDemo();
      closeSuggestions();
    }
    resetAmenityCategories();
    setActiveCard(null);
    map.invalidateSize();
    map.flyTo(view.center, view.zoom);
  }

  resetBar.addEventListener('click', resetToInitialView);
  // A <div role="button">, so Enter/Space are wired by hand.
  resetBar.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      resetToInitialView();
    }
  });
  // Guarded: current templates no longer render this button.
  if (sidebarResetBtn) sidebarResetBtn.addEventListener('click', resetToInitialView);
  // #logo is a real <a href="/"> (the no-JS fallback); with JS it resets
  // in place.
  const logoEl = dom.id('logo');
  logoEl.addEventListener('click', (e) => {
    e.preventDefault();
    resetToInitialView();
  });
  // Enter activates the link natively; Space needs wiring.
  logoEl.addEventListener('keydown', (e) => {
    if (e.key === ' ') {
      e.preventDefault();
      resetToInitialView();
    }
  });

  // moveend follows every zoom (see listFollowsMove).
  map.on('moveend', checkResetBarVisibility);

  const featuredTrack = dom.id('featured-track');
  const featuredRow = dom.id('featured-row');
  const featuredMarquee = dom.id('featured-marquee');

  // ==========================================================================
  // FEATURED CAROUSEL
  // ==========================================================================

  // The featured places in this visit's order - see pickFeaturedSelection().
  let featuredSelection = null;

  // Fisher-Yates.
  function shuffledCopy(arr) {
    const copy = arr.slice();
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }

  // Every featured place, uncapped, shuffled once per visit so each gets a
  // turn in the most-seen leftmost slot. Idempotent: called before
  // buildMarkers() and again by buildFeatured() without re-rolling.
  function pickFeaturedSelection() {
    if (!featuredSelection) {
      featuredSelection = shuffledCopy(places.filter(p => p.featured));
    }
    return featuredSelection;
  }

  function buildFeatured() {
    // The strip is a Site setting and may be absent.
    if (!featuredTrack) return;

    // As renderList: until the data lands, the server's strip stands.
    if (!dataReady) return;

    const featuredPlaces = pickFeaturedSelection();

    // Built detached, then patched in (see renderList), so a filter change
    // does not yank the moving strip back to the start.
    const track = document.createElement('div');

    featuredPlaces.forEach(p => {
      // An <a> with delegated clicks, as in renderList.
      const card = document.createElement('a');
      // Prefixed: the same place also has a place-card-<slug> in the list.
      card.id = 'featured-card-' + placeSlug(p);
      card.dataset.slug = placeSlug(p);
      card.href = '/places/' + placeSlug(p) + '/';
      card.className = 'featured-card';
      card.setAttribute('aria-label', 'View ' + p.name + ' on the map');
      card.title = p.name;
      const frame = el('div', 'featured-thumb-frame');

      frame.appendChild(thumbNode(p, 'featured-thumb', null, 220));
      frame.appendChild(fromTrustedMarkup(featuredStarBadgeSvg));
      frame.appendChild(el('span', 'sr-only', 'Featured'));
      card.appendChild(frame);

      card.appendChild(el('div', 'featured-name', p.name));

      // A one-line row that ellipsises, so the order is a priority order:
      // rating, distance, then Facebook and price, which can afford to be cut.
      const meta = el('div', 'featured-meta');

      meta.appendChild(ratingBadgeNode(p, true, true));

      if (userLocation) {
        const km = haversineKm(userLocation.lat, userLocation.lng, p.lat, p.lng);

        meta.appendChild(separatorNode());
        meta.appendChild(el('span', 'featured-distance', km.toFixed(1) + ' km'));
      }

      const facebook = facebookReviewNode(p);

      if (facebook) {
        meta.appendChild(separatorNode());
        meta.appendChild(facebook);
      }

      if (priceLabel(p)) {
        meta.appendChild(separatorNode());
        meta.appendChild(el('span', 'price-tag', priceLabel(p)));

        const prayer = prayerSpaceNode(p);

        if (prayer) { meta.appendChild(document.createTextNode(' ')); meta.appendChild(prayer); }
      }

      card.appendChild(meta);

      if (p.summary) {
        card.appendChild(el('div', 'featured-details', p.summary));
      }
      track.appendChild(card);
    });

    dom.patch(featuredTrack, track);
    // After the patch: it clones what the patch left.
    syncFeaturedMarquee();
  }

  /* The marquee animates in CSS (#featured-marquee in map.css); this
     builds the seamless loop's second copy and sets the card count, which
     sets the speed. The clone is aria-hidden with ids and tab stops
     stripped - duplicate ids would break dom.patch and getElementById. */
  function syncFeaturedMarquee() {
    if (!featuredMarquee || !featuredTrack) return;

    dom.remove(featuredMarquee.querySelector('.featured-seq--clone'));

    const cards = featuredTrack.children.length;

    // One card per 4s of travel; see the animation shorthand in map.css.
    featuredMarquee.style.setProperty('--featured-count', String(cards || 1));

    // Cards that fit are centred and held still.
    const fits = featuredTrack.scrollWidth <= (featuredRow?.clientWidth ?? 0);

    featuredRow?.classList.toggle('is-static', fits || cards === 0);

    if (fits || cards === 0) return;

    const clone = featuredTrack.cloneNode(true);

    clone.removeAttribute('id');
    clone.classList.add('featured-seq--clone');
    clone.setAttribute('aria-hidden', 'true');

    // An explicit sweep rather than `inert`, for older browsers.
    clone.querySelectorAll('[id]').forEach(n => n.removeAttribute('id'));
    clone.querySelectorAll('[tabindex]').forEach(n => n.setAttribute('tabindex', '-1'));

    dom.insert(featuredMarquee, clone);
  }

  // Width decides whether the strip moves at all.
  window.addEventListener('resize', syncFeaturedMarquee);

  // ==========================================================================
  // DATA LOADING & BOOTSTRAP
  // ==========================================================================

  /* THE PAGE TOKEN the data endpoints require, minted by this page's render.
     Sent as a HEADER, not a query param: a custom header is what another
     origin cannot send without a CORS preflight we refuse (and a ?token=
     would leak into logs and Referers).

     A token lasts 12-24 hours (PageToken::WINDOW), less than a tab can stay
     open, so it is a `let` and a 403 means "stale document": a fresh token
     is read from a re-fetch of this same page, without a reload. */
  let pageToken = dom.one('meta[name="hc-token"]')?.content || '';
  let tokenRefresh = null;

  function tokenHeaders() {
    return pageToken ? { 'X-HC-Token': pageToken } : {};
  }

  function refreshPageToken() {
    // One flight at a time, so concurrent 403s do not race.
    if (tokenRefresh) return tokenRefresh;

    tokenRefresh = fetch(location.pathname + location.search, {
      cache: 'no-store',
      headers: { 'X-HC-Token-Refresh': '1' },
    })
      .then(res => (res.ok ? res.text() : Promise.reject(new Error('http ' + res.status))))
      .then(html => {
        // Parsed (inert) and only the meta tag read.
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('meta[name="hc-token"]')?.content || '';

        if (!fresh) throw new Error('no token in page');
        pageToken = fresh;

        return fresh;
      })
      .finally(() => { tokenRefresh = null; });

    return tokenRefresh;
  }

  /**
   * A tokened GET that survives its token expiring. A 403 here only ever
   * means RequirePageToken refused the header (before the handler ran), so
   * one retry with a fresh token is safe. Anything else passes through.
   */
  function fetchWithToken(url) {
    const go = () => fetch(url, { cache: 'no-store', headers: tokenHeaders() });

    return go().then(res => {
      if (res.status !== 403) return res;

      return refreshPageToken().then(go, () => res);
    });
  }

  // Loads the boot payload, retrying twice (500ms, 1500ms).
  function loadPlacesData(attempt) {
    // boot.js starts this same request from <head> (~700ms earlier). Taken
    // once and cleared, so retries are real retries; a stale-token 403 falls
    // through to fetchWithToken on the next attempt.
    var preflight = attempt === 0 ? window.__hcPlaces : null;
    window.__hcPlaces = null;

    return (preflight || fetchWithToken('/api/places.php'))
      .then(res => {
        if (!res.ok) throw new Error('Failed to load places data');
        return res.json();
      })
      .catch(err => {
        if (attempt >= 2) throw err;
        const delayMs = attempt === 0 ? 500 : 1500;
        return new Promise(resolve => setTimeout(resolve, delayMs)).then(() => loadPlacesData(attempt + 1));
      });
  }

  // Not fetched on articles pages, where nothing that uses it is shown.
  if (IS_ARTICLES_VIEW) {
    maybeHideLoadingOverlay();
  } else {
  bootPlaces();
  }

  /* A boot that runs out of retries arms itself rather than giving up: a
     phone fronting a discarded tab often reloads before the radio is back.
     It retries when the page is looked at again or the browser comes
     online - see armBootRetry(). */
  let bootRetryArmed = false;

  /* THE BOOT IN THREE TASKS, NOT ONE: pins, list, view. Run together they
     held the main thread for ~250ms on a mid-range phone, the page's longest
     task; between them the browser can paint and answer a tap. Nothing in
     the gaps can go wrong: the pins exist before the first one, a location
     fix there only aims for the view setInitialView() lands on anyway, and
     a place opened by URL is still opened last. */
  const yieldToMain = () => (window.scheduler && typeof window.scheduler.yield === 'function'
    ? window.scheduler.yield()
    : new Promise(resolve => setTimeout(resolve, 0)));

  function bootPlaces() {
    return loadPlacesData(0)
    .then(async data => {
      // A second boot after a successful one would build every marker twice.
      if (places.length) return null;
      places = data;
      // Before anything keys a place by its slug - see fillPlaceSlugs().
      fillPlaceSlugs(places);
      // Before the renders, which refuse to run until it is set.
      dataReady = true;

      // Start the splash's wind-down now, so it overlaps the renders below.
      finishLoadingBorder();

      applyCategoriesFromUrl();
      pickFeaturedSelection();
      // Before the markers are built, since it decides where they go.
      spreadCoincidentPins();
      // Together, in this order: buildMarkers() only creates the markers;
      // updateMarkerVisibility() is what draws the pins.
      buildMarkers();
      updateMarkerVisibility();
      buildFeatured();

      await yieldToMain();
      renderList();

      await yieldToMain();
      setInitialView();
      // Never open a place the active filter excludes (the server cannot see
      // a legacy #slug fragment to redirect it). Checked here because a
      // filtered-out place still has an entry in `markers`.
      const requestedSlug = getRequestedSlug();
      const requestedPlace = requestedSlug ? findPlaceBySlug(requestedSlug) : null;
      const requestedIsFilteredOut = !!requestedPlace && !passesActiveCategories(requestedPlace);
      if (requestedIsFilteredOut) closeActivePlace();
      const openedFromHash = requestedIsFilteredOut ? false : openPlaceFromSlug(requestedSlug);
      if (openedFromHash) {
        // Stop a later location fix from re-fitting the view away from it.
        viewIncludesUser = true;
      }
      applySearchFromUrl();
      maybeHideLoadingOverlay();
    })
    .catch(err => {
      console.error(err);
      /* The data never came, but the server's cards did, and they stay:
         every place is still a link in the list, for the reader and for
         crawlers. The error and its Try again link go above them; a later
         failure (see armBootRetry) replaces the earlier message. */
      // err.message is not ours, so it goes in as text.
      const empty = el('div', null, 'Could not load place data (' + err.message + '). ');
      const retryLink = el('a', null, 'Try again');
      const earlier = dom.one('#empty-state', listEl);

      empty.id = 'empty-state';
      retryLink.href = '#';
      retryLink.id = 'retry-load-places';
      retryLink.addEventListener('click', (e) => {
        e.preventDefault();
        location.reload();
      });

      empty.appendChild(retryLink);
      // Inserted, not patched in: the server's cards stay under it.
      dom.remove(earlier);
      dom.insert(listEl, empty, listEl.firstChild);
      dataReady = true;
      finishLoadingBorder();
      setInitialView();
      maybeHideLoadingOverlay();
      armBootRetry();
    });
  }

  /**
   * Retries the boot when the tab is looked at again or the browser comes
   * online. Wired once, after a failure. Tests `places.length`, not
   * dataReady - the failure path sets that too.
   */
  function armBootRetry() {
    if (bootRetryArmed) return;
    bootRetryArmed = true;

    const again = () => {
      if (places.length) return;

      bootPlaces();
    };

    // Only becoming visible counts; the network signals retry even while
    // hidden, so the pins are drawn by the time anyone looks.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') again();
    });
    window.addEventListener('online', again);
    window.addEventListener('pageshow', again);
  }

  /* leaflet.min.css loads async, so re-measure once every stylesheet has
     applied (a no-op if the size was already right). */
  window.addEventListener('load', () => {
    if (map) map.invalidateSize();
  }, { once: true });

  // Not tied to the data load - a slow network must not delay it.
  scheduleLocationPrompt();

  // ==========================================================================
  // WEBMCP TOOLS - THE MAP
  // ==========================================================================
  /* The tools live in assets/mcp-tools.js; this hands them the map's
     internals. Getters for what changes; the real functions (not copies), so
     a tool searches exactly as the search box does. This list is the
     agent's entire reach into the page. */
  if (window.hcMapTools) {
    window.hcMapTools.install({
      places: () => places,
      userLocation: () => userLocation,
      isReady: () => dataReady && places.length > 0,
      activeFilter: () => ({
        category: Object.keys(activeAmenityCategories).find(key => activeAmenityCategories[key]) || null,
        cuisine: activeCuisine,
      }),

      AMENITY_FIELDS, TAG_CATALOGUE, filterBtns, submitModalForm, reportModalForm,

      slugify, placeSlug, placeUrl, googleMapsLink, appleMapsLink, priceLabel,
      haversineKm, detailsText, findPlaceBySlug, focusPlace, matchesSearch, searchScore,
      passesActiveCategories, clearFilters: clearAmenityCategories, sortTopRated,
      loadPlaceDetail, loadPlaceReviews, fetchWithToken,
      openSubmitModal, openReportModal,
      validateForm, validatableFields, fieldMessage,
    });
  }

  /* Back/forward restores the whole view from the URL - filters (including
     turning one OFF, which applyCategoriesFromUrl never does), then the
     place, since the filters decide which places exist. */
  function applyViewFromUrl() {
    const at = parseLocationPath();
    const wantedCategory = at.view === 'category' && at.slug in activeAmenityCategories ? at.slug : null;

    Object.keys(activeAmenityCategories).forEach((key) => {
      activeAmenityCategories[key] = key === wantedCategory;
    });
    amenityCategoryBtns.forEach((btn) => updateAmenityCategoryBtnUI(btn, btn.dataset.amenity));
    setCuisineRow(at.cuisine || '');

    resetListWindow();
    renderList();
    updateMarkerVisibility();
    updatePageMeta();
  }

  window.addEventListener('popstate', () => {
    applyViewFromUrl();

    const slug = getRequestedSlug();

    if (slug) {
      openPlaceFromSlug(slug);
    } else {
      closePlaceModal();
      setActivePin(null);
      setActiveCard(null);
    }
  });


/* NO PAGE ZOOM ON MOBILE
   A zoomed page leaves Leaflet's idea of the map's size wrong, so only the
   map zooms. The viewport meta allows zoom (see the layout) and
   touch-action stops pinch on the page; iOS Safari also needs:

   1. gesture* events blocked - Safari's pinch events, which ignore
      touch-action. Leaflet uses raw touches, so map pinch still works.
   2. A second tap within 300ms suppressed - Safari double-tap-zooms even
      with user-scalable=no. (300ms, not 500, so real double-clicks survive.)
   (Safari's zoom into a focused text field is stopped in main.js, which
   article pages load too - "NO ZOOM INTO A FOCUSED FIELD".)

   Suppressing that tap also kills Leaflet's dblclick zoom (and Leaflet 1.9
   relies on the browser synthesising dblclick, which iOS does unreliably),
   so a double tap on the map - two taps close in time AND place - zooms the
   map here, around the tapped point. */
(() => {
  'use strict';

  ['gesturestart', 'gesturechange', 'gestureend'].forEach(type => {
    document.addEventListener(type, e => e.preventDefault(), { passive: false });
  });

  // Roughly a fingertip; further apart, it was two separate taps.
  const TAP_SLOP = 40;

  let lastTouch = 0;
  let lastTouchPoint = null;

  document.addEventListener('touchend', e => {
    const now = Date.now();
    const touch = e.changedTouches && e.changedTouches[0];
    const quick = now - lastTouch <= 300;

    if (quick) {
      e.preventDefault();
    }

    const doubled = quick && touch && lastTouchPoint
      && Math.abs(touch.clientX - lastTouchPoint.x) <= TAP_SLOP
      && Math.abs(touch.clientY - lastTouchPoint.y) <= TAP_SLOP;

    lastTouch = now;
    lastTouchPoint = touch ? { x: touch.clientX, y: touch.clientY } : null;

    if (!doubled || !map) return;

    // The map surface only - pins and controls handle their own taps.
    const el = e.target && e.target.closest ? e.target.closest('#map') : null;

    if (!el) return;
    if (e.target.closest('.leaflet-marker-icon, .leaflet-control, .leaflet-popup')) return;

    map.setZoomAround(map.mouseEventToContainerPoint(touch), map.getZoom() + 1);

    // A third tap starts a fresh gesture.
    lastTouch = 0;
    lastTouchPoint = null;
  }, { passive: false });
})();
})();
